import type { TranslationKeys } from './types';

export const en: TranslationKeys = {
  'search.group.text': "In text",
  'ai.tabReady': "Opened “{name}” tab",
  'walls.draw': "Draw",
  'walls.connect': "Join endpoints",
  'walls.connectHint': "Click the first endpoint, then the second. The first endpoint moves to the second. Esc cancels selection.",
  'walls.drawHint': "Start on empty space or a wall endpoint. Double-click to finish the chain.",
  'walls.connectChanged': "The endpoint changed or joining would collapse the wall. Select endpoints again.",
  'editor.format': "Text formatting",
  'editor.bold': "Bold",
  'editor.italic': "Italic",
  'editor.heading': "Heading",
  'editor.list': "List",
  'editor.quote': "Quote",
  'editor.undo': "Undo typing",
  'editor.redo': "Redo typing",
  'editor.orderedList': "Numbered list",
  'editor.link': "Link",
  'editor.linkTitle': "Link",
  'editor.linkLabel': "Address",
  'editor.linkInvalid': "Only an http(s):// or mailto: address. Empty removes the link.",
  'studio.maps.besideLong': 'Open beside',
  'studio.maps.addTitle': 'Add to the beat',
  'studio.maps.addCampaignMap': '＋ A campaign map…',
  'studio.note.open': 'Open the note',
  'beat.next.openBeat': 'Open the beat',
  'aiField.languages': "languages",
  'aiField.backstory': "backstory",
  'aiField.roleplay_json': "roleplay",
  'aiField.playersText': "player text",
  'aiField.gmText': "GM notes",
  'aiField.title': "title",
  'aiField.text': "text",
  'aiField.completed': "completed",
  'ai.results.history': "Change history",
  'ai.results.historyHint': "Results in this conversation, including hidden cards. Undo availability is limited by the campaign journal.",
  'ai.results.empty': "No changes in this conversation yet.",
  'ai.results.saved': "Saved to campaign",
  'ai.results.noChanges': 'Completed without changes',
  'ai.results.partial': "Some changes were undone",
  'ai.results.kept': "Manual edits preserved",
  'ai.results.details': "Details",
  'ai.results.open': "Open result",
  'ai.results.menu': "Result actions",
  'ai.results.hide': "Hide from chat",
  'ai.results.restore': "Restore to chat",
  'ai.results.hidden': "Hidden from chat on this device",
  'ai.results.undo': "Undo changes…",
  'ai.results.undoTitle': "Undo campaign changes?",
  'ai.results.undoBody': "This changes campaign data. To only remove the card, choose “Hide from chat”. Later manual edits will be checked separately.",
  'ai.results.review': "Review changes",
  'ai.results.pending': "Not applied yet",
  'ai.results.reviewHint': "Choose actions to apply. Dependent actions require their preceding steps.",
  'ai.results.apply': "Apply selected",
  'ai.results.decline': "Decline",
  'ai.results.declined': "Proposal declined",
  'ai.results.declineError': "Could not decline the proposal. Try again.",
  'ai.results.applying': "Applying changes…",
  'ai.results.dismissing': "Declining proposal…",
  'ai.results.selected': "Selected actions: {n} of {total}",
  'ai.results.proposed': "Proposed",
  'ai.results.mode': "Changes",
  'ai.results.suggest': "Propose first",
  'ai.results.auto': "Apply directly",
  'ai.results.modeHint': "For subsequent requests, within your permissions. Showing to players requires separate confirmation.",
  'ai.results.dependency': "Requires another action",
  // === Toolbar ===
  'toolbar.select': 'Select',
  'toolbar.draw': 'Draw',
  'toolbar.erase': 'Eraser',
  'toolbar.shape': 'Shape',
  'toolbar.measure': 'Measure',
  'toolbar.ping': 'Ping',
  'toolbar.pingHint': 'or just hold on the map for 0.5 s',
  'toolbar.fog': 'Fog of War',
  'toolbar.note': 'Note',
  'toolbar.portal': 'Portal',
  'toolbar.selectDrawing': 'Select Drawing',
  'toolbar.text': 'Text',
  'toolbar.aoe': 'AoE',
  'toolbar.mapObject': 'Object',
  'toolbar.wall': 'Walls',
  'toolbar.token': 'Token',
  'toolbar.token.helper': 'Drag a character from the character panel onto the map',
  'toolbar.undo': 'Undo',
  'toolbar.redo': 'Redo',

  // === Toolbar Groups ===
  'toolbar.group.select': 'Select',
  'toolbar.group.drawing': 'Drawing',
  'toolbar.group.fog': 'Fog of War',
  'toolbar.group.measure': 'Measure and ping',
  'toolbar.group.pins': 'Map Pins',
  'toolbar.group.text': 'Text',
  'toolbar.group.token': 'Token',
  'toolbar.group.mapObjects': 'Map Objects',
  'toolbar.group.note': 'Notes',
  'toolbar.group.objects': 'Objects',
  'toolbar.group.aiEdit': 'AI Edit',
  'toolbar.inpaintRect': 'Rect Inpaint',
  'toolbar.inpaintLasso': 'Lasso Inpaint',
  'toolbar.detailRect': 'Detail Map',
  'toolbar.initiative': 'Initiative',
  'toolbar.held': 'held',
  'toolbar.snapActive': 'Snap',
  'toolbar.overflow': 'More...',

  // === Canvas Context Menu ===
  'contextMenu.addToken': 'Add Token',
  'contextMenu.addPin': 'Add Pin',
  'contextMenu.actAs': 'Act as',
  'contextMenu.addText': 'Add Text',
  'contextMenu.editToken': 'Edit Token',
  'contextMenu.deleteToken': 'Delete Token',
  'contextMenu.copyToken': 'Copy Token',
  'contextMenu.moveToFront': 'Move to Front',
  'contextMenu.moveToBack': 'Move to Back',
  'contextMenu.openEntry': 'Open Entry',
  'contextMenu.movePin': 'Move Pin',
  'contextMenu.deletePin': 'Delete Pin',
  'contextMenu.deleteDrawing': 'Delete Drawing',
  'contextMenu.changeColor': 'Change Color',
  'contextMenu.viewToken': 'View Token',

  // === Drawing Selection ===
  'drawingSelection.stroke': 'stroke',
  'drawingSelection.strokes': 'strokes',
  'drawingSelection.toolbar': 'Drawing selection actions',
  'drawingSelection.delete': 'Delete',

  // === Sidebar Tabs ===
  'sidebar.map': 'Map',
  'sidebar.journal': 'Journal',
  'sidebar.notes': 'Notes',
  'sidebar.chat': 'Chat',
  'sidebar.srd': 'SRD',
  'sidebar.initiative': 'Initiative',
  'sidebar.participants': 'Participants',
  'sidebar.ai': 'Tia',
  'sidebar.characters': 'Characters',

  // === Sidebar Sections (Map tab) ===
  'sidebar.section.pages': 'Pages',
  'sidebar.section.tokens': 'Tokens',
  'sidebar.section.pins': 'Pins',
  'sidebar.section.mapObjects': 'Map Objects',

  // === Inspector ===
  'inspector.tokenVision': 'Vision',
  'inspector.visionRange': 'Range',
  'inspector.darkvision': 'Darkvision',
  'inspector.pinOpenEntry': 'Open Entry',

  // === Common Actions ===
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.loading': 'Loading...',
  'common.search': 'Search',
  'common.reset': 'Reset',
  'common.next': 'Next',
  'common.confirm': 'Confirm',
  'common.name': 'Name',
  'common.none': '— none —',

  // === Auth ===
  'auth.login': 'Sign In',
  'auth.loginTitle': 'Sign In',
  'auth.loginBusy': 'Signing in...',
  'auth.register': 'Register',
  'auth.registerTitle': 'Register',
  'auth.registerBusy': 'Registering...',
  'auth.registerSubmit': 'Register',
  'auth.inviteCode': 'Invite code',
  'auth.inviteCodeHint': 'Closed beta code. Ask the administrator.',
  'auth.inviteCodePlaceholder': 'Enter invite code',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.passwordHint': 'Password (min 8 chars)',
  'auth.displayName': 'Name',
  'auth.hasAccount': 'Already have an account? Sign in',
  'auth.noAccount': 'No account? Register',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.profileTitle': 'Profile Settings',
  'auth.displayNameLabel': 'Display name:',
  'auth.saveBusy': 'Saving...',

  // === Lobby ===
  'lobby.createSession': 'Create Session',
  'lobby.joinSession': 'Join Session',
  'lobby.yourName': 'Your name:',
  'lobby.sessionCode': 'Session code:',
  'lobby.connecting': 'Connecting...',
  'lobby.enter': 'Enter',

  // === Session List ===
  'sessionList.title': 'Your Sessions',
  'sessionList.session': 'Session',
  'sessionList.player': 'Player',
  'sessionList.loading': 'Loading sessions...',
  'sessionList.copied': 'Copied!',
  'sessionList.copyCode': 'Copy code',
  'sessionList.today': 'Today',
  'sessionList.yesterday': 'Yesterday',
  'sessionList.daysAgo': 'ago',
  'sessionList.rename': 'Rename',
  'sessionList.delete': 'Delete',
  'sessionList.copyInvite': 'Copy invite link',
  'sessionList.deleteConfirm': 'Are you sure you want to delete this session?',
  'sessionList.deleteConfirmTitle': 'Delete Session',
  'sessionList.previewComingSoon': 'Coming soon',
  'sessionList.inviteCopied': 'Invite link copied',

  // === Chat ===
  'chat.title': 'Chat',
  'chat.placeholder': 'Message or /roll 2d6+3',
  'chat.rolls': ' rolls ',
  'chat.helpUsage': 'Usage: /roll 2d6+3',
  'chat.nameChanged': 'Name changed to:',
  'chat.helpRoll': '/roll NdX+M — roll dice',
  'chat.helpName': '/name Name — change name',
  'chat.helpClear': '/clear — clear chat',
  'chat.helpGm': '/gm text — GM-only message',
  'chat.unknownCommand': 'Unknown command. /help for list of commands.',
  'chat.welcome': 'Welcome to VTT! Type /help in chat for commands.',
  'chat.welcomeShortcuts': 'Ctrl+S — save session, Ctrl+L — load',

  // === Initiative ===
  'initiative.title': 'Initiative',
  'initiative.next': 'Next',
  'initiative.otherPage': '(other page)',
  'initiative.add': 'Add to the fight',
  'initiative.addMenu': 'Add to the fight…',
  'initiative.more': 'More',
  'initiative.closeHint': 'Close the window — the fight goes on; open again: «Initiative» on the left panel (I)',
  'initiative.collapse': 'Collapse to the title',
  'initiative.noFight': 'No fight.',
  'initiative.endedRounds': 'Fight over · rounds: {n}.',
  'initiative.collect': 'Gather the fighters',
  'initiative.sameAgain': 'The same fighters ({n})',
  'initiative.orFromMap': 'Or right-click tokens on the map → «To initiative».',
  'initiative.whoInFight': 'Who fights',
  'initiative.nOfOnMap': '{n} of {m} on the map',
  'initiative.onlySelected': 'Only the ones selected on the map ({n})',
  'initiative.party': 'Party',
  'initiative.foes': 'Opponents',
  'initiative.hiddenFromPlayers': 'Hidden from the players',
  'initiative.rollsHimself': 'rolls himself',
  'initiative.dexMod': 'Dex {mod}',
  'initiative.noSheet': 'no sheet: +0',
  'initiative.playersRoll': 'Players roll themselves',
  'initiative.groupSame': 'Identical ones — one group',
  'initiative.groupSameTip': 'DMG rule: identical monsters act together, one roll',
  'initiative.startFight': 'Start the fight',
  'initiative.d20dex': 'd20 + Dex',
  'initiative.d20dexTip': 'Opponents: d20 + Dexterity from the sheet',
  'initiative.cancel': 'Cancel',
  'initiative.assembled': 'Fight assembled · round 1 has not started',
  'initiative.waitingRolls': 'Waiting for the roll: {names}',
  'initiative.beginNow': 'Start without waiting',
  'initiative.beginNowTip': 'Not rolled: {names}. They take their place when they roll',
  'initiative.roundN': 'Round {n}',
  'initiative.waiting': 'waiting',
  'initiative.yourTurn': 'Your turn',
  'initiative.youNext': 'you are next',
  'initiative.youAfter': 'you go after: {name}',
  'initiative.youDelayed': 'you delayed your turn',
  'initiative.nextTip': 'Next turn (Space)',
  'initiative.nextTurn': 'Next turn',
  'initiative.prev': 'Back — the previous turn',
  'initiative.delayMine': 'Delay',
  'initiative.delayMineTip': 'Delay your turn — you join when you say',
  'initiative.endTurn': 'End turn',
  'initiative.rollForPlayer': 'Roll for the player',
  'initiative.rollForPlayerName': 'Roll for the player «{name}»',
  'initiative.notRolled': 'Has not rolled yet',
  'initiative.roll': 'Roll',
  'initiative.editValue': 'Change the number {v}',
  'initiative.valueOf': 'Initiative «{name}»',
  'initiative.hpOf': 'Hit points «{name}»',
  'initiative.hpWord': 'HP',
  'initiative.hpTip': 'Hit points {hp}/{max}',
  'initiative.markOut': 'Out',
  'initiative.zeroHp': '0 HP',
  'initiative.hiddenTip': 'Hidden from the players — not in their list',
  'initiative.hidden': 'Hidden from the players',
  'initiative.tieTip': 'Equal {v} with «{other}» — the higher Dexterity goes first ({a} vs {b}). Drag to change the order.',
  'initiative.tie': 'Tie',
  'initiative.outTip': 'Out — the turn is skipped',
  'initiative.downTip': 'Unconscious — keeps the turn: death saves',
  'initiative.unconscious': 'Unconscious',
  'initiative.fromRound': 'from round {n}',
  'initiative.fromRoundTip': 'Added mid-round after its place had passed',
  'initiative.owed': 'next',
  'initiative.owedTip': 'The number moved above the current one and it has not acted this round',
  'initiative.yourTurnTag': 'your turn',
  'initiative.actsTag': 'acts',
  'initiative.showOnMap': 'Show on the map',
  'initiative.showOnMapName': '{name} — show on the map',
  'initiative.openSheet': 'Open the sheet',
  'initiative.hpMenu': 'Hit points…',
  'initiative.enterValue': 'Enter the number',
  'initiative.enterGroupValue': 'Enter the group\'s number',
  'initiative.reroll': 'Reroll',
  'initiative.delayTurn': 'Delay the turn',
  'initiative.split': 'Split the group — a roll for each',
  'initiative.showToPlayers': 'Show to the players',
  'initiative.hideFromPlayers': 'Hide from the players',
  'initiative.removeFromFight': 'Remove from the fight',
  'initiative.removeGroup': 'Remove the whole group ({n})',
  'initiative.groupNote': 'One of the group — expand ▸ and use his ⋯.',
  'initiative.ownValue': 'Own number — take out of the group',
  'initiative.showEach': 'Show each',
  'initiative.collapseGroup': 'Collapse the group',
  'initiative.moreOf': 'More: {name}',
  'initiative.drag': 'Drag — change the order',
  'initiative.delayedHeader': 'Delayed',
  'initiative.untilEndOfRound': 'until the end of round {n}',
  'initiative.untilEndTip': 'Not joined by the end of the round — the turn is lost, next round back at its place',
  'initiative.join': 'Join',
  'initiative.joinTip': 'Acts now, before {name}',
  'initiative.joinTipHidden': 'Acts now, before the current one',
  'initiative.waitsDelayed': 'waits',
  'initiative.endFight': 'End the fight…',
  'initiative.endAsk': 'End the fight?',
  'initiative.endAskHint': 'The order and the round count ({n}) are cleared. Hit points and conditions stay.',
  'initiative.endConfirm': 'End the fight',
  'initiative.menuNote': 'Space — next turn. A token into the fight — right-click it on the map → «To initiative».',
  'initiative.onMap': 'On the map: {names}',
  'initiative.appearedAfter': 'appeared after the fight started',
  'initiative.toFight': 'Into the fight',
  'initiative.dismissNew': 'Do not add',
  'initiative.pickAll': 'Every token of the map is in the fight.',
  'initiative.new': 'new',
  'initiative.joinGroups': 'Identical ones — to their group',
  'initiative.rollAndAdd': 'Roll and add ({n})',
  'initiative.turnStays': 'the turn does not move',
  'initiative.close': 'Close',
  'initiative.undo': 'Undo',
  'initiative.n.started': 'Opponents rolled (d20 + Dexterity). Round 1 starts when every player has rolled',
  'initiative.n.startedAll': 'Rolled for everybody — round 1',
  'initiative.n.begun': 'Round 1 started without the rolls of: {names} — they take their place when they roll',
  'initiative.n.addedGroup': '{name} — into the group «{group}» ({v})',
  'initiative.n.addedLater': '{name} — {roll}: its place in this round has passed, acts from round {n}',
  'initiative.n.addedNow': '{name} — {roll}: acts in this round',
  'initiative.n.removedOne': 'Out of the fight: {names}. The token stays on the map',
  'initiative.n.removedMany': 'Out of the fight: {names}. The tokens stay on the map',
  'initiative.n.value': 'number {v}',
  'initiative.n.roll': 'roll {roll}',
  'initiative.n.reroll': 'reroll {roll}',
  'initiative.n.move': 'a new place (the number {v} stays)',
  'initiative.n.top': 'Nobody has acted in round 1 — the order starts over, {cur} acts',
  'initiative.n.keeps': 'The turn stays with him',
  'initiative.n.owed': 'Its place in round {r} has passed but it has not acted — acts next; from round {r1} at the new place',
  'initiative.n.acted': 'It has acted in round {r} — no second turn; from round {r1} at the new place',
  'initiative.n.same': 'The turn did not move',
  'initiative.n.allRolled': 'Everybody rolled — round 1, {cur} acts',
  'initiative.n.round': 'Round {n}',
  'initiative.n.lost': '{names} did not join — the turn of round {r} is lost, back at its place ({v})',
  'initiative.n.delayed': '{name} delayed the turn. Joins when he says — until the end of round {r}; otherwise the turn is lost and in round {r1} he is back at his place',
  'initiative.n.joined': '{name} joins now — before {before}; his place is here from now on ({v})',
  'initiative.n.split': 'The group is split, a new roll for each: {rolls}',
  'initiative.n.splitOwed': 'Whoever landed above the current one and has not acted acts next',
  'initiative.n.ended': 'Fight over · rounds: {n}',
  'initiative.n.restored': 'Undone',
  'initiative.n.firstTurn': 'This is the first turn of the fight',
  'initiative.n.back': 'Back: {cur} acts',
  'initiative.n.turnEnded': '{name} ended the turn',
  'contextMenu.initiativeAdd': 'To initiative',
  'contextMenu.initiativeAddN': 'To initiative ({n})',
  'contextMenu.initiativeRemove': 'Remove from initiative',
  'contextMenu.initiativeRemoveN': 'Remove from initiative ({n})',
  'contextMenu.alreadyInFight': 'Already in the fight: {names}',

  // === Tokens ===
  'tokens.title': 'Tokens',
  'tokens.add': 'Add Token',
  'tokens.boundToSheet': 'Bound to character sheet',
  'tokens.sheetLabel': 'Sheet:',
  'tokens.tokenName': 'Token name:',
  'tokens.defaultName': 'Token',
  'tokens.added': 'Token added:',
  'tokens.created': 'Token created:',

  // === Dice ===
  'dice.title': 'Dice',
  'dice.roll': 'Roll',

  // === Pages ===
  'pages.deleteConfirmTitle': 'Delete Page?',
  'pages.deleteConfirmDesc': 'All tokens and drawings on this page will be permanently deleted.',
  'pages.deleteConfirmDescWithChildren': 'All tokens and drawings on this page will be deleted. Nested pages will be moved to root level.',
  'pages.deletePlayersHere': 'The players are on this map now. Where should they go?',
  'pages.deleteChooseTarget': '— choose a map —',

  // === Characters ===
  'characters.title': 'Characters',
  'characters.createWizard': 'Create via wizard (D&D 5e)',
  'characters.empty': 'No characters',
  'characters.create': '+ Create Character',
  'characters.createBlank': 'or a blank sheet — fill it by hand',
  'characters.newCharacter': 'New Character',
  'characters.nameLabel': 'Name:',
  'characters.namePlaceholder': 'Character name',
  'characters.templateLabel': 'Template:',
  'characters.typeLabel': 'Type:',
  'characters.typePC': 'PC (Player)',
  'characters.typeNPC': 'NPC',
  'characters.builtIn': '(built-in)',
  'characters.foreignSheet': 'Other player\'s character',
  'characters.performanceWarning': 'characters — performance may be affected',
  'characters.assignOwner': 'Assign Owner',
  'characters.assignOwnerDescription': 'Choose a player to own this character:',
  'characters.publicFields': 'Public fields (what the party sees)',
  'characters.sheetVisibleTitle': 'Sheet visible to players — hide',
  'characters.sheetHiddenTitle': 'Sheet hidden from players — show',
  'characters.cardVisibilityTitle': 'Card on scene',
  'characters.publicFieldsDescription': 'Fields other players can see on this sheet:',
  'characters.noOwner': 'No owner',
  'characters.createWithoutOwner': 'Without owner',
  'characters.createAndAssign': 'Assign & Create',
  'characters.ungrouped': 'No folder',
  'characters.newGroup': 'New folder',
  'characters.groupName': 'Folder name',
  'characters.deleteGroup': 'Delete folder',
  'characters.deleteConfirmTitle': 'Delete the character sheet?',
  'characters.deleteConfirmDesc': '“{name}” will be deleted for good.',
  'characters.renameGroup': 'Rename folder',

  // === Participants ===
  'participants.title': 'Participants',

  // === Notes ===
  'notes.title': 'Map Notes',
  'notes.empty': 'No notes. Use the note tool to create one.',
  'notes.untitled': 'Untitled',
  'notes.gmOnly': 'GM only',
  'notes.newNote': 'New Note',
  'notes.editNote': 'Edit Note',
  'notes.noteTitle': 'Title',
  'notes.noteContent': 'Content',
  'notes.icon': 'Icon',
  'notes.color': 'Color',
  'notes.selectColor': 'Select color',

  // === Note icons ===
  'noteIcon.pin': 'Pin',
  'noteIcon.scroll': 'Scroll',
  'noteIcon.skull': 'Skull',
  'noteIcon.chest': 'Chest',
  'noteIcon.star': 'Star',
  'noteIcon.question': 'Question',
  'noteIcon.exclamation': 'Warning',
  'noteIcon.camp': 'Camp',
  'noteIcon.sword': 'Combat',
  'noteIcon.key': 'Key',

  // === Reconnect ===
  'reconnect.message': 'Reconnecting...',

  // === Connection Status ===
  'status.online': '\u25CF Online',
  'status.reconnecting': '\u25CC Reconnecting...',

  // === Workspace Presets ===
  'preset.explore': 'Explore (Alt+1)',
  'preset.combat': 'Combat (Alt+2)',
  'preset.prep': 'Prep (Alt+3)',
  'preset.fullscreen': 'Fullscreen Mode',
  'preset.exit': 'Disconnect',

  // === Shortcuts ===
  'shortcuts.title': 'Keyboard Shortcuts',
  'shortcuts.hint': 'Shortcuts do not work when an input field is focused.',
  'shortcuts.sectionTools': 'Tools',
  'shortcuts.sectionCamera': 'Camera',
  'shortcuts.sectionTokens': 'Tokens',
  'shortcuts.sectionChat': 'Chat & Initiative',
  'shortcuts.sectionWorkspace': 'Workspace',
  'shortcuts.toolSelect': 'Tool: Select',
  'shortcuts.toolDraw': 'Tool: Draw',
  'shortcuts.toolEraser': 'Tool: Eraser',
  'shortcuts.toolRuler': 'Tool: Ruler',
  'shortcuts.toolPing': 'Tool: Ping (click the map; same as holding 0.5 s)',
  'shortcuts.toolFog': 'Tool: Fog of War',
  'shortcuts.toolNote': 'Tool: Note (GM)',
  'shortcuts.toolPortal': 'Tool: Portal (GM)',
  'shortcuts.snapToggle': 'Snap to grid (toggle)',
  'shortcuts.zoomIn': 'Zoom in',
  'shortcuts.zoomOut': 'Zoom out',
  'shortcuts.fitToScreen': 'Fit to screen',
  'shortcuts.exitFullscreen': 'Exit fullscreen',
  'shortcuts.deleteTokens': 'Delete selected tokens',
  'shortcuts.quickSelectToken': 'Quick select token by number',
  'shortcuts.nextTurn': 'Next initiative turn',
  'shortcuts.openMapPanel': 'Open map panel',
  'shortcuts.presetExplore': 'Preset: Explore (GM)',
  'shortcuts.presetCombat': 'Preset: Combat (GM)',
  'shortcuts.presetPrep': 'Preset: Prep (GM)',
  'shortcuts.toggleSidebar': 'Toggle sidebar',
  'shortcuts.showShortcuts': 'Show/hide shortcuts',
  'shortcuts.saveSession': 'Save session',
  'shortcuts.loadSession': 'Load session',
  'shortcuts.undo': 'Undo',
  'shortcuts.redo': 'Redo',
  'shortcuts.toolSelectDrawing': 'Tool: Select Drawing (GM)',
  'shortcuts.toolText': 'Tool: Text (GM)',
  'shortcuts.toolAoe': 'Tool: AoE (GM)',
  'shortcuts.toolMapObject': 'Tool: Map Object (GM)',
  'shortcuts.fogBrushDec': 'Fog brush: decrease',
  'shortcuts.fogBrushInc': 'Fog brush: increase',
  'shortcuts.copy': 'Copy token',
  'shortcuts.paste': 'Paste token',
  'shortcuts.toggleInitiative': 'Toggle Initiative (I)',

  // === Tool Options ===
  'toolOptions.snapToGrid': 'Snap to grid',
  'toolOptions.color': 'Color:',
  'toolOptions.width': 'Width:',
  'toolOptions.mode': 'Mode:',
  'toolOptions.modeSimple': 'Simple',
  'toolOptions.modeAlternating': 'Alternating (D&D)',
  'toolOptions.modeEuclidean': 'Euclidean',
  'toolOptions.modeHex': 'Hex',
  'toolOptions.gridSizeFt': 'Grid (ft):',
  'toolOptions.shape': 'Shape:',
  'toolOptions.shapeRect': 'Rectangle',
  'toolOptions.shapePolygon': 'Polygon',
  'toolOptions.shapeCircle': 'Circle',
  'toolOptions.navigation': 'Navigation:',
  'toolOptions.navFree': 'Free',
  'toolOptions.navConfirm': 'Confirm',
  'toolOptions.navGmOnly': 'GM Only',
  'toolOptions.reFog': 'Re-fog',
  'toolOptions.dynamicFogHint': 'Dynamic fog: coverage follows token vision',
  'toolOptions.clearAllFog': 'Clear All',
  'toolOptions.coverAllFog': 'Cover All',
  'toolOptions.shapeLine': 'Line',
  'toolOptions.fogMode': 'Mode:',
  'toolOptions.brushSize': 'Brush:',
  'toolOptions.mapSettings': 'Map Settings',
  'toolOptions.gridSize': 'Grid Size:',
  'toolOptions.mapScale': 'Map Scale:',
  'toolOptions.bgColor': 'Background:',
  'toolOptions.deleteMap': 'Delete Map',
  'toolOptions.confirmDeleteMap': 'Delete map?',
  'toolOptions.fogSystem': 'System:',
  'toolOptions.fogManual': 'Manual',
  'toolOptions.fogDynamic': 'Dynamic',
  'toolOptions.wallType': 'Type:',
  'toolOptions.wallTypeWall': 'Wall',
  'toolOptions.wallTypeDoor': 'Door',
  'toolOptions.wallTypeWindow': 'Window',
  'toolOptions.wallTypeTerrain': 'Terrain',
  'toolOptions.wallTypeSecret': 'Secret',
  'toolOptions.wallTypeEthereal': 'Ethereal',
  'toolOptions.aiEditMode': 'Mode',
  'toolOptions.sketchToMap': 'Map from Sketch',

  // === Token Edit Dialog ===
  'tokenEdit.title': 'Edit Token',
  'tokenEdit.name': 'Name:',
  'tokenEdit.size': 'Size:',
  'tokenEdit.sizeTiny': 'Tiny (\u00BD)',
  'tokenEdit.sizeMedium': 'Medium (1)',
  'tokenEdit.sizeLarge': 'Large (2)',
  'tokenEdit.sizeHuge': 'Huge (3)',
  'tokenEdit.sizeGargantuan': 'Gargantuan (4)',
  'tokenEdit.color': 'Color:',
  'tokenEdit.notes': 'Notes:',
  'tokenEdit.visible': 'Visible to players',
  'tokenEdit.light': 'Light',
  'tokenEdit.lightEnabled': 'Emits Light',
  'tokenEdit.lightBright': 'Bright Radius',
  'tokenEdit.lightDim': 'Dim Radius',
  'tokenEdit.lightColor': 'Light Color',
  'tokenEdit.visionType': 'Vision Type',
  'tokenEdit.visionTypeNormal': 'Normal',
  'tokenEdit.visionTypeDarkvision': 'Darkvision',

  // === Global Illumination ===
  'page.globalIllumination': 'Illumination',
  'page.illuminationBright': 'Bright',
  'page.illuminationDim': 'Dim',
  'page.illuminationDark': 'Dark',
  'page.illuminationColor': 'Light color tint',

  // === Conditions Dialog ===
  'conditions.title': 'Conditions',

  // === Context Menu ===
  'contextMenu.inspect': 'Inspect',
  'contextMenu.edit': 'Edit',
  'contextMenu.conditions': 'Conditions',
  'contextMenu.hp': 'HP',
  'contextMenu.resize': 'Resize',
  'contextMenu.addAura': 'Add Aura',
  'contextMenu.layerUp': 'Layer Up',
  'contextMenu.layerDown': 'Layer Down',
  'contextMenu.copy': 'Copy',
  'contextMenu.delete': 'Delete',
  'contextMenu.auras': 'Auras:',
  'contextMenu.fogReset': 'Reset Fog of War',
  'contextMenu.hideToken': 'Hide from players',
  'contextMenu.showToken': 'Show to players',
  'contextMenu.enableVision': 'Enable Vision',
  'contextMenu.disableVision': 'Disable Vision',
  'contextMenu.openSheet': 'Open Sheet',

  // === Link Context Menu ===
  'linkMenu.edit': 'Edit',
  'linkMenu.navigate': 'Navigate to target',
  'linkMenu.toggleVisibility': 'Toggle visibility',
  'linkMenu.delete': 'Delete',
  'linkMenu.zone': 'Zone',

  // === Aura Editor ===
  'aura.editTitle': 'Edit Aura',
  'aura.addTitle': 'Add Aura',
  'aura.radiusFt': 'Radius (ft):',
  'aura.color': 'Color:',
  'aura.opacity': 'Opacity:',
  'aura.label': 'Label:',
  'aura.style': 'Style:',
  'aura.styleSolid': 'Solid',
  'aura.styleDashed': 'Dashed',
  'aura.stylePulse': 'Pulse',
  'aura.gmOnly': 'GM Only',

  // === Location Link Dialog ===
  'locationLink.editTitle': 'Edit Zone',
  'locationLink.createTitle': 'Create Portal Zone',
  'locationLink.targetPage': 'Target Page',
  'locationLink.selectPage': 'Select page\u2026',
  'locationLink.label': 'Label',
  'locationLink.labelPlaceholder': 'Zone name\u2026',
  'locationLink.style': 'Style',
  'locationLink.stylePortal': 'Portal',
  'locationLink.styleDoor': 'Door',
  'locationLink.styleStairs': 'Stairs',
  'locationLink.styleArea': 'Area',

  // === Template Editor ===
  'templates.title': 'Templates',
  'templates.builtIn': '(built-in)',
  'templates.createTemplate': '+ Create Template',
  'templates.editTemplate': 'Edit Template',
  'templates.newTemplate': 'New Template',
  'templates.nameLabel': 'Name:',
  'templates.newSection': 'New Section',
  'templates.newField': 'New Field',
  'templates.addField': '+ Field',
  'templates.addSection': '+ Section',
  'templates.deleteSection': 'Delete Section',

  // === AI Chat ===
  'ai.title': 'Tia',
  'aiCard.was': 'Before',
  'aiCard.now': 'After',
  'ai.historyLoadError': 'Could not load history. Please try again.',
  'ai.historyRenameError': 'Could not save the title. Please try again.',
  'ai.historyBusy': 'Wait for the reply or stop generation before switching chats.',
  'ai.historyRetention': 'Conversations are kept for 30 days after their last activity.',
  'ai.historyTitle': 'Chat title',
  'ai.historySave': 'Save title',
  'ai.historyCancel': 'Cancel',
  'ai.historyCurrent': 'Current',
  'ai.historyRename': 'Rename “{title}”',
  'ai.historyMore': 'Show more',
  'ai.historyRetry': 'Retry',
  'ai.newChat': 'New Chat',
  'ai.emptyHint': 'Tia collects stories. Tell her yours.',
  'ai.thinking': 'Tia is thinking...',
  'ai.historySummarized': 'I folded the start of this conversation into a summary — what we agreed on, I remember.',
  'ai.placeholder': 'Ask Tia…',
  'ai.models': 'Models',
  'ai.textModel': 'Text model',
  'ai.imageModel': 'Image model',
  'aiModel.mapModel': 'Map model',
  'settings.aiTextModel': 'Default assistant text model',
  'settings.aiTextModelServer': 'Server default',
  'aiCard.changes': 'Changes ({count})',
  'aiChange.beatMoved': 'Beat “{name}” moved in the plan',
  'ai.plan.step.move': 'I will move the beat “{name}”',
  'aiGroup.sheet': 'Sheet “{name}”',
  'aiGroup.beat': 'Beat “{name}”',
  'aiGroup.journal': 'Record “{name}”',
  'aiGroup.token': 'Token “{name}”',
  'aiGroup.page': 'Map “{name}”',
  'aiGroup.other': '“{name}”',
  'aiGroup.count.one': '{n} change',
  'aiGroup.count.few': '{n} changes',
  'aiGroup.count.many': '{n} changes',
  'aiGroup.show': 'show',
  'aiGroup.hide': 'hide',
  'aiGroup.empty': 'empty',
  'aiGroup.yes': 'yes',
  'aiGroup.no': 'no',
  'aiField.item': 'item',
  'aiField.equipped': 'worn: “{item}”',
  'aiField.beatOrder': 'order in the plan',
  'aiField.proficiencies': 'proficiencies and languages',
  'aiField.proficiency_cats': 'proficiency groups',
  'aiField.subclass': 'subclass',
  'aiField.subrace': 'subrace',
  'aiField.sessionLabel': 'title',
  'aiField.status': 'status',
  'aiField.name': 'name',
  'aiCard.undone': 'Undone',
  'aiCard.undoAll': 'Undo all',
  'aiCard.undo': 'Undo',
  'aiCard.undoRelated': 'Undo (+{count} related)',
  'aiCard.kept': '— your edit is kept',
  'aiCard.toastUndone': 'Undone. Your later edits stayed.',
  'aiCard.undoRefused': 'Could not undo: that answer is no longer in the journal, or only the GM can undo it.',
  'aiChange.beatCreated': 'Created the beat “{name}”',
  'aiChange.reserveCreated': 'Created the reserve beat “{name}”',
  'aiChange.outcomeCreated': 'Fork “{name}” of “{beat}”',
  'aiChange.noteCreated': 'Note “{name}”',
  'aiChange.questCreated': 'Quest “{name}”',
  'aiChange.entryCreated': 'Record “{name}”',
  'aiChange.entryChanged': 'Record “{name}” changed',
  'aiChange.prepExtended': 'Prep of “{name}” extended',
  'aiChange.objectiveDone': '“{quest}”: objective “{name}” done',
  'aiChange.objectiveUndone': '“{quest}”: objective “{name}” unticked',
  'aiChange.beatStatus': 'Beat “{name}”: {status}',
  'aiChange.beatRenamed': 'Beat renamed: “{before}” → “{name}”',
  'aiChange.beatChanged': 'Beat “{name}” changed',
  'aiChange.currentBeat': 'Now playing “{name}”',
  'aiChange.timelineDraft': 'Chronicle draft “{name}”',
  'aiChange.pageCreated': 'Created the map “{name}”',
  'aiChange.pinCreated': 'Pin “{name}”',
  'aiChange.tokenCreated': 'Token “{name}”',
  'aiChange.sheetCreated': 'Sheet “{name}”',
  'aiChange.deleted': 'Deleted: “{name}”',
  'aiUndo.conflictTitle': 'You already edited this after Tia',
  'aiUndo.conflictBodyOne': 'Restore as before Tia? Your edits here will be lost.',
  'aiUndo.conflictBodyMany': 'Restore as before Tia? Your edits in these places will be lost.',
  'aiUndo.restore': 'Restore as before',
  'aiUndo.keepRest': 'Keep my edits, undo the rest',
  'aiUndo.keepOnly': 'Keep my edits',
  'aiUndo.cancel': 'Cancel',
  'aiMark.added': 'Tia added',
  'aiMark.changed': 'Tia changed',
  'pinPopup.confirmBodyWhole': 'They will see the pin and the whole record, except the GM\'s hidden fields.',
  'aiCard.irreversible': 'no undo',
  'aiCard.field.description': 'description',
  'aiCard.field.content': 'text',
  'aiCard.field.gmNotes': 'for the GM',
  'aiCard.field.rewards': 'rewards',
  'aiChange.tokenChanged': 'Token “{name}”: {field}',
  'aiChange.tokenMoved': 'Token “{name}” moved',
  'aiChange.sheetEdited': 'Sheet “{name}” edited',
  'aiChange.pageRenamed': 'Map renamed: “{before}” → “{name}”',
  'aiChange.fogChanged': 'Fog on “{name}” changed',
  'aiChange.timeMoved': 'World time moved: {name}',
  'aiChange.linkAdded': 'New link on “{name}”',
  'aiChange.tagsChanged': 'Tags of “{name}” changed',
  'ai.thinkingLabel': 'Thinking',
  'ai.stop': 'Stop generation',
  'ai.conversationHistory': 'Chat History',
  'ai.noConversations': 'No previous conversations',
  'ai.loadingHistory': 'Loading history...',
  'ai.toolErrorFriendly': 'Could not complete this operation',
  'ai.stoppedByGM': 'Generation stopped by GM.',
  'ai.openThinkingModal': 'Open full thinking',
  'ai.emptyTiaGreeting': 'Hello! I\'m Tia. How can I help with your adventure?',
  'ai.emptyPersonaGreeting': "Hello! I'm {name}. Ask me a question.",
  'ai.personaMemory': 'Persona Memory',
  'ai.chronicle': 'Recap',
  'ai.generating': 'Generating...',
  'ai.saveAsJournal': 'Save as Journal Entry',
  'ai.noPersonaMemory': 'No memories yet. Tia will form impressions after conversations.',
  'ai.requestChronicle': 'Request recap',
  'ai.requestEpilogue': 'Epilogue',

  // === AI Tool Labels ===
  'aiTool.getSessionInfo': 'Getting session info',
  'aiTool.getCurrentPage': 'Viewing current page',
  'aiTool.listSheets': 'Listing character sheets',
  'aiTool.getSheet': 'Viewing character sheet',
  'aiTool.createNote': 'Creating note',
  'aiTool.searchMonsters': 'Searching monsters in SRD',
  'aiTool.getMonster': 'Getting monster stat block',
  'aiTool.searchClasses': 'Searching classes in SRD',
  'aiTool.searchSpells': 'Searching spells in SRD',
  'aiTool.createSheet': 'Creating character sheet',
  'aiTool.updateSheet': 'Updating character sheet',
  'aiTool.createToken': 'Creating token',
  'aiTool.bindSheet': 'Binding sheet to token',
  'aiTool.createNpc': 'Creating NPC (sheet + token)',
  'aiTool.initiativeUpdate': 'Initiative: changing the order',
  'aiTool.initiativeNext': 'Next turn',
  'aiTool.initiativeReset': 'Ending the fight',
  'aiTool.applyDamage': 'Applying damage',
  'aiTool.applyHealing': 'Healing',
  'aiTool.spellSlotSpend': 'Spending spell slot',
  'aiTool.concentrationSet': 'Setting concentration',
  'aiTool.effectAdd': 'Adding effect',
  'aiTool.effectUpdate': 'Updating effect',
  'aiTool.effectRemove': 'Removing effect',
  'aiTool.repeatingRowAdd': 'Adding row',
  'aiTool.repeatingRowUpdate': 'Updating row',
  'aiTool.repeatingRowRemove': 'Removing row',
  'aiTool.pageActivate': 'Switching page',
  'aiTool.pageTransfer': 'Transferring tokens',
  'aiTool.pageCreate': 'Creating page',
  'aiTool.pageRename': 'Renaming page',
  'aiTool.locationLinkCreate': 'Creating location link',
  'aiTool.fogReveal': 'Revealing fog of war',
  'aiTool.fogReset': 'Resetting fog of war',
  'aiTool.fogToggle': 'Toggling fog of war',
  'aiTool.sheetRest': 'Resting',
  'aiTool.abilityAdd': 'Adding ability',
  'aiTool.abilityRemove': 'Removing ability',
  'aiTool.spellbookAdd': 'Adding spell to spellbook',
  'aiTool.spellbookRemove': 'Removing spell from spellbook',
  'aiTool.tokenMove': 'Moving token',
  'aiTool.tokenUpdate': 'Updating token',
  'aiTool.tokenDelete': 'Deleting token',
  'aiTool.chatMessage': 'Sending message',
  'aiTool.chatRoll': 'Rolling dice',
  'aiTool.rulerShow': 'Showing ruler',
  'aiTool.searchJournal': 'Searching journal',
  'aiTool.getJournalEntry': 'Reading journal entry',
  'aiTool.getRelatedEntries': 'Finding related entries',
  'aiTool.createJournalEntry': 'Creating journal entry',
  'aiTool.updateJournalEntry': 'Updating journal entry',
  'aiTool.revealJournalEntry': 'Revealing journal entry',
  'aiTool.journalLink': 'Linking journal entries',
  'aiTool.getSpell': 'Getting spell details',
  'aiTool.searchEquipment': 'Searching equipment',
  'aiTool.searchMagicItems': 'Searching magic items',
  'aiTool.searchRaces': 'Searching races',
  'aiTool.searchConditions': 'Searching conditions',
  'aiTool.createTimelineEvents': 'Generating chronicle events',
  'aiTool.generateCalendar': 'Generating calendar',

  // === Timeline Batch Preview (054) ===
  'timelineBatch.title': 'Review AI Events',
  'timelineBatch.selectAll': 'Select all',
  'timelineBatch.deselectAll': 'Deselect all',
  'timelineBatch.selected': 'Selected',
  'timelineBatch.confirm': 'Confirm',
  'timelineBatch.cancel': 'Cancel',
  'timelineBatch.titlePlaceholder': 'Event title',

  // === Calendar AI Tab (054) ===
  'calendarAi.unavailable': 'Calendar template unavailable',
  'calendarAi.worldDescription': 'World description',
  'calendarAi.worldPlaceholder': 'Describe the world: setting, seasons, features...',
  'calendarAi.minChars': 'Min. 10 characters',
  'calendarAi.generating': 'Generating...',
  'calendarAi.generate': 'Generate calendar',
  'calendarAi.preview': 'Calendar preview',
  'calendarAi.calendarName': 'Name',
  'calendarAi.yearLabel': 'Year label',
  'calendarAi.epoch': 'Epoch',
  'calendarAi.months': 'Months',
  'calendarAi.accept': 'Accept',
  'calendarAi.regenerate': 'Regenerate',
  'calendarAi.replaceTitle': 'Replace calendar?',
  'calendarAi.replaceWarning': 'The current calendar will be replaced. This action cannot be undone.',
  'calendarAi.replaceConfirm': 'Replace',

  // === Timeline Link Picker (054) ===
  'linkPicker.add': 'Add link',
  'linkPicker.search': 'Search...',
  'linkPicker.remove': 'Remove',
  'linkPicker.limitReached': 'Link limit reached',
  'linkPicker.journal': 'Journal',
  'linkPicker.characters': 'Characters',
  'linkPicker.pins': 'Map pins',
  'linkPicker.noResults': 'No results found',

  // === AI Map Dialog ===
  'aiMap.title': 'AI Map Generation',
  'aiMap.description': 'Enter a prompt to generate a map. You can draw a sketch for a more precise result.',
  'aiMap.promptLabel': 'Prompt',
  'aiMap.placeholder': 'Describe the map: \'A dungeon with long corridors and three rooms\'',
  'aiMap.noStrokesHint': 'Without a sketch, the map will be generated from text only.',
  'aiMap.errorGeneration': 'Generation error',
  'aiMap.errorNetwork': 'Network error. Try again.',
  'aiMap.generating': 'Generating map... This may take up to 2 minutes.',
  'aiMap.preview': 'Preview',
  'aiMap.regenerate': 'Regenerate',
  'aiMap.accept': 'Accept',
  'aiMap.generate': 'Generate Map',
  'aiMap.altText': 'Generated map',

  // === AI Inpainting ===
  'inpaint.promptPlaceholder': 'Describe what should appear in the selected area...',
  'inpaint.generate': 'Generate',
  'inpaint.accept': 'Accept',
  'inpaint.regenerate': 'Regenerate',
  'inpaint.cancel': 'Cancel',
  'inpaint.generating': 'Generating...',
  'inpaint.tooSmall': 'Area too small for inpainting',
  'inpaint.tooLarge': 'To replace the entire map, use map generation',
  'inpaint.errorGeneration': 'Inpainting generation error',
  'inpaint.errorNetwork': 'Network error',

  // === AI Outpainting ===
  'outpaint.title': 'Expand Map',
  'outpaint.directionTop': 'Up',
  'outpaint.directionRight': 'Right',
  'outpaint.directionBottom': 'Down',
  'outpaint.directionLeft': 'Left',
  'outpaint.expandSize': 'Expand Size',
  'outpaint.overlap': 'Overlap',
  'outpaint.prompt': 'Describe the expanded area',
  'outpaint.generate': 'Generate',
  'outpaint.regenerate': 'Regenerate',
  'outpaint.accept': 'Accept',
  'outpaint.cancel': 'Cancel',
  'outpaint.generating': 'Generating...',
  'outpaint.sizeWarning': 'Result will exceed 4096px',
  'outpaint.errorGeneration': 'Generation error',
  'outpaint.errorNetwork': 'Network error',
  'outpaint.noMap': 'Upload a map before expanding',
  'outpaint.expandMap': 'Expand Map',

  // === AI Detail Pass ===
  'detail.dialogTitle': 'Generate Detail Map',
  'detail.cropLabel': 'Map Fragment',
  'detail.semanticLabel': 'Fragment Description (auto)',
  'detail.promptPlaceholder': 'Describe what should be on the detail map...',
  'detail.generate': 'Generate',
  'detail.accept': 'Accept',
  'detail.regenerate': 'Regenerate',
  'detail.cancel': 'Cancel',
  'detail.existingLink': 'A map already exists for this area: {name}',
  'detail.createNew': 'Create New',
  'detail.regenerateExisting': 'Regenerate Existing',
  'detail.errorSmallRegion': 'Area too small (minimum 64×64)',
  'detail.errorNoMap': 'Page has no map',
  'detail.generating': 'Generating...',
  'detail.accepting': 'Creating page...',

  // === SRD Panel ===
  'srd.title': 'SRD',
  'srd.monsters': 'Monsters',
  'srd.spells': 'Spells',
  'srd.classes': 'Classes',
  'srd.races': 'Races',
  'srd.equipment': 'Equipment',
  'srd.magicItems': 'Magic Items',
  'srd.conditions': 'Conditions',
  'srd.searchPlaceholder': 'Search...',
  'srd.loading': 'Loading...',
  'srd.results': 'results',
  'srd.noResults': 'Nothing found',
  'srd.loadMore': 'Load More',
  'srd.back': 'Back',
  'srd.allCR': 'All CR',
  'srd.allTypes': 'All Types',
  'srd.allLevels': 'All Levels',
  'srd.cantrip': 'Cantrip',
  'srd.level': 'Lv.',
  'srd.concentrationAbbr': 'C',
  'srd.allSchools': 'All Schools',
  'srd.allClasses': 'All Classes',
  'srd.allCategories': 'All Categories',
  'srd.allRarities': 'All Rarities',
  'srd.noFeaturesAtLevel': 'No new features at this level',
  'srd.token': 'Token',
  'srd.plusSheet': '+ Sheet',

  // === Journal ===
  'journal.empty': 'No characters. Create one in the Map tab.',
  'journal.pcGroup': 'Player Characters',
  'journal.npcGroup': 'NPC',
  // Journal panel
  'journal.panel.title': 'Journal',
  'journal.panel.createEntry': 'Create entry',
  'journal.panel.allTypes': 'All types',
  'journal.panel.searchPlaceholder': 'Search…',
  'journal.panel.noEntries': 'No entries yet',
  'journal.panel.noEntriesHint': 'Press "+" to create the first entry',
  'journal.panel.noResults': 'Nothing found',
  // Journal types
  'journal.type.npc': 'NPC',
  'journal.type.location': 'Location',
  'journal.type.quest': 'Quest',
  'journal.type.faction': 'Faction',
  'journal.type.handout': 'Handout',
  'journal.type.lore': 'Lore',
  // Journal sections (player view)
  'journal.section.quests': 'Quests',
  'journal.section.myNotes': 'My Notes',
  'journal.section.knowledge': 'Knowledge',
  'journal.section.onThisMap': 'On this map',
  // Journal groups
  'journal.group.ungrouped': 'No section',
  'journal.group.new': 'New section',
  'journal.group.namePlaceholder': 'Section name',
  'journal.group.delete': 'Delete section',
  'journal.group.rename': 'Rename section',
  // Journal visibility
  'journal.visibility.gm': 'GM only',
  'journal.visibility.personal': 'Personal',
  'journal.visibility.all': 'All',
  // Journal context menu
  'journal.menu.edit': 'Edit',
  'journal.menu.reveal': 'Reveal to players',
  'journal.menu.hide': 'Hide from players',
  'chat.system.conditionEnded': '{name}: condition ended — {labels}',
  'chat.system.entryRevealed': '📖 Entry "{name}" is now visible to all',
  'chat.system.hitDie': '{name} — hit die {die}: {roll} → +{healed}, HP {hp}',
  'chat.system.deathSave.success': '{name} — death save: {roll}, success {tally}',
  'chat.system.deathSave.failure': '{name} — death save: {roll}, failure {tally}',
  'chat.system.deathSave.nat20': '{name} — death save: natural 20, regains 1 hit point',
  'chat.system.deathSave.nat1': '{name} — death save: natural 1, two failures {tally}',
  'chat.system.deathSave.stabilized': '{name} — death save: {roll}, third success — stable',
  'chat.system.deathSave.died': '{name} — death save: {roll}, third failure — dies',
  'error.character_dead': 'The character is dead: rest, hit dice, healing and levels do not help — a resurrection is needed: the GM presses «Resurrect» in the death saves card',
  'error.exhausted': 'No charges or slots of this level left — a rest restores them',
  'error.rest_at_zero_hp': 'A long rest needs at least 1 hit point: stabilise and heal the character first',
  'journal.menu.pin': 'Pin',
  'journal.menu.unpin': 'Unpin',
  'journal.menu.delete': 'Delete',
  // Journal delete dialog
  'journal.delete.title': 'Delete entry?',
  'journal.delete.description': 'This action cannot be undone. The entry will be permanently deleted.',
  'journal.delete.cancel': 'Cancel',
  'journal.delete.confirm': 'Delete',
  // Journal create dialog
  'journal.create.title': 'New entry',
  'journal.create.namePlaceholder': 'Entry name…',
  'journal.create.submit': 'Create',
  // Journal preview fields
  'journal.preview.race': 'Race',
  'journal.preview.occupation': 'Occupation',
  'journal.preview.disposition': 'Disposition',
  'journal.preview.description': 'Description',
  'journal.preview.secrets': 'Secrets',
  'journal.preview.personality': 'Personality',
  'journal.preview.questHooks': 'Quest hooks',
  'journal.preview.alive': 'Alive',
  'journal.preview.dead': 'Dead',
  'journal.preview.objectives': 'Objectives',
  'journal.preview.gmNotes': 'GM Notes',
  'journal.preview.rewards': 'Rewards',
  'journal.preview.attitude': 'Attitude',
  'journal.preview.content': 'Content',
  'journal.preview.category': 'Category',
  // Journal disposition
  'journal.disposition.friendly': 'Friendly',
  'journal.disposition.neutral': 'Neutral',
  'journal.disposition.hostile': 'Hostile',
  'journal.disposition.unknown': 'Unknown',

  // Journal editor
  'journal.editor.namePlaceholder': 'Entry name',
  'journal.editor.gmOnly': 'GM only',
  'journal.editor.reveal': 'Reveal to players',
  'journal.editor.hide': 'Hide from players',
  'journal.editor.revealedFields': 'Field visibility',
  'journal.editor.revealedFieldsHint': 'Select fields visible to players',
  'journal.editor.revealConfirm': 'Reveal "{name}" to players? Players will be notified.',
  'journal.editor.revealConfirmAction': 'Reveal',
  'journal.editor.pin': 'Pin',
  'journal.editor.unpin': 'Unpin',
  'journal.editor.openEditor': 'Open in editor',
  'journal.editor.visibilityGm': 'GM only',
  'journal.editor.visibilityAll': 'All players',
  'journal.editor.tags': 'Tags',
  'journal.editor.tagsPlaceholder': 'Add tag...',
  'journal.editor.addItem': 'Add',
  'journal.editor.removeItem': 'Remove',
  'journal.editor.edit': 'Edit',
  'journal.editor.done': 'Done',
  'journal.editor.more': 'More',
  'journal.editor.bindToPin': 'Bind to pin',
  'journal.editor.bindToSheet': 'Bind character sheet',
  'journal.editor.bindToPage': 'Bind to map',
  'journal.editor.configureFields': 'Configure visible fields',
  'journal.editor.noContent': 'No content',

  // Journal form: NPC
  'journal.form.npc.portrait': 'Portrait (URL)',
  'journal.form.npc.race': 'Race',
  'journal.form.npc.occupation': 'Occupation',
  'journal.form.npc.disposition': 'Disposition',
  'journal.form.npc.description': 'Description',
  'journal.form.npc.personality': 'Personality',
  'journal.form.npc.secrets': 'Secrets',
  'journal.form.npc.questHooks': 'Quest Hooks',
  'journal.form.npc.alive': 'Alive',

  // Journal form: Location
  'journal.form.location.image': 'Image (URL)',
  'journal.form.location.parent': 'Parent location',
  'journal.form.location.description': 'Description',
  'journal.form.location.secrets': 'Secrets',
  'journal.form.location.page': 'Map page',

  // Journal form: Quest
  'journal.form.quest.status': 'Status',
  'journal.form.quest.questGiver': 'Quest giver',
  'journal.form.quest.description': 'Description',
  'journal.form.quest.gmNotes': 'GM Notes',
  'journal.form.quest.objectives': 'Objectives',
  'journal.form.quest.objectiveText': 'Objective text',
  'journal.form.quest.objectiveCompleted': 'Completed',
  'journal.form.quest.objectiveHidden': 'Hidden',
  'journal.form.quest.objectiveDescription': 'Objective description',
  'journal.objective.saving': 'Saving…',
  'journal.form.quest.rewards': 'Rewards',

  // Journal form: Faction
  'journal.form.faction.description': 'Description',
  'journal.form.faction.attitude': 'Attitude',
  'journal.form.faction.secrets': 'Secrets',

  // Journal form: Handout
  'journal.form.handout.content': 'Content',
  'journal.form.handout.image': 'Image (URL)',
  'journal.form.handout.gmNotes': 'GM Notes',

  // Journal form: Lore
  'journal.form.lore.category': 'Category',
  'journal.form.lore.content': 'Players see',
  'journal.form.lore.gmNotes': 'For the GM',

  // Journal form: Disposition options
  'journal.form.disposition.friendly': 'Friendly',
  'journal.form.disposition.neutral': 'Neutral',
  'journal.form.disposition.hostile': 'Hostile',
  'journal.form.disposition.unknown': 'Unknown',

  // Journal form: Quest status options
  'journal.form.questStatus.active': 'Active',
  'journal.form.questStatus.completed': 'Completed',
  'journal.form.questStatus.failed': 'Failed',
  'journal.form.questStatus.hidden': 'Hidden',

  // Journal: Links
  'journal.links.title': 'Links',
  'journal.links.add': 'Add',
  'journal.links.empty': 'No links. Click + to add.',
  'journal.links.relation': 'Relation',
  'journal.links.target': 'Target entry',
  'journal.links.customRelation': 'Custom',
  'journal.links.remove': 'Remove link',
  'journal.links.incoming': 'Referenced by',

  // Journal field labels (for revealedFields checkboxes)
  'journal.field.description': 'Description',
  'journal.field.race': 'Race',
  'journal.field.occupation': 'Occupation',
  'journal.field.personality': 'Personality',
  'journal.field.secrets': 'Secrets',
  'journal.field.questHooks': 'Quest Hooks',
  'journal.field.alive': 'Alive',
  'journal.field.attitude': 'Attitude',
  'journal.field.content': 'Content',
  'journal.field.gmNotes': 'GM Notes',
  'journal.field.objectives': 'Objectives',
  'journal.field.rewards': 'Rewards',
  'journal.field.status': 'Status',
  'journal.field.category': 'Category',
  'journal.field.image': 'Image',
  'journal.field.disposition': 'Disposition',

  // Journal: Map integration
  'journal.map.bindNote': 'Bind to map pin',
  'journal.map.noteNotFound': 'Pin not found',
  'journal.map.goToMap': 'Go to map',
  'journal.map.pageNotFound': 'Page not found',

  // Journal: NPC CharacterSheet
  'journal.npc.bindSheet': 'Bind sheet',
  'journal.npc.linkedSheet': 'Linked sheet',
  'journal.npc.sheetDeleted': 'Sheet deleted',
  'journal.npc.openSheet': 'Open',
  'journal.npc.unbindSheet': 'Unbind',

  // Journal: Relation labels
  'journal.relation.lives_in': 'Lives in',
  'journal.relation.quest_giver': 'Quest giver',
  'journal.relation.member_of': 'Member of',
  'journal.relation.enemy_of': 'Enemy of',

  // === Sheet View ===
  'sheet.speed': 'Speed',
  'sheet.initiativeMod': 'Initiative',
  'sheet.tokenDescription': 'Token Description',
  'sheet.tokenDescriptionPlaceholder': 'Describe how your character looks to others',
  'sheet.tokenDescriptionHint': 'Markdown supported. Visible to all participants.',
  'sheet.fullForm': 'Full Form',
  'sheet.statBlock': 'Stat Block',
  'sheet.tokenActions': 'Token Actions',
  'sheet.actionName': 'Name',
  'sheet.actionExpression': 'Expression (1d20+5)',
  'sheet.addAction': '+ Add',
  'sheet.hitDice': 'Hit Dice',
  'sheet.spendHitDie': 'Spend Hit Die',
  'sheet.featuresTraits': 'Features & Traits',
  'sheet.uses': 'uses',
  'sheet.rest': 'rest',
  'sheet.shortRest': 'Short Rest',
  'sheet.longRest': 'Long Rest',
  'sheet.levelUp': 'Level Up',
  'sheet.encumbered': 'Encumbered!',
  'sheet.rollTooltip': 'Roll',
  'sheet.proficiency': 'proficiency',

  // === Character Wizard ===
  'wizard.createTitle': 'Create Character',
  'wizard.step': 'Step',
  'wizard.createCharacter': 'Create Character',
  'wizard.stepRace': 'Race',
  'wizard.stepClass': 'Class',
  'wizard.stepAbilities': 'Abilities',
  'wizard.stepSkills': 'Skills',
  'wizard.stepEquipment': 'Equipment',
  'wizard.stepSpells': 'Spells',
  'wizard.stepReview': 'Review',
  'wizard.stepBackground': 'Background',
  'wizard.createDescription': 'Every level 1 choice of the 5e rules: race, class, abilities, background, skills, equipment, spells, look and personality.',
  'wizard.rebuildSheet': 'Rebuild this sheet',
  'wizard.seededSaved': 'The choices this character was made with: change what you need and rebuild the sheet',
  'wizard.seededRead': 'Read back from the sheet: race, class, background, abilities, spells. Pick the skills, languages and equipment again',
  'wizard.stepIncomplete': 'This step is not complete',
  'wizard.abilityPlus': '+1 to {n} abilities of your choice',
  'wizard.ac': 'AC',
  'wizard.age': 'Age',
  'wizard.alignment': 'Alignment',
  'wizard.alsoGet': 'Also',
  'wizard.alwaysPrepared': 'always prepared',
  'wizard.ancestry': 'Draconic ancestry',
  'wizard.appearance': 'Appearance',
  'wizard.attacks': 'Attacks',
  'wizard.backstory': 'Backstory',
  'wizard.bonds': 'Bonds',
  'wizard.cantrips': 'Cantrips',
  'wizard.classSkills': 'Class skills ({class})',
  'wizard.concentration': 'concentration',
  'wizard.cost': 'cost',
  'wizard.darkvision': 'Darkvision',
  'wizard.domainSpells': 'Domain spells',
  'wizard.equipment': 'Equipment',
  'wizard.expertise': 'Expertise: {n} skills (proficiency bonus ×2)',
  'wizard.eyes': 'Eyes',
  'wizard.favoredEnemy': 'Favored enemy',
  'wizard.favoredTerrain': 'Favored terrain',
  'wizard.features': 'Features',
  'wizard.fixedSkills': 'Already granted (background, race)',
  'wizard.flaws': 'Flaws',
  'wizard.fromBackground': 'From the background',
  'wizard.ft': 'ft',
  'wizard.gender': 'Gender',
  'wizard.gp': 'gp',
  'wizard.grantedSkills': 'Skills',
  'wizard.hair': 'Hair',
  'wizard.height': 'Height',
  'wizard.hitDice': 'Hit dice',
  'wizard.hp': 'Hit points',
  'wizard.ideals': 'Ideals',
  'wizard.initiative': 'Initiative',
  'wizard.languages': 'Languages',
  'wizard.level1Features': 'Level 1 features',
  'wizard.manualRange': 'Ability scores go from 3 to 18',
  'wizard.missingTitle': 'Still to choose before creating:',
  'wizard.ofChoice': 'of your choice',
  'wizard.passivePerception': 'Passive Perception',
  'wizard.personalityTraits': 'Personality traits',
  'wizard.pickLanguages': 'Languages of your choice',
  'wizard.pickTools': 'Tools of your choice',
  'wizard.pointsLeft': 'Points left',
  'wizard.prepareFromBook': 'Prepare from the spellbook: {n}',
  'wizard.profBonus': 'Proficiency bonus',
  'wizard.proficiencies': 'Proficiencies',
  'wizard.raceCantrip': 'High elf cantrip (wizard list)',
  'wizard.raceSkills': 'Race skills of your choice: {n}',
  'wizard.raceTraits': 'Race traits',
  'wizard.reroll': 'Re-roll all (once)',
  'wizard.resistances': 'Resistances',
  'wizard.roll': 'Roll 4d6 six times',
  'wizard.rolled': 'Rolled',
  'wizard.saves': 'Saves',
  'wizard.skills': 'Skills',
  'wizard.skillsCount': 'skills: {n}',
  'wizard.slots': 'level {n} slots',
  'wizard.spellAttack': 'Spell attack',
  'wizard.spellDc': 'Spell save DC',
  'wizard.spellInfo': 'Spell description',
  'wizard.spellcasting': 'spellcasting',
  'wizard.subrace': 'Subrace',
  'wizard.summary': 'Summary — as the sheet will show it',
  'wizard.tools': 'Tools',
  'wizard.weight': 'Weight',
  'wizard.shortRest': 'short rest',
  'wizard.longRest': 'long rest',
  'wizard.spellsHintWizard': 'The spellbook holds 6 level 1 spells; {n} of them can be prepared (INT + level).',
  'wizard.spellsHintKnown': 'Known spells are always ready to cast.',
  'wizard.spellsHintPrepared': 'Clerics and druids prepare {n} spells (modifier + level) from the whole class list; they change after a long rest.',
  'wizard.spellbook': 'Spellbook (level 1)',
  'wizard.spellsKnown': 'Known level 1 spells',
  'wizard.spellsPrepared': 'Prepared level 1 spells',
  'wizard.any.simple': 'Any simple weapon',
  'wizard.any.simple-melee': 'Any simple melee weapon',
  'wizard.any.martial': 'Any martial weapon',
  'wizard.any.martial-melee': 'Any martial melee weapon',
  'wizard.any.instrument': 'Any musical instrument',
  'wizard.any.arcane-focus': 'Arcane focus',
  'wizard.any.druidic-focus': 'Druidic focus',
  'wizard.any.holy-symbol': 'Holy symbol',
  'wizard.dragon.Black': 'Black',
  'wizard.dragon.Blue': 'Blue',
  'wizard.dragon.Brass': 'Brass',
  'wizard.dragon.Bronze': 'Bronze',
  'wizard.dragon.Copper': 'Copper',
  'wizard.dragon.Gold': 'Gold',
  'wizard.dragon.Green': 'Green',
  'wizard.dragon.Red': 'Red',
  'wizard.dragon.Silver': 'Silver',
  'wizard.dragon.White': 'White',
  'wizard.method.standard-array': 'Standard array',
  'wizard.method.point-buy': 'Point buy',
  'wizard.method.roll': 'Roll 4d6',
  'wizard.method.manual': 'Manual',
  'wizard.methodHint.standard-array': 'Assign 15, 14, 13, 12, 10, 8 — each once.',
  'wizard.methodHint.point-buy': '27 points; each score 8 to 15 before race bonuses.',
  'wizard.methodHint.roll': 'Six rolls of 4d6 dropping the lowest die; assign the results.',
  'wizard.methodHint.manual': 'Your own values from 3 to 18 (e.g. agreed with the GM).',
  'wizard.missing.race': 'race',
  'wizard.missing.race.subrace': 'subrace',
  'wizard.missing.race.ancestry': 'draconic ancestry',
  'wizard.missing.race.abilityPlus': '+1 to two abilities (race)',
  'wizard.missing.race.skills': 'race skills',
  'wizard.missing.race.cantrip': 'race cantrip',
  'wizard.missing.class': 'class',
  'wizard.missing.class.subclass': 'subclass (domain, origin, patron)',
  'wizard.missing.class.option': 'feature choice',
  'wizard.missing.class.favoredEnemy': 'favored enemy',
  'wizard.missing.class.favoredTerrain': 'favored terrain',
  'wizard.missing.class.expertise': 'expertise',
  'wizard.missing.skills': 'class skills',
  'wizard.missing.background': 'background',
  'wizard.missing.languages': 'languages',
  'wizard.missing.tools': 'tools',
  'wizard.missing.abilities': 'ability scores (3–18, all six)',
  'wizard.missing.abilities.pointBuy': 'point buy: at most 27, 8 to 15',
  'wizard.missing.spells.cantrips': 'cantrips',
  'wizard.missing.spells.level1': 'level 1 spells',
  'wizard.missing.spells.prepared': 'prepared spells',
  'wizard.missing.name': 'name',
  'wizard.subclass.cleric': 'Divine domain',
  'wizard.subclass.sorcerer': 'Sorcerous origin',
  'wizard.subclass.warlock': 'Otherworldly patron',
  'wizard.enemy.aberrations': 'Aberrations',
  'wizard.enemy.beasts': 'Beasts',
  'wizard.enemy.celestials': 'Celestials',
  'wizard.enemy.constructs': 'Constructs',
  'wizard.enemy.dragons': 'Dragons',
  'wizard.enemy.elementals': 'Elementals',
  'wizard.enemy.fey': 'Fey',
  'wizard.enemy.fiends': 'Fiends',
  'wizard.enemy.giants': 'Giants',
  'wizard.enemy.monstrosities': 'Monstrosities',
  'wizard.enemy.oozes': 'Oozes',
  'wizard.enemy.plants': 'Plants',
  'wizard.enemy.undead': 'Undead',
  'wizard.enemy.humanoids-two-races': 'Humanoids (two races)',
  'wizard.terrain.arctic': 'Arctic',
  'wizard.terrain.coast': 'Coast',
  'wizard.terrain.desert': 'Desert',
  'wizard.terrain.forest': 'Forest',
  'wizard.terrain.grassland': 'Grassland',
  'wizard.terrain.mountain': 'Mountain',
  'wizard.terrain.swamp': 'Swamp',
  'wizard.terrain.underdark': 'Underdark',

  // === Conditions (D&D) ===
  'condition.blinded': 'Blinded',
  'condition.charmed': 'Charmed',
  'condition.deafened': 'Deafened',
  'condition.frightened': 'Frightened',
  'condition.grappled': 'Grappled',
  'condition.incapacitated': 'Incapacitated',
  'condition.invisible': 'Invisible',
  'condition.paralyzed': 'Paralyzed',
  'condition.petrified': 'Petrified',
  'condition.poisoned': 'Poisoned',
  'condition.prone': 'Prone',
  'condition.restrained': 'Restrained',
  'condition.stunned': 'Stunned',
  'condition.unconscious': 'Unconscious',
  'condition.exhaustion': 'Exhaustion',
  'condition.concentration': 'Concentration',

  // === Toast messages ===
  'toast.mapUploaded': 'Map uploaded:',
  'toast.mapUploadError': 'Map upload error',
  'toast.tokenAdded': 'Token added:',
  'toast.tokenCreated': 'Token created:',
  'toast.sessionSaved': 'Session saved',
  'toast.sessionLoaded': 'Session loaded',

  // === Server Error Codes ===
  'error.INVALID_TOKEN': 'Invalid token',
  'error.USER_NOT_FOUND': 'User not found',
  'error.sessionError': 'Session error',
  'error.SESSION_NOT_FOUND': 'Session not found',
  'error.NOT_AUTHORIZED': 'Not authorized',
  'error.UNAUTHORIZED': 'Not authorized',
  'error.NO_SKETCH': 'No sketch uploaded',
  'error.INVALID_PROMPT': 'Prompt is required (1-1000 chars)',
  'error.PAGE_NOT_FOUND': 'Page not found',
  'error.AI_UNAVAILABLE': 'AI provider unavailable',
  'error.TIMEOUT': 'Generation timed out. Try again.',
  'error.AI_ERROR': 'Generation error. Try again.',
  'error.AUTH_REQUIRED': 'Sign in required',
  'error.BAD_REQUEST': 'Bad request',
  'error.NOT_FOUND': 'Not found',
  'error.FORBIDDEN': 'You don\u2019t have permission for this action',

  // === Pluralization ===
  'plural.participant_one': 'participant',
  'plural.participant_few': 'participants',
  'plural.participant_many': 'participants',
  'plural.day_one': 'day',
  'plural.day_few': 'days',
  'plural.day_many': 'days',

  // === AppShell session bar ===
  'session.label': 'Session:',
  'session.participantsLabel': 'Participants:',
  'session.disconnect': 'Disconnect',
  'session.expandSidebar': 'Expand sidebar',
  'session.collapseSidebar': 'Collapse sidebar',
  'session.detachPanel': 'Detach panel',
  'session.panelDetached': 'Panel opened in separate window',
  'session.roleGM': 'GM',
  'session.rolePlayer': 'Player',
  'session.connectionLost': 'Connection lost. Reconnecting...',
  'session.connectionRestored': 'Connection restored',
  'session.sessionCreated': 'Session created',
  'session.sessionJoined': 'You joined the session',
  'session.participantJoined': 'joined the session',
  'session.participantLeft': 'left the session',
  'session.pageCreated': 'created',
  'session.sheetCreated': 'created',

  // === Input Dialog ===
  'inputDialog.cancel': 'Cancel',
  'inputDialog.confirm': 'Confirm',
  'inputDialog.tooLong': 'At most {max} characters — the server will not accept more.',

  // === Confirm Dialog ===
  'confirmDialog.replaceBinding': 'Replace Binding',
  'confirmDialog.replaceBindingDesc': 'is already bound to a sheet. Replace binding with',
  'confirmDialog.replace': 'Replace',

  // === AppDialogs ===
  'appDialogs.loading': 'Loading...',

  // === File upload ===
  'upload.mapLoaded': 'Map loaded:',
  'upload.mapError': 'Map upload error',
  'upload.tokenAdded': 'Token added:',

  // === Misc ===
  'misc.drawingsCleared': 'Drawings cleared',
  'misc.sessionSaved': 'Session saved',
  'misc.sessionLoaded': 'Session loaded',
  'misc.loadError': 'File load error',
  'misc.hpDialogTitle': 'HP for',
  'misc.hpDialogHint': 'A number is the new total. «-4» or «+3» is damage or healing from the current.',
  'misc.resizeDialogTitle': 'Size (cells): 0.5, 1, 2, 3, 4:',
  'misc.newTokenDialogTitle': 'New token name:',

  // === AI Confirm Card ===
  'aiConfirm.details': 'Details',
  'aiConfirm.cancel': 'Cancel',
  'aiConfirm.create': 'Create',

  // === Chat Roll ===
  'chatRoll.crit': 'Crit!',
  'chatRoll.fumble': 'Fumble!',
  'chatRoll.damage': 'Damage',

  // === Condition Tooltip ===
  'conditionTooltip.noDescription': 'No description available',

  // === Confirm Dialog defaults ===
  'confirmDialog.confirm': 'Confirm',
  'confirmDialog.cancel': 'Cancel',

  // === Effects Panel ===
  'effects.title': 'Active Effects',
  'effects.empty': 'No active effects',
  'effects.enable': 'Enable',
  'effects.disable': 'Disable',
  'effects.remove': 'Remove',
  'effects.addEffect': '+ Add Effect',
  'effects.source': 'Source',
  'effects.target': 'Target',
  'effects.mode': 'Mode',
  'effects.value': 'Value',
  'effects.add': 'Add',

  // === AI Map generation count ===
  'aiMap.generationCount': 'generation',

  // === Note Editor placeholders ===
  'notes.titlePlaceholder': 'Note title...',
  'notes.contentPlaceholder': 'Note text (markdown)...',
  'notes.noContent': 'No content',
  'notes.quickCreate': 'Quick Create',
  'notes.setParent': 'Move under...',
  'notes.moveToRoot': 'Move to root',

  // === Map Pins ===
  'pins.title': 'Map Pins',
  'pins.empty': 'No pins. Click the map to place one.',
  'pins.newPin': 'New Pin',
  'pins.editPin': 'Edit Pin',
  'pins.icon': 'Icon',
  'pins.color': 'Color',
  'pins.selectColor': 'Select color',
  'pins.gmOnly': 'GM only',
  'pins.ownerOnly': 'Owner only',
  'pins.visibility': 'Visibility',
  'pins.linkedEntry': 'Linked journal entry',
  'pins.orphan': 'No linked entry',
  'pins.unlinkEntry': 'Unlink entry',
  'pins.linkEntry': 'Link entry...',
  'pins.delete': 'Delete pin',
  'pins.deleteConfirm': 'Delete this pin?',
  'pins.deleteConfirmDesc': 'The pin disappears from the map for everyone. A linked journal entry stays.',
  'pins.created': 'Pin created',
  'pins.deleted': 'Pin deleted',
  'pins.limitReached': 'Pin limit reached (max 100 per page)',
  'pins.quickCreate': 'Quick Create',
  'pins.noEntry': 'No entry',
  'pins.journalEntry': 'Journal Entry',
  'pins.selectEntry': 'Select entry...',
  'pins.visibilityAll': 'Visible to all',
  'pins.showAskTitle': 'Show to the players?',
  'pins.showAskBody': 'The players will see this marker and its text. You can take it back any time by choosing «GM only».',

  // === Fullscreen Mini Panel ===
  'fullscreen.round': 'Round:',
  'fullscreen.nextTurn': 'Next Turn',
  'fullscreen.exitFullscreen': 'Exit fullscreen mode',

  // === Location Breadcrumb ===
  'breadcrumb.back': '\u2190 Back',

  // === Navigate Request Toast ===
  'navigate.wantsToGoTo': 'wants to navigate to',
  'navigate.deny': 'Deny',
  'navigate.approve': 'Approve',

  // === NPC Stat Block ===
  'npc.conditions': 'Conditions:',
  'npc.attacks': 'Attacks',
  'npc.rollAbility': 'Roll',
  'npc.checkAbility': 'Check',
  'npc.rollAttack': 'Roll attack',
  'npc.abilityStr': 'STR',
  'npc.abilityDex': 'DEX',
  'npc.abilityCon': 'CON',
  'npc.abilityInt': 'INT',
  'npc.abilityWis': 'WIS',
  'npc.abilityCha': 'CHA',

  // === PopoutWindow ===
  'popout.close': 'Close',

  // === Portrait Upload ===
  'portrait.fileTooLarge': 'File too large (max 10 MB)',
  'portrait.networkError': 'Network error',
  'portrait.alt': 'Portrait',

  // === Repeating Section ===
  'repeating.add': '+ Add',
  'repeating.empty': 'No entries',
  'repeating.remove': 'Remove',
  'repeating.showAll': 'Show all',
  'repeating.hidden': 'hidden',

  // === Selection Bar ===
  'selectionBar.inspect': 'Inspect',
  'selectionBar.edit': 'Edit',
  'selectionBar.conditions': 'Conditions',
  'selectionBar.delete': 'Delete',

  // === Sheet Modal ===
  'sheetModal.popout': 'Open in separate window',
  'sheetModal.templateNotFound': 'Template not found',

  // === Spell Slots Widget ===
  'spells.slotsTitle': 'Spell Slots',
  'spells.noSlots': 'No spell slots',
  'spells.level': 'Level',
  'spells.spendSlot': 'Spend slot',
  'spells.spent': 'Spent',

  // === Token Inspect Popup ===
  'tokenInspect.gmNotes': 'GM Notes (hidden from players)',
  'tokenInspect.openSheet': 'Open Character Sheet',
  'tokenInspect.ac': 'AC:',

  // === Sidebar detached tab ===
  'sidebar.detached': 'detached',
  'sidebar.notification': 'New notification',

  // === SrdPanel filter placeholders ===
  'srd.filterType': 'Type',
  'srd.filterLevel': 'Level',
  'srd.filterSchool': 'School',
  'srd.filterClass': 'Class',
  'srd.filterCategory': 'Category',
  'srd.filterRarity': 'Rarity',

  // === Dialog close ===
  'dialog.close': 'Close',

  // === Token Panel preset ===
  'tokens.presetToken': 'Token',

  // === Session Page Tabs ===
  'sessionPage.sessionsTab': 'Sessions',
  'sessionPage.profileTab': 'Profile',

  // === App (lobby) ===
  'app.profile': 'Profile',
  'app.logout': 'Sign Out',
  'app.joinByCode': 'Join by Code',

  // === AI Error Messages ===
  'aiError.serviceUnavailable': 'AI service is temporarily unavailable',
  'aiError.noModelKey': 'No model key: Tia cannot answer. Set the key on the server.',
  'aiError.noModelPlayer': 'Tia is not answering now: the GM has no model connected.',
  'aiError.rateLimit': 'Too many requests. Please wait a minute.',
  'aiError.networkError': 'Network error. Check your connection.',
  'aiError.timeout': 'Timeout: no response from AI',
  'aiError.sendError': 'Error sending message',
  'aiError.gmOnly': 'Only the GM can use AI',
  'aiError.voicePermissionDenied': 'Microphone access denied. Allow in browser settings.',
  'aiError.voiceNotAvailable': 'Voice input is not available.',
  'aiError.tokenBudget': 'Token budget exceeded. Try again later.',
  'aiError.playerAiDisabled': 'Player AI is disabled by the GM.',

  // === Player AI ===
  'playerAi.title': 'Tia',
  'playerAi.send': 'Send',
  'playerAi.newChat': 'New chat',

  // === Game System ===
  'gameSystem.label': 'Game System',
  'gameSystem.dnd5e': 'D&D 5e',
  'gameSystem.pf2e': 'Pathfinder 2e',
  'gameSystem.generic': 'Generic',
  'srd.unavailable': 'SRD data is available only for D&D 5e',

  // === Auth Errors ===
  'authError.loginError': 'Login error',
  'authError.registerError': 'Registration error',
  'authError.updateError': 'Profile update error',
  'authError.networkError': 'Network error',

  // === Chat ===
  'chat.defaultName': 'GM',

  // === Chat Sound Settings ===
  'chat.sound.settings': 'Chat Sound Settings',
  'chat.sound.volume': 'Volume',
  'chat.sound.mute': 'Mute sounds',
  'chat.sound.chatEnabled': 'Message sounds',
  'chat.sound.diceEnabled': 'Dice sounds',

  // === Ruler / Measurement ===
  'ruler.ft': 'ft',
  'ruler.total': 'Total:',

  // === Token copy ===
  'tokens.copySuffix': '(copy)',

  // === Sidebar: Alerts ===
  'sidebar.alerts': 'Alerts',

  // === Sidebar: Timeline ===
  'sidebar.timeline': 'Chronicle',
  'sidebar.plan': 'Game plan',

  // === Alerts ===
  'alerts.title': 'Alerts',
  'alerts.create': 'Create Alert',
  'alerts.edit': 'Edit Alert',
  'alerts.delete': 'Delete',
  'alerts.trigger': 'Play',
  'alerts.emptyTitle': 'No alerts',
  'alerts.emptyHint': 'Create your first alert to enhance the atmosphere',
  'alerts.presets': 'Presets',
  'alerts.triggerActive': 'Auto-trigger active',
  'alerts.nameLabel': 'Name',
  'alerts.visualLabel': 'Visual (GIF/WebM/PNG/JPEG/WebP)',
  'alerts.soundLabel': 'Sound (MP3/OGG)',
  'alerts.layoutLabel': 'Layout',
  'alerts.durationLabel': 'Duration',
  'alerts.volume': 'Volume',
  'alerts.triggerLabel': 'Trigger',
  'alerts.customEventPlaceholder': 'Event name',
  'alerts.triggerEnabledLabel': 'Enable auto-trigger',
  'alerts.deleteConfirmTitle': 'Delete alert?',
  'alerts.deleteConfirmDesc': 'The alert and associated media files will be permanently deleted.',
  'alerts.preset.critical-hit': 'Critical Hit',
  'alerts.preset.character-death': 'Character Death',
  'alerts.preset.combat-start': 'Combat Start',
  'alerts.preset.new-location': 'New Location',
  'alerts.layout.fullscreen': 'Fullscreen',
  'alerts.layout.banner-top': 'Banner Top',
  'alerts.layout.banner-bottom': 'Banner Bottom',
  'alerts.duration.auto': 'Auto',
  'alerts.duration.manual': 'Manual',
  'alerts.trigger.manual': 'Manual',
  'alerts.trigger.initiative:start': 'Initiative Start',
  'alerts.trigger.initiative:round': 'New Round',
  'alerts.trigger.token:death': 'Token Death',
  'alerts.trigger.roll:critical': 'Critical Hit',
  'alerts.trigger.roll:fumble': 'Critical Fumble',
  'alerts.trigger.page:activate': 'Page Change',
  'trigger.askShow': 'A trigger fired — show the players: {what}?',
  'trigger.show': 'Show',
  'trigger.notNow': 'Not now',
  'alerts.trigger.custom': 'Custom',
  'alerts.mediaSection': 'Media',
  'alerts.settingsSection': 'Settings',
  'alerts.triggerSection': 'Trigger',
  'alerts.limitCounter': 'Alerts: {count}/{max}',
  'alerts.limitWarning': 'Approaching alert limit',
  'alerts.limitReached': 'Alert limit reached (50)',
  'alerts.triggerLimitReached': 'Auto-trigger limit reached (20)',
  'alerts.sound.settings': 'Alert Sound Settings',
  'alerts.sound.volume': 'Volume',
  'alerts.sound.mute': 'Mute sound',
  'alerts.error.nameRequired': 'Enter a name (1-100 characters)',
  'alerts.error.durationRange': 'Duration must be 1-30 seconds',
  'alerts.error.customEventRequired': 'Specify event name',
  'alerts.error.fileTooLarge': 'Maximum file size is 10 MB',
  'alerts.error.uploadFailed': 'File upload error',

  // === Stickers ===
  'sticker.title': 'Stickers',
  'sticker.cooldown': 'Cooldown:',
  'sticker.cooldownUnit': 's',
  'sticker.createPack': 'Create Pack',
  'sticker.connectPack': 'Connect',
  'sticker.editPack': 'Edit',
  'sticker.deletePack': 'Delete Pack',
  'sticker.disconnectPack': 'Disconnect',
  'sticker.emptyTitle': 'No connected sticker packs',
  'sticker.emptyHint': 'Create a pack or connect an existing one',
  'sticker.noStickers': 'No stickers yet',
  'sticker.packName': 'Pack Name',
  'sticker.packNamePlaceholder': 'Group Memes',
  'sticker.stickerCount': 'stickers',
  'sticker.uploadHint': 'PNG, WebP · up to 2 MB',
  'sticker.uploadDragHint': 'Drag files here',
  'sticker.uploadBrowse': 'Browse Files',
  'sticker.limitReached': 'Sticker limit reached',
  'sticker.connectTitle': 'Connect Sticker Pack',
  'sticker.connectButton': 'Connect',
  'sticker.allConnected': 'All your sticker packs are already connected to this session',
  'sticker.noPacks': 'You have no sticker packs',
  'sticker.createFirst': 'Create your first!',
  'sticker.deleteConfirmTitle': 'Delete sticker pack?',
  'sticker.deleteConfirmMessage': 'The pack and all stickers will be permanently deleted',
  'sticker.disconnectConfirmTitle': 'Disconnect sticker pack?',
  'sticker.disconnectConfirmMessage': 'Stickers from this pack will become unavailable in this session',
  'sticker.builtIn': 'Built-in',
  'sticker.custom': 'Custom',
  'sticker.done': 'Done',
  'sticker.cancel': 'Cancel',
  'sticker.close': 'Close',
  'sticker.unavailable': 'Sticker unavailable',
  'sticker.emptyPack': 'Empty pack',
  'sticker.error.formatInvalid': 'Only PNG and WebP formats are supported',
  'sticker.error.fileTooLarge': 'File too large (max 2 MB)',
  'sticker.error.tooManyUploads': 'Too many uploads, please wait',
  'sticker.error.packNotFound': 'Sticker pack not found',
  'sticker.error.uploadFailed': 'Sticker upload error',
  'sticker.error.limitExceeded': 'Pack sticker limit reached',
  'sticker.mediaTab.alerts': 'Alerts',
  'sticker.mediaTab.stickers': 'Stickers',

  // === Release Notes ===
  'releaseNotes.badgeNew': 'New feature',
  'releaseNotes.title': "What's New",
  'releaseNotes.menuItem': "What's New",
  'releaseNotes.sectionNew': 'New',
  'releaseNotes.sectionFix': 'Fixes',
  'releaseNotes.sectionImprovement': 'Improvements',
  'releaseNotes.currentVersion': 'Current version',
  'releaseNotes.learnMore': 'Learn more',
  'releaseNotes.version': 'Version',
  'releaseNotes.empty': 'No release notes yet',

  // === Mobile ===
  'mobile.overflowMenu': 'More',
  'mobile.aiChat': 'Tia',
  'mobile.chat': 'Chat',
  'mobile.sheet': 'Sheet',
  'mobile.initiative': 'Init',
  'mobile.dice': 'Dice',
  'mobile.voiceInput': 'Voice input',
  'mobile.voiceListening': 'Listening...',
  'mobile.voiceError': 'Recognition error',
  'mobile.voiceNotSupported': 'Voice input not supported',

  'mobile.tokenPlaced': 'Token placed',

  // === Mobile Canvas (024-2) ===
  'mobile.tokenPopup.hp': 'HP',
  'mobile.tokenPopup.close': 'Close',
  'mobile.tokenPopup.conditions': 'Conditions',

  // === Mobile Touch Interactions (024-3) ===
  'mobile.characterSheet.aiHint': 'Try: ask the AI to make changes',
  'mobile.characterSheet.aiHintDismiss': 'Got it',

  // === PWA (024-4) ===
  'pwa.offlineBanner': 'No server connection',
  'pwa.installPrompt': 'Install roll1 to home screen',
  'pwa.installButton': 'Install',
  'pwa.installDismiss': 'Not now',

  // === Push Notifications (024-5) ===
  'push.promptTitle': 'Get notified when it\'s your turn?',
  'push.promptAllow': 'Allow',
  'push.promptDismiss': 'Not now',

  // === Bug Report ===
  'bugReport.title': 'Report a bug',
  'bugReport.description': 'Describe the issue — element and session context will be attached automatically.',
  'bugReport.titleLabel': 'Title',
  'bugReport.titlePlaceholder': 'Briefly describe the problem...',
  'bugReport.descriptionLabel': 'Details',
  'bugReport.descriptionPlaceholder': 'Steps to reproduce, expected vs actual behavior...',
  'bugReport.cancel': 'Cancel',
  'bugReport.submit': 'Create Issue',
  'bugReport.submitted': 'GitHub issue creation page opened',
  'bugReport.menuItem': 'Report bug',
  'bugReport.rateLimited': 'Too many reports. Please wait.',
  'bugReport.retryIn': 'Try again in {{seconds}}s',

  // === Undo/Redo (032-A) ===
  'undo.tokenMove': 'Move token',

  // === AoE Templates (037) ===
  'aoe.cone': 'Cone',
  'aoe.sphere': 'Sphere',
  'aoe.cube': 'Cube',
  'aoe.line': 'Line',
  'aoe.cylinder': 'Cylinder',
  'aoe.size': 'Size (ft)',
  'aoe.color': 'Color',
  'aoe.label': 'Spell',
  'aoe.clearAll': 'Clear All',
  'aoe.autoRemove': 'Auto-remove',
  'aoe.durationNone': 'None',
  'aoe.duration1Round': '1 round (6s)',
  'aoe.duration1Min': '1 min',
  'aoe.duration10Min': '10 min',
  'spellAoe.aim': 'Fit the area from your token',
  'spellAoe.noToken': 'Put the character token on the map',
  'spellAoe.noTokenGm': 'Select a token on the map',
  'spellAoe.keep': 'Keep on the map',
  'spellAoe.cancel': 'Cancel',
  'spellAoe.title': '{spell} · {shape} {size} ft',
  'spellAoe.hintAim': 'Move the cursor (finger) over the map. Esc cancels',
  'spellAoe.hintCone': 'Starts at the token, points where you aim. Esc cancels',
  'spellAoe.outOfRange': 'Beyond the spell range ({range} ft)',
  'spellAoe.offer': 'Spell area: {spell}',
  'spellAoe.offerAction': 'Fit it',
  'spellAoe.hintOriginTurn': 'Press — the starting point within range, drag — turn. Esc cancels',

  // === Roll Card (037) ===
  'rollCard.hit': 'Hit!',
  'rollCard.miss': 'Miss',
  'rollCard.damage': 'Damage',
  'rollCard.crit': 'Crit!',
  'rollCard.fumble': 'Fumble!',
  'rollCard.breakdown': 'Breakdown',
  'rollCard.audit': 'Full audit',

  // === Action HUD (037) ===
  'actionHud.attack': 'Attacks',
  'actionHud.spell': 'Spells',
  'actionHud.ability': 'Abilities',
  'actionHud.other': 'Other',
  'actionHud.compact': 'Compact',
  'actionHud.expanded': 'Expanded',
  'actionHud.icon': 'Icon',
  'actionHud.color': 'Color',

  // === Death Save (037) ===
  'deathSave.dying': 'Dying',
  'deathSave.stable': 'Stable',
  'deathSave.dead': 'Dead',
  'deathSave.alive': 'Alive',
  'deathSave.roll': 'Roll Death Save',
  'deathSave.success': 'Success',
  'deathSave.failure': 'Failure',
  'deathSave.nat20': 'Miracle! Character regains consciousness.',
  'deathSave.nat1': 'Critical failure! Two failures.',
  'deathSave.stabilized': 'Character stabilized.',
  'deathSave.died': 'Character has died.',

  // === Concentration (037) ===
  'concentration.broken': 'Concentration broken!',
  'concentration.maintained': 'Concentration maintained',
  'concentration.dc': 'DC {dc}',

  // === Condition: Dead (037) ===
  'condition.dead': 'Dead',

  // === Mobile Content Access (034-phase-4) ===
  'mobile.switchLanguage': 'Language',
  'mobile.notes': 'Notes',
  'mobile.journal': 'Journal',
  'mobile.map': 'Maps',
  'mobile.timeline': 'Chronicle',
  'mobile.srd': 'SRD',
  'sheetModal.clickToEditName': 'Click to edit name',

  // === Map Objects ===
  'mapObject.empty': 'No map objects on this page.',
  'mapObject.createObject': 'Create Object',
  'mapObject.title': 'Map Objects',
  'mapObject.deleteConfirmTitle': 'Delete the map object?',
  'mapObject.deleteConfirmDesc': '“{name}” disappears from the map for everyone.',
  'mapObject.visibleAll': 'Visible to all',
  'mapObject.gmOnly': 'GM only',
  'mapObject.noun': 'Map Object',
  'mapObject.label': 'Label',
  'mapObject.description': 'Description',
  'mapObject.category': 'Category',
  'mapObject.properties': 'Properties',
  'mapObject.cover': 'Cover',
  'mapObject.coverNone': 'None',
  'mapObject.coverHalf': 'Half',
  'mapObject.coverThreeQuarter': '3/4',
  'mapObject.coverFull': 'Full',
  'mapObject.state': 'State',
  'mapObject.stateNone': 'None',
  'mapObject.stateClosed': 'Closed',
  'mapObject.stateOpen': 'Open',
  'mapObject.stateBroken': 'Broken',
  'mapObject.stateLit': 'Lit',
  'mapObject.stateExtinguished': 'Extinguished',
  'mapObject.traversable': 'Traversable',
  'mapObject.difficultTerrain': 'Difficult Terrain',
  'mapObject.hp': 'HP',
  'mapObject.maxHp': 'Max HP',
  'mapObject.locked': 'Locked',
  'mapObject.damage': 'Damage',
  'mapObject.light': 'Light',
  'mapObject.lightRadius': 'Radius (ft)',
  'mapObject.lightColor': 'Color',
  'mapObject.display': 'Display',
  'mapObject.displayColor': 'Color',
  'mapObject.icon': 'Icon',
  'mapObject.opacity': 'Opacity',
  'mapObject.visibility': 'Visibility',
  'mapObject.visibilityGm': 'GM',
  'mapObject.visibilityAll': 'All',
  'mapObject.interactable': 'Interactable',
  'mapObject.catFurniture': 'Furniture',
  'mapObject.catTerrain': 'Terrain',
  'mapObject.catHazard': 'Hazard',
  'mapObject.catDoor': 'Door',
  'mapObject.catContainer': 'Container',
  'mapObject.catLight': 'Light',
  'mapObject.catCustom': 'Custom',
  'mapObject.coverHalfBadge': 'Half cover',
  'mapObject.coverThreeQuarterBadge': '3/4 cover',
  'mapObject.coverFullBadge': 'Full cover',

  // === Fog Confirm Dialogs ===
  'fog.confirmCoverAll.title': 'Cover all with fog?',
  'fog.confirmCoverAll.description': 'This will cover the entire map with fog of war. This action cannot be undone.',
  'fog.confirmClearAll.title': 'Clear all fog?',
  'fog.confirmClearAll.description': 'This will remove all fog of war from the map. This action cannot be undone.',
  'fog.confirm.confirm': 'Confirm',
  'fog.confirm.cancel': 'Cancel',

  // === Quick Access Panel ===
  'quickAccess.title': 'Quick Access',
  'quickAccess.empty': 'Pin tools, rolls, or macros',
  'quickAccess.add': 'Add',
  'quickAccess.remove': 'Remove',
  'quickAccess.collapse': 'Collapse',
  'quickAccess.expand': 'Expand',
  'quickAccess.close': 'Close',
  'quickAccess.categoryTool': 'Tool',
  'quickAccess.categoryRoll': 'Roll',
  'quickAccess.categoryMacro': 'Macro',
  'quickAccess.rollLabel': 'Label',
  'quickAccess.rollFormula': 'Formula',
  'quickAccess.macroLabel': 'Label',
  'quickAccess.macroCommands': 'Commands (one per line)',
  'quickAccess.invalidFormula': 'Invalid dice formula',
  'quickAccess.limitReached': 'Maximum 20 items',
  'quickAccess.duplicateTool': 'Tool already added',
  'quickAccess.macroCommandCount': '{count} command(s)',
  'quickAccess.categoryAction': 'Action',
  'quickAccess.categoryTab': 'Tab',
  'quickAccess.sectionPinned': 'Pinned',
  'quickAccess.sectionRecent': 'Recent',
  'quickAccess.noSheetsWithActions': 'No sheets with actions',
  'quickAccess.duplicateAction': 'Action already added',
  'quickAccess.duplicateTab': 'Tab already added',
  'shortcuts.toggleQuickAccess': 'Quick Access Panel',

  // === Settings Dialog ===
  'settings.title': 'Settings',
  'settings.tab.ai': 'AI Assistant',
  'settings.tab.campaign': 'Campaign',
  'settings.tab.display': 'Display',
  'settings.tab.combat': 'Combat Rules',
  'settings.tab.permissions': 'Permissions',
  'settings.tab.resources': 'Resources',
  'settings.resources.gmOnly': 'Campaign resources are visible to the GM only.',
  'settings.resources.loadError': 'Failed to load usage stats',
  'settings.resources.tokens30d': 'AI tokens · 30 days',
  'settings.resources.money30d': 'AI spend for the period',
  'settings.resources.moneyText': 'text',
  'settings.resources.moneyImage': 'images',
  'settings.resources.moneyVoice': 'voice',
  'settings.resources.moneyBilled': 'billed',
  'settings.resources.players': 'players',
  'settings.resources.tokensAllTime': 'AI tokens · all time',
  'settings.resources.allTimeHint': 'across the whole campaign',
  'settings.resources.interactions30d': 'AI requests · 30 days',
  'settings.resources.chartTitle': 'Daily token spend (30 days)',
  'settings.resources.tokensUnit': 'tokens',
  'settings.resources.interactionsUnit': 'requests',
  'settings.resources.tableToggle': 'Show as table',
  'settings.resources.colDay': 'Day',
  'settings.resources.colTokens': 'Tokens',
  'settings.resources.colInteractions': 'Requests',

  // AI tab
  'settings.ai.worldDescription': 'World Description',
  'settings.ai.worldDescriptionPlaceholder': 'Describe the world, setting, key locations...',
  'settings.ai.houseRules': 'House Rules',
  'settings.ai.houseRulesPlaceholder': 'Any custom rules or modifications...',
  'settings.ai.tone': 'Tone',
  'settings.ai.gmInstructions': 'GM Instructions',
  'settings.ai.gmInstructionsPlaceholder': 'Instructions for the AI assistant...',
  'settings.ai.excludedEntities': 'Excluded Entities (Spoiler Guard)',
  'settings.ai.searchEntities': 'Search characters, notes...',
  'settings.ai.sectionPriority': 'AI Context Section Priority',
  'settings.ai.briefingFocus': 'Session Focus',
  'settings.ai.briefingFocusDesc': 'What this session is about — the AI weighs it above general context',
  'settings.ai.briefingFocusPlaceholder': 'e.g. The party infiltrates the Duke\'s masquerade ball...',
  'settings.ai.briefingActiveNpcs': 'Active NPCs',
  'settings.ai.briefingActiveNpcsDesc': 'Comma-separated names the AI should keep on stage this session',
  'settings.ai.briefingActiveNpcsPlaceholder': 'Duke Aldric, Captain Vey, the masked stranger',
  'settings.ai.snapshotsLabel': 'AI operation snapshots (undo)',
  'settings.ai.deleteWithoutAsking': 'Tia deletes without asking',
  'settings.ai.deleteWithoutAskingDesc': 'Off — every delete of an entry, sheet, beat, event, fork, link or pin waits for your button, which shows what goes along. On — Tia deletes at once; undoing her answer brings it back.',
  'snapshot.title': 'Undo snapshots',
  'snapshot.refresh': 'Refresh',
  'snapshot.empty': 'No snapshots yet',
  'snapshot.undo': 'Undo…',
  'snapshot.restoring': 'Undoing…',
  'snapshot.age.now': 'just now',
  'snapshot.age.min': '{n} min ago',
  'snapshot.age.h': '{n} h ago',
  'snapshot.age.d': '{n} d ago',
  'snapshot.label.homebrewConnect': 'homebrew connected',
  'snapshot.label.homebrewUpdate': 'homebrew updated',
  'snapshot.label.draftAccept': 'drafts accepted',
  'snapshot.label.levelUp': 'level up: {name}',
  'snapshot.label.createCharacter': 'character created: {name}',
  'snapshot.label.encounter': 'encounter: {name}',
  'snapshot.label.import': 'import, step {name}',
  'snapshot.kind.sheets': 'sheets',
  'snapshot.kind.pages': 'maps',
  'snapshot.kind.tokens': 'tokens',
  'snapshot.kind.chat': 'chat messages',
  'snapshot.kind.initiative': 'combatants',
  'snapshot.confirm.title': 'Undo the campaign to this snapshot?',
  'snapshot.confirm.what': 'Snapshot {age}: {label}. The undo puts the whole campaign back to that moment: what you and the players did after it goes too.',
  'snapshot.confirm.counting': 'Counting what will change…',
  'snapshot.confirm.nothing': 'No sheets, maps, tokens, chat or combat changed since the snapshot.',
  'snapshot.confirm.removed': 'Deleted (made after the snapshot):',
  'snapshot.confirm.reverted': 'Put back as in the snapshot (changed since):',
  'snapshot.confirm.returned': 'Brought back (deleted since the snapshot):',
  'snapshot.confirm.deleteOne': 'Undo and delete {n} new',
  'snapshot.confirm.deleteFew': 'Undo and delete {n} new',
  'snapshot.confirm.deleteMany': 'Undo and delete {n} new',
  'snapshot.confirm.plain': 'Undo',

  // Campaign tab
  'settings.campaign.measurementMode': 'Measurement Mode',
  'settings.campaign.gridSizeFt': 'Grid Size (ft)',
  'settings.campaign.navigationMode': 'Navigation Mode',
  'settings.campaign.navFree': 'Free',
  'settings.campaign.navConfirm': 'Confirm',
  'settings.campaign.navGmOnly': 'GM Only',
  'settings.campaign.importCampaign': 'Import Campaign',

  // Display tab
  'settings.display.gridStyle': 'Grid Style',
  'settings.display.gridLines': 'Lines',
  'settings.display.gridDots': 'Dots',
  'settings.display.gridNone': 'None',
  'settings.display.gridOpacity': 'Grid Opacity',
  'settings.display.sizeTiny': 'Tiny',
  'settings.display.sizeSmall': 'Small',
  'settings.display.sizeMedium': 'Medium',
  'settings.display.sizeLarge': 'Large (2×2)',
  'settings.display.sizeHuge': 'Huge (3×3)',
  'settings.display.sizeGargantuan': 'Gargantuan (4×4)',
  'settings.display.ambientOverlay': 'Time of Day Overlay',
  'settings.display.hpBarVisibility': 'HP Bar Visibility',
  'settings.display.hpAlways': 'Always',
  'settings.display.hpHover': 'On Hover',
  'settings.display.hpGmOnly': 'GM Only',
  'settings.display.hpNever': 'Never',
  'settings.display.hpBarHint': 'Players see exact hit points only on their own tokens. On others\' tokens and monsters they get only a coarse bar (10 % steps), no numbers — with «Always» and «On hover». With «GM only» and «Never» there is no bar on those tokens.',
  'settings.display.defaultTokenSize': 'Default Token Size',

  // Combat tab
  'settings.combat.autoRollInitiative': 'Auto-roll Initiative',
  'settings.combat.autoRollInitiativeDesc': 'Automatically roll d20 + DEX modifier when initiative starts',
  'settings.combat.confirmDamage': 'Confirm Damage',
  'settings.combat.confirmDamageDesc': 'Show confirmation dialog before applying damage',
  'settings.combat.critRange': 'Critical Range',
  'settings.combat.critRangeDesc': 'Minimum d20 value for a critical hit (default: 20)',
  'settings.combat.deathSavesEnabled': 'Death Saves Enabled',
  'settings.combat.deathSavesEnabledDesc': 'Enable D&D 5e death saving throws',

  // Permissions tab
  'settings.permissions.description': 'Configure what players can do. GM always has full access.',
  'settings.permissions.moveOtherTokens': 'Move Other Tokens',
  'settings.permissions.moveOtherTokensDesc': 'Allow players to move tokens not assigned to them',
  'settings.permissions.drawOnMap': 'Draw on Map',
  'settings.permissions.drawOnMapDesc': 'Allow players to use drawing tools',
  'settings.permissions.addTokens': 'Add Tokens',
  'settings.permissions.addTokensDesc': 'Allow players to create tokens on the map',
  'settings.permissions.createNotes': 'Create Notes',
  'settings.permissions.createNotesDesc': 'Allow players to create map pins and notes',
  'settings.permissions.useAi': 'Use AI',
  'settings.permissions.useAiDesc': 'Allow players to interact with the AI assistant',
  'settings.permissions.playerAiRate': 'Player AI rate limit',
  'settings.permissions.playerAiRateDesc': 'Max AI requests per player per minute (1–60)',
  'settings.permissions.playerAiBudget': 'Player AI token budget',
  'settings.permissions.playerAiBudgetDesc': 'Max AI tokens per player per hour. Empty = unlimited',
  'settings.permissions.unlimited': 'Unlimited',
  'settings.permissions.allowPlayerStickers': 'Player Stickers',
  'settings.permissions.allowPlayerStickersDesc': 'Allow players to send and manage sticker packs',
  'settings.permissions.viewOtherSheets': 'View Other Sheets',
  'settings.permissions.viewOtherSheetsDesc': 'What players can see on other characters\' sheets',
  'settings.permissions.viewNone': 'None',
  'settings.permissions.viewBasic': 'Basic (public fields)',
  'settings.permissions.viewFull': 'Full',

  // V003: Tool options all settings link
  'toolOptions.allSettings': 'All Settings...',

  // V005: Confirm damage prompt
  'combat.confirmDamagePrompt': 'Apply {damage} damage to {name}?',

  // V006: Permission denied tooltips
  'permissions.denied.drawOnMap': 'Drawing is disabled by GM',
  'permissions.denied.addTokens': 'Token creation is disabled by GM',
  'permissions.denied.createNotes': 'Note creation is disabled by GM',

  // === Server Error Codes (command:error) ===
  'error.not_found': 'Not found',
  'error.material_is_prep_source': 'This entry is the beat’s prep source — it is not added again as a material',
  'error.map_not_found': 'The map is not in this campaign',
  'error.map_not_in_beat': 'This map is no longer in the beat',
  'error.unauthorized': 'Not authorized',
  'error.OWNERSHIP_CHECK_FAILED': 'This is not your sheet: it cannot be changed',
  'error.forbidden': 'Forbidden',
  'error.invalid_input': 'Invalid input',
  'error.invalid_name': 'Invalid name',
  'error.invalid_data': 'Invalid data',
  'error.invalid_type': 'Invalid type',
  'error.invalid_tags': 'Too many tags',
  'error.invalid_visibility': 'Invalid visibility',
  'error.duplicate': 'Already exists',
  'error.duplicate_link': 'Link already exists',
  'error.self_link': 'Cannot link to itself',
  'error.self_reference': 'Cannot reference itself',
  'error.circular_reference': 'Circular reference detected',
  'error.LINK_NOT_FOUND': 'Link not found',
  'error.links_limit': 'Links limit reached',
  'error.PAGE_LIMIT': 'Maximum 20 pages reached',
  'error.CANNOT_DELETE_LAST': 'Cannot delete the only page',
  'error.PLAYERS_ON_PAGE': 'The players are on this map — choose where to move them',
  'error.MOVE_TARGET_NOT_FOUND': 'The map to move the players to is not found',
  'error.PLAYERS_MOVED': 'The players have been moved to another map since',
  'error.INVALID_PAGE_LIST': 'Invalid page list',
  'error.SAME_PAGE': 'Source and target pages are the same',
  'error.INVALID_NAME': 'Name must be 1–50 characters',
  'error.NAME_TOO_LONG': 'Name must be at most 50 characters',
  'error.TOKEN_NOT_FOUND': 'Token not found',
  'error.SHEET_NOT_FOUND': 'Sheet not found',
  'error.alert_limit': 'Alert limit reached (50)',
  'error.trigger_limit': 'Auto-trigger limit reached (20)',
  'error.unknown_preset': 'Unknown preset',
  'error.invalid_label': 'Invalid label',
  'error.invalid_color': 'Invalid color',
  'error.invalid_icon': 'Invalid icon',
  'error.invalid_content': 'Invalid content',
  'error.invalid_title': 'Invalid title',
  'error.note_limit': 'Note limit reached',
  'error.parent_not_found': 'Parent note not found',
  'error.invalid_area': 'Invalid area',
  'error.invalid_description': 'Invalid description',
  'error.invalid_terrain': 'Invalid terrain type',
  'error.invalid_cover': 'Invalid cover type',
  'error.TARGET_PAGE_NOT_FOUND': 'Target page not found',
  'error.NAVIGATE_REQUEST_NOT_FOUND': 'Navigation request not found',
  'error.GM_ONLY_NAVIGATION': 'Only GM can navigate in this mode',
  'error.builtin_template': 'Cannot edit built-in template',
  'error.in_use': 'Template is in use by character sheets',
  'error.validation_error': 'Validation error',
  'error.invalid_state': 'Invalid state',
  'error.death_saves_disabled': 'Death saves are disabled',
  'error.system_not_supported': 'Not available for this game system',
  'error.hit_dice_exhausted': 'No hit dice left',
  'error.no_hit_dice': 'No hit dice available',
  'error.no_spell_slots': 'No spell slots available',
  'error.no_pact': 'No pact magic configured',
  'error.not_repeating': 'Section is not repeating',
  'error.invalid_action': 'Invalid action',
  'error.invalid_expression': 'Invalid expression',
  'error.invalid_level': 'Invalid level',
  'error.invalid_mode': 'Invalid mode',
  'error.not_draft': 'Not a draft',
  'error.session_limit': 'Session limit reached',
  'error.session_full': 'Session is full',
  'error.active_limit': 'Only one active session allowed',
  'error.timeline_limit': 'Chronicle event limit reached',
  'error.tags_limit': 'Tags limit reached',
  'error.PIN_LIMIT': 'Pin limit reached',
  'error.PIN_NOT_FOUND': 'Pin not found',
  'error.WALL_NOT_FOUND': 'Wall not found',
  'error.WALL_INVALID': 'Invalid wall',
  'error.WALL_LIMIT': 'Wall limit reached',
  'error.WALL_NOT_DOOR': 'Wall is not a door',
  'error.WALL_CHAIN_MIN_POINTS': 'Wall needs at least 2 points',
  'error.INVALID_ILLUMINATION': 'Invalid illumination value',
  'error.invalid_condition': 'Invalid condition',
  'error.invalid_transition': 'Invalid transition',
  'error.restore_failed': 'Restore failed',
  'error.max_level': 'Maximum level reached',
  'error.trigger_disabled': 'Trigger is disabled',
  'error.internal_error': 'Internal error',

  // === Timeline ===
  'timeline.eventType.sessionStart': 'Session Start',
  'timeline.eventType.sessionRecap': 'Session Recap',
  'timeline.eventType.encounter': 'Encounter',
  'timeline.eventType.discovery': 'Discovery',
  'timeline.eventType.plot': 'Plot',
  'timeline.eventType.travel': 'Travel',
  'timeline.eventType.rest': 'Rest',
  'timeline.eventType.downtime': 'Downtime',
  'timeline.eventType.character': 'Character',
  'timeline.eventType.custom': 'Other',
  'timeline.eventType.chapter': 'Chapter',
  'timeline.timeOfDay.dawn': 'Dawn',
  'timeline.timeOfDay.morning': 'Morning',
  'timeline.timeOfDay.midday': 'Midday',
  'timeline.timeOfDay.afternoon': 'Afternoon',
  'timeline.timeOfDay.dusk': 'Dusk',
  'timeline.timeOfDay.evening': 'Evening',
  'timeline.timeOfDay.night': 'Night',
  'timeline.timeOfDay.midnight': 'Midnight',
  'timeline.form.editTitle': 'Edit Event',
  'timeline.form.newTitle': 'New Event',
  'timeline.form.titleLabel': 'Text',
  'timeline.form.titlePlaceholder': 'What happened?',
  'timeline.form.typeLabel': 'Event Type',
  'timeline.form.descriptionLabel': 'Description',
  'timeline.form.descriptionPlaceholder': 'Details...',
  'timeline.form.yearLabel': 'Year',
  'timeline.form.monthLabel': 'Month',
  'timeline.form.dayLabel': 'Day',
  'timeline.form.timeOfDayLabel': 'Time of Day',
  'timeline.form.manualTimeOverride': 'Custom time text',
  'timeline.form.customTimePlaceholder': 'Custom time description',
  'timeline.form.timeTextLabel': 'Time (text)',
  'timeline.form.timeLabelPlaceholder': 'Day 3, Morning',
  'timeline.form.visibilityLabel': 'Visibility',
  'timeline.form.visibilityAll': 'All',
  'timeline.form.visibilityGmOnly': 'GM only',
  'timeline.form.visibilityPlayers': 'Players',
  'timeline.form.tagsLabel': 'Tags (comma-separated)',
  'timeline.form.tagsPlaceholder': 'combat, dragon, Waterdeep',
  'timeline.form.linksLabel': 'Links',
  'timeline.form.sessionLabel': 'Session',
  'timeline.form.noSession': 'No session',
  'timeline.form.cancelButton': 'Cancel',
  'timeline.form.saveButton': 'Save',
  'timeline.form.createButton': 'Create',
  'timeline.form.suggestButton': 'Suggest',
  'timeline.form.suggestTitle': 'Suggest an event',
  'timeline.form.linkTitle': 'Links of the event',
  'timeline.form.whatHappenedLabel': 'What happened',
  'timeline.form.whatSuggestLabel': 'What do you suggest',
  'timeline.form.addButton': 'Add to chronicle',
  'timeline.form.suggestToGmButton': 'Suggest to GM',
  'timeline.form.details': 'Details',
  'timeline.form.atTime': 'At {time}',
  'timeline.form.staysDraft': 'stays a draft',
  'timeline.form.customTimeHint': 'Leave empty to use the date',
  'timeline.form.visibleToAll': 'Visible to all',
  'timeline.form.selectedPlayers': 'Selected players',
  'timeline.form.noPlayers': 'No players in the campaign yet',
  'timeline.form.linkWith': 'Link with:',
  'timeline.form.findLink': 'Find what to link',
  'timeline.form.findLinkPlaceholder': 'Find a record, map, beat…',
  'timeline.form.linkName': '{kind} “{name}”',
  'timeline.form.linkKind.beat': 'beat',
  'timeline.form.linkKind.map': 'map',
  'timeline.form.linkKind.record': 'record',
  'timeline.form.linkKind.quest': 'quest',
  'timeline.form.linkKind.character': 'character',
  'timeline.form.linkKind.pin': 'pin',
  'timeline.form.linkWhy.inProgress': 'in progress',
  'timeline.form.linkWhy.players': 'with the players',
  'timeline.form.linkWhy.quest': 'the beat’s quest',
  'timeline.form.linkWhy.selected': 'selected on the map',
  'timeline.form.unlink': 'Remove the link to {name}',
  'timeline.filters.title': 'Filters',
  'timeline.filters.resetAriaLabel': 'Reset all filters',
  'timeline.filters.reset': 'Reset',
  'timeline.filters.searchPlaceholder': 'Search by title and description...',
  'timeline.filters.searchAriaLabel': 'Search events',
  'timeline.filters.typeGroupLabel': 'Filter by type',
  'timeline.filters.tagsGroupLabel': 'Filter by tags',
  'timeline.filters.allSessions': 'All sessions',
  'timeline.filters.typeLabel': 'Type',
  'timeline.filters.tagLabel': 'Tag',
  'timeline.filters.myEventsOnly': 'My events',
  'timeline.cursor.dayFormat': 'Day',
  'timeline.cursor.editDateTooltip': 'Edit date',
  'timeline.cursor.addDate': '+ date',
  'timeline.cursor.partsGroup': 'Time of day',
  'timeline.cursor.prevPart': 'Previous part of day',
  'timeline.cursor.nextPart': 'Next part of day',
  'rest.shiftOffer': 'A long rest. Move the world time by the night?',
  'rest.shiftAction': 'Move the time',
  'timeline.cursor.partNextDay': '{part}, next day',
  'timeline.cursor.undo': 'Undo',
  'timeline.cursor.prevDay': 'Previous day',
  'timeline.cursor.nextDay': 'Next day',
  'timeline.cursor.month': 'Month',
  'timeline.cursor.year': 'Year',
  'timeline.cursor.day': 'Day',
  'timeline.cursor.datePicker': 'Date',
  'timeline.cursor.closePicker': 'Close',
  'timeline.cursor.inTheWorld': 'In the world: {time}',
  'timeline.list.noDate': 'No date',
  'timeline.list.addEventToDay': 'Add event to this day',
  'timeline.list.now': 'now',
  'timeline.list.deleteConfirm': 'Really delete?',
  'timeline.list.draftBadge': 'draft',
  'timeline.list.editTooltip': 'Edit',
  'timeline.list.more': 'read all',
  'timeline.list.less': 'collapse',
  'timeline.list.deleteTooltip': 'Delete…',
  'timeline.drafts.regionLabel': 'Waiting for publication',
  'timeline.drafts.title': 'Waiting for publication',
  'timeline.drafts.approveAriaLabel': 'Publish',
  'timeline.drafts.rejectAriaLabel': 'Reject',
  'timeline.panel.emptyTitle': 'The chronicle is empty',
  'timeline.panel.emptySubtext': 'No campaign events yet',
  'timeline.panel.regionLabel': 'Campaign chronicle',
  'timeline.panel.noTime': 'No time set',
  'timeline.panel.createEventAriaLabel': 'Add event',
  'timeline.panel.noFilterResults': 'No events match the filters',
  'timeline.panel.emptySubtextGM': 'Create the first campaign event',
  'timeline.panel.createEventButton': 'Create event',
  'timeline.autoTransitions.removeAll': 'Remove all transitions',
  'timeline.autoTransitions.removeAllTitle': 'Remove all transitions?',
  'timeline.autoTransitions.removeAllDesc': 'Transition events to remove: {count} — published ones disappear for the players too. Other chronicle events stay.',
  'timeline.autoTransitions.removeAllConfirm': 'Remove',
  'timeline.panel.title': 'Chronicle',
  'timeline.panel.addEvent': 'Add event',
  'timeline.panel.suggestEvent': 'Suggest an event',
  'timeline.panel.more': 'More',
  'timeline.panel.calendarLine': 'Calendar: {name}',
  'timeline.panel.noCalendar': 'none',
  'timeline.panel.changeCalendar': 'change…',
  'timeline.panel.published': 'Published',
  'timeline.calendar.title': 'Campaign calendar',
  'timeline.calendar.current': 'Current calendar',
  'timeline.calendar.monthsInfo': '{months} months, {days} days a week',
  'timeline.calendar.remove': 'Remove',
  'timeline.calendar.edit': 'Edit',
  'timeline.calendar.choosePreset': 'Pick a preset or make your own',
  'timeline.calendar.presetInfo': '{months} months, {days} days a week',
  'timeline.calendar.createOwn': 'Make your own',
  'timeline.calendar.customName': 'My calendar',
  'timeline.calendar.customMonth': 'Month 1',
  'timeline.calendar.aiGenerate': 'Calendar template',
  'timeline.calendar.name': 'Name',
  'timeline.calendar.yearLabel': 'Year label',
  'timeline.calendar.epoch': 'Epoch',
  'timeline.calendar.months': 'Months',
  'timeline.calendar.addMonth': 'Add month',
  'timeline.calendar.removeMonth': 'Remove month',
  'timeline.calendar.intercalary': 'Intercalary',
  'timeline.calendar.newMonth': 'New month',
  'timeline.calendar.save': 'Save',
  'timeline.calendar.cancel': 'Cancel',
  'timeline.calendar.yearLabelPlaceholder': 'DR, YK, AD…',
  'timeline.calendar.epochPlaceholder': 'Dalereckoning…',
  'timeline.calendar.intercalaryShort': 'IC',
  'timeline.session.line': 'Session {number} · {status} · {date}',
  'timeline.session.statusActive': 'in progress',
  'timeline.session.statusPlanned': 'planned',
  'timeline.session.statusCompleted': 'completed',
  'timeline.session.none': 'No sessions yet',
  'timeline.session.start': 'Start session',
  'timeline.session.startEvent': 'Session {number} started',
  'timeline.session.startEventTitled': 'Session {number} started: {title}',
  'plan.session.none': 'No session running',
  'timeline.summary.button': 'Session summary',
  'timeline.summary.title': 'Session {number} summary',
  'timeline.summary.hint': 'What to publish to the players:',
  'timeline.summary.played': 'Played: {beats}.',
  'timeline.summary.playedNone': 'Played: nothing.',
  'timeline.summary.empty': 'No drafts.',
  'timeline.summary.uncheckedStays': 'Unchecked items stay drafts.',
  'timeline.summary.publishAndFinish': 'Publish checked and finish',
  'timeline.summary.sureCount': 'Sure? The players will see {count} entries.',
  'timeline.summary.sureNone': 'Sure? Nothing is published; the session is finished.',
  'timeline.summary.sureYes': 'Yes, publish and finish',
  'timeline.summary.sureBack': 'Back',
  'timeline.drafts.hint': 'players don\'t see them yet',
  'timeline.drafts.sourceAuto': 'auto',
  'timeline.drafts.sourceGm': 'GM',
  'timeline.drafts.sourcePlayer': 'player',
  'timeline.drafts.accept': 'Accept',
  'timeline.drafts.edit': 'Edit',
  'timeline.drafts.reject': 'Reject',
  'timeline.drafts.publish': 'Publish',
  'timeline.drafts.remove': 'Remove',
  'timeline.drafts.publishAllAuto': 'Publish all auto',
  'timeline.drafts.publishTitle': 'Publish “{title}”?',
  'timeline.drafts.acceptTitle': 'Accept “{title}”?',
  'timeline.drafts.publishDesc': 'The players will see it in the chronicle.',
  'timeline.drafts.publishAllTitle': 'Publish all auto drafts?',
  'timeline.drafts.publishAllDesc': 'The players will see: {titles}.',
  'timeline.drafts.rejectTitle': 'Reject “{title}”?',
  'timeline.drafts.rejectReason': 'Reason (optional)',
  'timeline.drafts.rejectHint': 'The author sees the reason.',
  'timeline.drafts.rejected': 'The GM rejected “{title}”',
  'timeline.drafts.rejectedWithReason': 'The GM rejected “{title}”: {reason}',
  'timeline.mine.title': 'My suggestions',
  'timeline.mine.waiting': 'waiting for the GM',
  'timeline.filters.myEventsHint': 'written or suggested by me',
  'timeline.visibility.all': 'Visible to all',
  'timeline.visibility.gm': 'GM only',
  'timeline.visibility.players': 'Selected players',
  'timeline.visibility.menuLabel': 'Who sees it',
  'timeline.visibility.choosePlayers': 'Who sees “{title}”',
  'timeline.visibility.noPlayers': 'No players in the campaign yet.',
  'timeline.visibility.apply': 'Apply',
  'timeline.visibility.confirmAllTitle': 'Show “{title}” to all players?',
  'timeline.visibility.confirmPlayersTitle': 'Show “{title}” to the selected players?',
  'timeline.visibility.confirmAllDesc': 'Every player will see it in the chronicle.',
  'timeline.visibility.confirmPlayersDesc': 'They will see it in the chronicle: {names}.',
  'timeline.visibility.confirm': 'Show',
  'timeline.links.add': '+ link',
  'timeline.links.record': 'Record',
  'timeline.links.recordGone': 'This record no longer exists.',
  'timeline.create.confirmTitle': 'Show “{title}” to the players?',
  'timeline.create.confirmDesc': 'It is saved under «Waiting for publication»; the players don\'t see it yet. Publish it now or keep it there.',
  'timeline.create.keepDraft': 'Keep as a draft',

  // === Campaign Sessions ===
  'campaignSessions.ariaLabel': 'Game session management',
  'campaignSessions.title': 'Sessions',
  'campaignSessions.newSession': 'New session',
  'campaignSessions.titleLabel': 'Title (optional)',
  'campaignSessions.namePlaceholder': 'Dragon\'s Dungeon',
  'campaignSessions.dateLabel': 'Date',
  'campaignSessions.active': 'Active',
  'campaignSessions.complete': 'Complete',
  'campaignSessions.planned': 'Planned',
  'campaignSessions.start': 'Start',
  'campaignSessions.completed': 'Completed',
  'campaignSessions.noSessions': 'No sessions yet',
  'campaignSessions.endTitle': 'End session #{number}?',
  'campaignSessions.endUnpublished': 'Unpublished transitions: {count}. The players do not see them; they are never published by themselves.',
  'campaignSessions.endPublish': 'Publish',
  'campaignSessions.endRemove': 'Remove',
  'campaignSessions.endKeepDrafts': 'Keep as drafts',

  // === Import Wizard ===
  'importWizard.title': 'Import Campaign',
  'importWizard.sourceText': 'Text / notes',
  'importWizard.selectSource': 'Select source',
  'importWizard.sourceDndBeyondSoon': 'D&D Beyond (coming soon)',
  'importWizard.sourcePdfSoon': 'PDF (coming soon)',
  'importWizard.sourceImageSoon': 'Image (coming soon)',
  'importWizard.extractingRelations': 'Extracting relations...',
  'importWizard.analyzingRelations': 'Analyzing relations between entities',
  'importWizard.wave1': 'Containers (pages, sheets)',
  'importWizard.wave2': 'Content (journal, notes)',
  'importWizard.wave3': 'Relations (links, pins)',
  'importWizard.importError': 'Import error',
  'importWizard.source': 'Source',
  'importWizard.dragDropHint': 'Drag a file here or click to browse',
  'importWizard.analyzing': 'Analyzing...',
  'importWizard.analyze': 'Analyze',
  'importWizard.conflictsFound': 'Conflicts found',
  'importWizard.tableType': 'Type',
  'importWizard.tableName': 'Name',
  'importWizard.tableAction': 'Action',
  'importWizard.tableNewName': 'New name',
  'importWizard.strategySkip': 'Skip',
  'importWizard.strategyMerge': 'Merge',
  'importWizard.strategyRename': 'Rename',
  'importWizard.newNamePlaceholder': 'New name',
  'importWizard.applying': 'Applying...',
  'importWizard.apply': 'Apply',
  'importWizard.importPreview': 'Import preview',
  'importWizard.importComplete': 'Import complete',
  'importWizard.stepSource': 'Source',
  'importWizard.stepUpload': 'Upload',
  'importWizard.stepConflicts': 'Conflicts',
  'importWizard.stepResult': 'Result',
  'importWizard.strategyOverwrite': 'Overwrite',
  'importWizard.previewSheets': 'Characters',
  'importWizard.previewTokens': 'Tokens',
  'importWizard.previewPages': 'Pages',
  'importWizard.previewNotes': 'Notes',
  'importWizard.resultAdded': 'Added',
  'importWizard.resultOverwritten': 'Overwritten',
  'importWizard.resultSkipped': 'Skipped',

  // === Bug Report additions ===
  'bugReport.sectionDescription': '## Description',
  'bugReport.sectionElement': '## Element',
  'bugReport.sectionContext': '## Context',
  'bugReport.noDescription': '_No description_',
  'bugReport.roleGM': 'GM',
  'bugReport.rolePlayer': 'Player',

  // === SRD labels ===
  'srd.traits': 'Traits',
  'srd.actions': 'Actions',
  'srd.reactions': 'Reactions',
  'srd.legendaryActions': 'Legendary Actions',
  'srd.castingTime': 'Casting Time',
  'srd.range': 'Range',
  'srd.components': 'Components',
  'srd.duration': 'Duration',
  'srd.resistances': 'Resistances',
  'srd.immunities': 'Immunities',
  'srd.senses': 'Senses',
  'srd.languages': 'Languages',
  'srd.cr': 'CR',

  // === Spellbook ===
  'spellbook.title': 'Spellbook',
  'spellbook.empty': 'No spells in spellbook',
  'spellbook.cantrips': 'Cantrips',
  'spellbook.concentration': 'Concentration',

  // === Compendium Browser ===
  'compendium.equipment': 'Equipment',
  'compendium.spells': 'Spells',
  'compendium.searchEquipment': 'Search equipment...',
  'compendium.searchSpells': 'Search spells...',
  'compendium.noEquipment': 'No equipment found.',
  'compendium.noSpells': 'No spells found.',
  'compendium.addToInventory': 'Add to Inventory',
  'compendium.addToSpellbook': 'Add to Spellbook',
  'compendium.cost': 'Cost',
  'compendium.weight': 'Weight',
  'compendium.ac': 'AC',
  'compendium.homebrew': 'Homebrew',
  'compendium.concentration': 'Concentration',
  'compendium.properties': 'Properties',

  // === Chat additions ===
  'chat.search': 'Search',
  'chat.searchPlaceholder': 'Search...',
  'chat.unpinMessage': 'Unpin message',
  'chat.pinMessage': 'Pin message',
  'chat.gmPrefix': '[GM] ',
  'chat.exportTitle': '# Chat Export',

  // === Character Wizard additions ===
  'wizard.characterNameLabel': 'Character Name',
  'wizard.characterNamePlaceholder': 'Enter character name...',
  'wizard.backgroundLabel': 'Background',
  'wizard.backgroundPlaceholder': 'Character background...',
  'wizard.raceLabel': 'Race',
  'wizard.speed': 'Speed',
  'wizard.classLabel': 'Class',
  'wizard.hitDie': 'Hit Die',
  'wizard.selectClassFirst': 'Select a class first.',
  'wizard.noEquipmentPack': 'No starting equipment pack for this class.',
  'wizard.equipped': '(equipped)',

  // === Campaign Import ===
  'campaignImport.textInputHint': 'Paste campaign text or upload a file (.txt, .md)',
  'campaignImport.placeholder': 'Paste campaign description, NPCs, locations, quests...',
  'campaignImport.uploadFile': 'Upload file',
  'campaignImport.limitExceeded': 'Text exceeds the limit. Shorten it or split into parts.',
  'campaignImport.analyzing': 'Analyzing...',
  'campaignImport.analyze': 'Analyze',

  // === Homebrew Import ===
  'homebrewImport.title': 'Import Homebrew Content',
  'homebrewImport.inputLabel': 'Paste text or upload PDF',
  'homebrewImport.placeholder': 'Paste homebrew content here...',
  'homebrewImport.or': 'or',
  'homebrewImport.uploadPdf': 'Upload PDF',
  'homebrewImport.extract': 'Extract Entities',
  'homebrewImport.uploading': 'Uploading PDF...',
  'homebrewImport.backgroundNote': 'You can close this dialog — extraction will continue in background.',
  'homebrewImport.extracting': 'Extracting entities with AI...',
  'homebrewImport.preparingChunks': 'Preparing chunks...',
  'homebrewImport.packageNameLabel': 'Package Name',
  'homebrewImport.save': 'Save',
  'homebrewImport.saving': 'Saving package...',
  'homebrewImport.complete': 'Import complete!',
  'homebrewImport.back': 'Back',
  'homebrewImport.clear': 'Clear',
  'homebrewImport.chunk': 'Chunk',
  'homebrewImport.entities': 'entities',

  // === Entity Review ===
  'entityReview.selectAll': 'Select All',
  'entityReview.deselectAll': 'Deselect All',
  'entityReview.addManual': '+ Add Manual',
  'entityReview.selectedCount': 'selected',
  'entityReview.retryBelow': 'Retry below',
  'entityReview.retry': 'Retry',

  // === Character Portraits ===
  'characterPortraits.changeColor': 'Change color',
  'characterPortraits.online': 'online',
  'characterPortraits.ac': 'AC',
  'characterPortraits.publicOnly': 'Only this character\'s player and the GM see the sheet — this is its public part',
  'characterPortraits.offline': 'offline',
  'characterPortraits.chip': 'Characters ({count})',
  'characterPortraits.chipFold': 'Fold',
  // Visibility menu items
  'characterPortraits.visibility.always': 'Visible to all',
  'characterPortraits.visibility.gmOnly': 'Hidden from players',
  'characterPortraits.visibility.hidden': 'Archived (hidden from everyone)',
  'characterPortraits.visibilityLabel': 'Card on scene',
  'characterPortraits.sheetLabel': 'Character sheet',
  'characterPortraits.sheetVisibleToPlayers': 'Visible to players',
  'characterPortraits.sheetHiddenBadge': 'Sheet hidden from players (hide in the character list)',
  'characterPortraits.sizeLabel': 'Avatar size',
  'characterPortraits.size.small': 'Small',
  'characterPortraits.size.medium': 'Medium',
  'characterPortraits.size.large': 'Large',
  'characterPortraits.actingAs': 'You are acting as this character',
  'characterPortraits.hiddenFromPlayers': 'Hidden from players',
  // Tooltips
  'characterPortraits.tooltip.actorFrame': 'Active actor frame',
  'characterPortraits.tooltip.translucency': 'Translucency',

  // === Text Input Overlay ===
  'textInput.ariaLabel': 'Text input overlay',
  'textInput.placeholder': 'Enter text...',

  // === Dice Panel ===
  'dice.formulaPlaceholder': '2d6+3, 1d20+5...',

  // === Lobby ===
  'lobby.sessionCodePlaceholder': 'ABC123',

  // === Hook Errors ===
  'hookError.catalogFailed': 'Catalogization failed',
  'hookError.noEntitiesFound': 'No campaign entities found. Try refining the text.',
  'hookError.detailFailed': 'Detailization failed',
  'hookError.noResponseBody': 'No response body',
  'hookError.relationFailed': 'Relation extraction failed',
  'hookError.applyFailed': 'Apply failed',
  'hookError.fileTooLarge': 'File too large',
  'hookError.serverError': 'Server error',
  'hookError.networkError': 'Network error',
  'hookError.extractionFailed': 'Extraction failed',
  'hookError.streamEnded': 'Stream ended without result',
  'hookError.retryFailed': 'Retry failed',
  'hookError.saveFailed': 'Save failed',
  'hookError.loadFailed': 'Failed to load',
  'hookError.createFailed': 'Create failed',
  'hookError.uploadFailed': 'Upload failed',
  'hookError.generationFailed': 'Generation failed',
  'hookError.canvasContext': 'Canvas 2d context unavailable',
  'hookError.mapLoadFailed': 'Failed to load map image',
  'hookError.pageCreateTimeout': 'Timeout waiting for page creation',
  'hookError.lassoMinPoints': 'Lasso needs at least 3 points',
  'hookError.exportFailed': 'Failed to export mask',
  'homebrew.defaultPackageName': 'Imported Homebrew',

  // === Homebrew Library / Import Flow (P2) ===
  'homebrew.libraryTitle': 'Homebrew Library',
  'homebrew.import': '+ Import',
  'homebrew.importJson': 'Import JSON',
  'homebrew.libraryEmpty': 'No packages yet. Import homebrew content to get started.',
  'homebrew.loading': 'Loading...',
  'homebrew.export': 'Export',
  'homebrew.delete': 'Delete',
  'homebrew.confirm': 'Confirm',
  'homebrew.cancel': 'Cancel',
  'homebrew.entities': 'entities',
  'homebrew.connectTitle': 'Homebrew Packages',
  'homebrew.connectLoading': 'Loading packages...',
  'homebrew.connectEmpty': 'No homebrew packages in library. Import some content first.',
  'homebrew.checkUpdates': 'Check updates',
  'homebrew.extracting': 'Extracting...',
  'homebrew.reviewReady': 'Extraction complete — click to review {{count}} entities',
  'homebrew.type.class': 'Class',
  'homebrew.type.subclass': 'Subclass',
  'homebrew.type.spell': 'Spell',
  'homebrew.type.feat': 'Feat',
  'homebrew.type.race': 'Race',
  'homebrew.type.item': 'Item',
  'homebrew.connectFailed': 'Failed to connect package',
  'homebrew.updateFailed': 'Failed to check updates',

  // === Lazy chunk loading ===
  'error.chunkLoadFailed': 'Failed to load component. Check your internet connection.',

  // === Common additions ===
  'common.retry': 'Try again',

  // === Character HUD (066) ===
  'characterHud.pillHp': 'HP',
  'characterHud.expand': 'Expand',
  'characterHud.collapse': 'Collapse',
  'characterHud.openFullSheet': 'Full sheet',
  'characterHud.startCombat': '⚔ start combat',
  'characterHud.expandDocked': 'Dock panel',
  'characterHud.showCharacter': 'Character: stats, skills, features',
  'characterHud.combatBanner': '⚔ your turn',
  'characterHud.combatBannerTurnOf': '⚔ turn: {name}',
  'characterHud.combatBannerIdle': '⚔ combat',
  'characterHud.round': 'round',
  'characterHud.tileHp': 'HP',
  'wizard.rerollUsed': 'Re-roll used',
  'wizard.notProficient': 'Not proficient — this option is not available',
  'wizard.missing.equipment': 'equipment without proficiency (e.g. a warhammer for a cleric without martial weapons)',
  'characterHud.castAsRitual': '«{spell}»: no slot — cast as a ritual (+10 minutes)',
  'characterHud.resurrect': 'Resurrect',
  'characterHud.resurrectHp': 'hit points after resurrection',
  'error.dying_no_hit_dice': 'No hit dice while dying — stabilise the character first',
  'levelUp.aria': 'Level up',
  'levelUp.title': 'Level up: {class} {from} → {to}',
  'levelUp.hp': 'Hit points',
  'levelUp.hitDieCon': 'Hit die: d{die} · CON modifier: {con}',
  'levelUp.average': 'Average ({n} HP)',
  'levelUp.roll': 'Roll (1d{die} {con})',
  'levelUp.subclass': 'Subclass',
  'levelUp.newFeatures': 'New features',
  'levelUp.spellSlots': 'Spell slots',
  'levelUp.slotLine': 'Level {lvl}: {n}',
  'levelUp.cantrips': '+{n} cantrip(s)',
  'levelUp.asi': 'Ability score improvement',
  'levelUp.asiTwo': '+2 to one ability',
  'levelUp.asiOneOne': '+1 to two abilities',
  'levelUp.confirm': 'Level up to {n}',
  'characterHud.rollDamage': 'damage',
  'characterHud.noSlotForSpell': '«{spell}»: no free slot of level {n} or higher — a rest restores them',
  'characterHud.concentrationReplaced': 'Concentration on «{prev}» ends — now «{spell}»',
  'characterHud.concentrationOn': 'Concentration: {spell}',
  'characterHud.concentrationEnd': 'End',
  'characterHud.spellTapHint': 'tap — cast',
  'characterHud.deadNoRoll': 'The character is dead — no rolls',
  'characterHud.hpInputLabel': 'Damage or healing',
  'characterHud.hpInputPlaceholder': '−7 / +5',
  'characterHud.hpInputHint': 'Type «−7» (damage), «+5» (healing) or «=12» (exact), or a number and a button',
  'characterHud.unconsciousNoAct': 'Unconscious (0 hit points): no attacks or spells — death saves or healing',
  'characterHud.rollMode': 'Next d20',
  'characterHud.rollModeNormal': 'normal',
  'characterHud.rollModeAdv': 'advantage',
  'characterHud.rollModeDis': 'disadvantage',
  'characterHud.rollModeHint': 'For one roll: attack, check, skill, save, initiative. The character\'s conditions are counted on their own',
  'rollCard.advantage': 'advantage',
  'rollCard.disadvantage': 'disadvantage',
  'rollCard.modeManual': 'by hand',
  'rollCard.keptHint': 'counts',
  'characterHud.hpDamage': 'Damage',
  'characterHud.hpHeal': 'Heal',
  'characterHud.hpCritAtZero': 'critical hit (two failures)',
  'characterHud.hpDeadHint': 'The character is dead — only the GM brings them back: «Resurrect» in the death saves card',
  'characterHud.hpMax': 'Hit point maximum',
  'characterHud.hpMaxShort': 'Max',
  'characterHud.hpTempShort': 'Temp',
  'characterHud.saveRollTitle': 'Saving throw: {stat}',
  'characterHud.tileAc': 'AC',
  'characterHud.tileInit': 'Init',
  'characterHud.tileConc': 'Conc',
  'characterHud.tileSpeed': 'Spd',
  'characterHud.actionAction': 'Action',
  'characterHud.actionBonus': 'Bonus',
  'characterHud.actionReaction': 'Reaction',
  'characterHud.available': 'available',
  'characterHud.spent': 'spent',
  'characterHud.attacks': 'Attacks',
  'characterHud.spellSlots': 'Spell Slots',
  'characterHud.effects': 'Effects',
  'characterHud.noConcentration': '—',
  'characterHud.mockRoll': 'Roll (demo)',
  'characterHud.mockRollDesc': 'Demo roll, not sent to server.',
  'characterHud.tabCombat': 'Combat',
  'characterHud.tabCharacter': 'Character',
  'characterHud.tabCharShort': 'Char',
  'characterHud.tabRoleplay': 'Roleplay',
  'characterHud.tabBuilder': 'Builder',
  'characterHud.backToHud': '← back to HUD',
  'characterHud.inDevelopment': 'In development',
  'characterHud.stats': 'Ability Scores',
  'characterHud.topSkills': 'Top Skills',
  'characterHud.saves': 'Saving Throws',
  'characterHud.skills': 'Skills',
  'characterHud.searchSkills': 'search skill…',
  'characterHud.pinSkill': 'Pin skill',
  'characterHud.unpinSkill': 'Unpin skill',
  'characterHud.spells': 'Spells',
  'characterHud.detailCantrip': 'cantrip',
  'characterHud.detailCircle': 'circle',
  'characterHud.detailConcentration': 'concentration',
  'characterHud.detailRitual': 'ritual',
  'characterHud.detailCastingTime': 'casting time',
  'characterHud.detailRange': 'range',
  'characterHud.detailDuration': 'duration',
  'characterHud.detailComponents': 'components',
  'characterHud.detailUses': 'uses',
  'characterHud.detailHigherLevels': 'At higher levels',
  'characterHud.conditionAdd': 'condition',
  'characterHud.conditionRemove': 'Remove condition',
  'characterHud.conditionRounds': 'rounds (empty = indefinite)',
  'characterHud.conditionApply': 'Apply',
  'characterHud.collapseList': 'collapse',
  'characterHud.movement': 'Movement',
  'characterHud.movementHint': 'Click — spend 5 ft; at zero — reset',
  'characterHud.resetTurn': 'Reset turn resources (actions + movement)',
  'characterHud.features': 'Features',
  'characterHud.moreFeatures': 'more',
  'characterHud.spellsKnown': 'Prepared Spells',
  'characterHud.demoTitle': 'HUD Demo',
  'characterHud.demoPill': 'Pill',
  'characterHud.demoMini': 'Mini',
  'characterHud.demoCombat': 'Combat',
  'characterHud.demoDocked': 'Docked',
  'characterHud.demoToggle': 'Visibility',
  'characterHud.demoFullSheet': 'Full Sheet',
  'characterHud.demoNpc': 'NPC Card',
  'characterHud.demoReset': 'Reset',
  'characterHud.npcAlly': 'ally',
  'characterHud.npcWarm': 'warm',
  'characterHud.npcNeutral': 'neutral',
  'characterHud.npcFoe': 'foe',
  'characterHud.npcEnemy': 'enemy',
  'characterHud.close': 'Close',
  'characterHud.hpTemp': 'temp',
  'characterHud.level': 'Lv.',
  'characterHud.storeCorrupted': 'Character data corrupted. Loaded default character.',

  // === Builder Mode (066 phase-4) ===
  'characterHud.builder.stepIdentity': 'Character',
  'characterHud.builder.stepRace': 'Race',
  'characterHud.builder.stepClass': 'Class',
  'characterHud.builder.stepStats': 'Abilities',
  'characterHud.builder.stepBackground': 'Background',
  'characterHud.builder.stepSkills': 'Skills',
  'characterHud.builder.stepReview': 'Review',
  'characterHud.builder.alignLG': 'Lawful Good',
  'characterHud.builder.alignNG': 'Neutral Good',
  'characterHud.builder.alignCG': 'Chaotic Good',
  'characterHud.builder.alignLN': 'Lawful Neutral',
  'characterHud.builder.alignN': 'True Neutral',
  'characterHud.builder.alignCN': 'Chaotic Neutral',
  'characterHud.builder.alignLE': 'Lawful Evil',
  'characterHud.builder.alignNE': 'Neutral Evil',
  'characterHud.builder.alignCE': 'Chaotic Evil',
  'characterHud.builder.methodPointBuy': 'Point Buy',
  'characterHud.builder.methodStandardArray': 'Standard Array',
  'characterHud.builder.methodManual': 'Manual',
  'characterHud.builder.identityTitle': 'Character',
  'characterHud.builder.identitySubtitle': 'Basic character information',
  'characterHud.builder.labelName': 'Character name',
  'characterHud.builder.labelGender': 'Gender',
  'characterHud.builder.labelAlignment': 'Alignment',
  'characterHud.builder.placeholderName': 'Enter name…',
  'characterHud.builder.raceTitle': 'Race',
  'characterHud.builder.raceSubtitle': 'Choose your character\'s race',
  'characterHud.builder.subraceLabel': 'Subrace',
  'characterHud.builder.raceSpeed': 'Speed',
  'characterHud.builder.raceSize': 'Size',
  'characterHud.builder.raceLanguages': 'Languages',
  'characterHud.builder.raceTraits': 'Traits',
  'characterHud.builder.classTitle': 'Class',
  'characterHud.builder.classSubtitle': 'Choose your character\'s class',
  'characterHud.builder.classSaves': 'Saving Throws',
  'characterHud.builder.classProficiencies': 'Proficiencies',
  'characterHud.builder.classSkillChoices': 'Skills: choose',
  'characterHud.builder.classHitDie': 'Hit Die',
  'characterHud.builder.statsTitle': 'Ability Scores',
  'characterHud.builder.statsSubtitle': 'Distribute ability score values',
  'characterHud.builder.budgetRemaining': 'Points remaining',
  'characterHud.builder.budgetDone': 'All points spent ✓',
  'characterHud.builder.bgTitle': 'Background',
  'characterHud.builder.bgSubtitle': 'Choose your character\'s background',
  'characterHud.builder.bgSkills': 'Skills',
  'characterHud.builder.bgFeature': 'Feature',
  'characterHud.builder.skillsTitle': 'Skills',
  'characterHud.builder.skillsSubtitle': 'Choose class skills',
  'characterHud.builder.skillsCounter': 'Selected',
  'characterHud.builder.skillFromBg': '— background',
  'characterHud.builder.skillFromRace': '— race',
  'characterHud.builder.reviewTitle': 'Review',
  'characterHud.builder.reviewSubtitle': 'Review your character',
  'characterHud.builder.reviewIdentity': 'Character',
  'characterHud.builder.reviewStats': 'Ability Scores',
  'characterHud.builder.reviewCombat': 'Combat',
  'characterHud.builder.reviewSkills': 'Skills',
  'characterHud.builder.reviewProficiencies': 'Proficiencies',
  'characterHud.builder.reviewSaves': 'Saving Throws',
  'characterHud.builder.reviewErrors': 'Fix errors before saving',
  'characterHud.builder.next': 'Next',
  'characterHud.builder.prev': 'Back',
  'characterHud.builder.save': '✓ Save',
  'characterHud.builder.intro': 'The character wizard: every level 1 choice of the 5e rules. «Rebuild» replaces this sheet\'s rules content (abilities, skills, spells; the equipment if you choose so); the portrait, owner, tokens and roleplay board stay.',
  'characterHud.builder.readOnly': 'Only the sheet\'s owner or the GM can create or rebuild the character.',
  'characterHud.builder.rebuildTitle': 'Rebuild the character?',
  'characterHud.builder.rebuildDesc': 'This sheet\'s abilities, skills, features and spells are replaced by the wizard\'s. The portrait, owner, tokens and roleplay board stay.',
  'characterHud.builder.rebuildLevel': 'The character is level {N} now. The wizard builds level 1: after the rebuild the sheet is level 1 — level it up again ({M} times) with «Gain a level». To keep the level, press «Cancel»',
  'characterHud.builder.keepEquipment': 'Keep the current equipment (otherwise — the starting kit from the wizard)',
  'characterHud.builder.saved': 'Character created!',
  'characterHud.builder.previewPlaceholder': 'Start filling in your character',
  'characterHud.builder.previewSkills': 'Skills',
  'characterHud.builder.previewProficiencies': 'Proficiencies',
  'characterHud.builder.previewHp': 'HP',
  'characterHud.builder.previewAc': 'AC',
  'characterHud.builder.previewSpeed': 'Spd',
  'characterHud.builder.errEmptyName': 'Character name is required',
  'characterHud.builder.errNoRace': 'Choose a race',
  'characterHud.builder.errNoClass': 'Choose a class',
  'characterHud.builder.errPointBuy': 'Spend all 27 points',
  'characterHud.builder.errStandardArray': 'Assign all array values',
  'characterHud.builder.errManualRange': 'Ability scores must be 3–20',
  'characterHud.builder.errNoBackground': 'Choose a background',
  'characterHud.builder.errSkillsUnder': 'Select more skills',
  'characterHud.builder.errSkillsOver': 'Too many skills selected',
  'characterHud.builder.confirmOverwrite': 'Creating a new character will replace the current one. Roleplay data, inventory, and combat settings will be lost. Continue?',
  'characterHud.builder.confirmDiscard': 'You have unsaved changes in the builder wizard. Close without saving?',
  'characterHud.builder.skillOverlap': 'Background skill overlaps with class — you get an extra pick',

  // === Write Infrastructure (073) ===
  'characterHud.writeTimeout': 'Server did not respond, changes may be lost',
  'characterHud.sheetDeleted': 'Character sheet was deleted',
  'characterHud.writeRejected': 'Change rejected by server',
  'characterHud.rollTimeout': 'Roll not confirmed by server',
  'characterHud.restShort': 'Short Rest',
  'characterHud.restLong': 'Long Rest',
  'characterHud.deathSave': 'Death Saving Throw',
  'characterHud.deathDying': 'Dying',
  'characterHud.deathStable': 'Stable',
  'characterHud.deathDead': 'Dead',
  'characterHud.deathSavesTally': 'Death saves: successes and failures',
  'characterHud.hitDiceSpend': 'Spend Hit Die',
  'characterHud.inspirationOn': 'Inspiration gained',
  'characterHud.inspirationOff': 'Inspiration spent',

  // === Character Mode (066 phase-2) ===
  'characterMode.navIdentity': 'Identity',
  'characterMode.navStats': 'Ability Scores',
  'characterMode.navSkills': 'Skills',
  'characterMode.navDefenses': 'Defenses',
  'characterMode.navAbilities': 'Abilities',
  'characterMode.navSpells': 'Spells',
  'characterMode.navInventory': 'Inventory',
  'characterMode.navProficiencies': 'Proficiencies',
  'characterMode.navProgression': 'Progression',
  'characterMode.navResources': 'Resources',
  'characterMode.levelUp': 'Level Up to {N}',
  'characterMode.rest': 'Rest',
  'characterMode.hitDie': 'Hit die',
  'characterMode.levelUpShort': '↑ lvl {N}',
  'characterMode.passivePerception': 'Pass. Perception',
  'characterMode.passiveShort': 'Pass. perc.',
  'characterMode.passiveInvestigation': 'Pass. Investigation',
  'characterMode.passiveInsight': 'Pass. Insight',
  'characterMode.filterAll': 'All',
  'characterMode.filterProficient': 'Proficient',
  'characterMode.filterByAbility': 'By Ability',
  'characterMode.breakdownTotal': 'Total',
  'characterMode.breakdownStatMod': '{stat} modifier',
  'characterMode.breakdownProficiency': 'Proficiency bonus',
  'characterMode.breakdownExpertise': 'Expertise (×2)',
  'characterMode.breakdownEffects': 'Effects',
  'characterMode.breakdownBase': 'Base',
  'characterMode.spellDC': 'Spell DC',
  'characterMode.spellAttack': 'Spell Attack',
  'characterMode.concentration': 'Concentration',
  'characterMode.ritual': 'Ritual',
  'characterMode.prepared': 'Prepared',
  'characterMode.known': 'Known',
  'characterMode.cantrips': 'Cantrips',
  'characterMode.spellLevel': 'Level {N}',
  'characterMode.noSpells': 'No spells available',
  'characterMode.noFeatures': 'No features',
  'characterMode.noItems': 'No items',
  'characterMode.weight': 'Weight',
  'characterMode.currency': 'Currency',
  'characterMode.equipped': 'Equipped',
  'characterMode.attunement': 'Attunement',
  'characterMode.armor': 'Armor',
  'characterMode.weapons': 'Weapons',
  'characterMode.tools': 'Tools',
  'characterMode.languages': 'Languages',
  'characterMode.currentLevel': 'Current Level',
  'characterMode.levelManualHint': 'Manual set: HP/features not granted — use Level Up for full progression',
  'characterMode.nextLevel': 'Next Level',
  'characterMode.xpProgress': '{current} / {required} XP',
  'characterMode.resistances': 'Resistances',
  'characterMode.immunities': 'Immunities',
  'characterMode.vulnerabilities': 'Vulnerabilities',
  'characterMode.conditions': 'Conditions',
  'characterMode.speeds': 'Speeds',
  'characterMode.senses': 'Senses',
  'characterMode.classFeatures': 'Class',
  'characterMode.raceFeatures': 'Race',
  'characterMode.feats': 'Feats',
  'characterMode.allFeatures': 'All',
  'characterMode.inspiration': 'Inspiration',
  'characterMode.hitDice': 'Hit Dice',
  'characterMode.mockLevelUp': 'Level Up (demo)',
  'characterMode.search': 'Search...',
  'characterMode.proficiencyBonus': 'PB',
  'characterMode.walkSpeed': 'Walk',
  'characterMode.unitFt': 'ft.',
  'characterMode.unitLbs': 'lbs.',
  'characterMode.unitGp': 'gp',
  'characterMode.armorClass': 'Armor Class',
  'characterMode.castingTime': 'Casting Time',
  'characterMode.range': 'Range',
  'characterMode.castSpell': 'Cast {name}',
  'characterMode.slotAriaLabel': 'Slot level {level}: {current} of {max} available',
  'characterMode.useAriaLabel': 'Use {i} of {max}, {status}',
  'characterMode.available': 'available',
  'characterMode.spent': 'spent',
  'characterMode.shortRest': 'SR',
  'characterMode.longRest': 'LR',
  'characterMode.asiOrFeat': 'Ability Score Increase / Feat',
  'characterMode.attunementSlot': 'Attunement slot',
  'characterMode.attunementFilled': 'filled',
  'characterMode.attunementEmpty': 'empty',
  'characterMode.bdModifier': 'modifier',
  'characterMode.bdValue': 'Score',
  'characterMode.bdSave': 'saving throw',
  'characterMode.bdMod': 'Mod.',
  'characterMode.bdProficiency': 'proficiency',
  'characterMode.bdExpertise': 'expertise',
  'characterMode.bdPassive': 'passive',
  'characterMode.bdSkillMod': 'skill modifier',
  'characterMode.bdBaseAC': 'Base AC',
  'characterMode.bdShield': 'shield',
  'characterMode.bdItems': 'items',
  'characterMode.bdUnarmored': 'Unarmored',
  'characterMode.bdArmor': 'Armor bonus',
  'characterMode.bdMiscAc': 'Misc bonus',
  'characterMode.bdEffects': 'Effects',
  'characterMode.bdAttack': 'attack',
  'characterMode.bdStatMod': 'Ability mod.',
  'characterMode.bdSpellAttack': 'Spell attack bonus',

  // === Character Mode: Stat short labels (i18n refactor T101) ===
  'characterMode.stat.str': 'STR',
  'characterMode.stat.dex': 'DEX',
  'characterMode.stat.con': 'CON',
  'characterMode.stat.int': 'INT',
  'characterMode.stat.wis': 'WIS',
  'characterMode.stat.cha': 'CHA',
  'characterMode.saveAbbr': 'save',
  'characterMode.saveProficiencyHint': 'Saving throw proficiency for this ability',

  // === Character Mode: Skill labels (i18n refactor T101) ===
  'characterMode.skill.acrobatics': 'Acrobatics',
  'characterMode.skill.animalHandling': 'Animal Handling',
  'characterMode.skill.arcana': 'Arcana',
  'characterMode.skill.athletics': 'Athletics',
  'characterMode.skill.deception': 'Deception',
  'characterMode.skill.history': 'History',
  'characterMode.skill.insight': 'Insight',
  'characterMode.skill.intimidation': 'Intimidation',
  'characterMode.skill.investigation': 'Investigation',
  'characterMode.skill.medicine': 'Medicine',
  'characterMode.skill.nature': 'Nature',
  'characterMode.skill.perception': 'Perception',
  'characterMode.skill.performance': 'Performance',
  'characterMode.skill.persuasion': 'Persuasion',
  'characterMode.skill.religion': 'Religion',
  'characterMode.skill.sleightOfHand': 'Sleight of Hand',
  'characterMode.skill.stealth': 'Stealth',
  'characterMode.skill.survival': 'Survival',

  // === Roleplay Mode (066 phase-3) ===
  'roleplay.boardTitle': 'Roleplay',
  'roleplay.boardSubtitle': 'Character personality and story',
  'roleplay.cardHero': 'Who I Am',
  'roleplay.cardTriada': 'What Drives Me',
  'roleplay.cardManners': 'Mannerisms',
  'roleplay.cardBackstory': 'Backstory',
  'roleplay.cardGoals': 'Goals',
  'roleplay.cardNpc': 'Contacts',
  'roleplay.cardFactions': 'Factions',
  'roleplay.cardSessionLog': 'Session Log',
  'roleplay.cardMoments': 'Key Moments',
  'roleplay.catAll': 'All',
  'roleplay.catPersonality': 'Personality',
  'roleplay.catConnections': 'Connections',
  'roleplay.catPast': 'Past',
  'roleplay.catPresent': 'Present',
  'roleplay.catFuture': 'Future',
  'roleplay.tagPersonality': 'PERSONALITY',
  'roleplay.tagConnections': 'CONNECTIONS',
  'roleplay.tagPast': 'PAST',
  'roleplay.tagPresent': 'PRESENT',
  'roleplay.tagFuture': 'FUTURE',
  'roleplay.done': 'Done',
  'roleplay.doneEsc': 'Done · esc',
  'roleplay.spanQuarter': '¼',
  'roleplay.spanThird': '⅓',
  'roleplay.spanHalf': '½',
  'roleplay.spanTwoThirds': '⅔',
  'roleplay.spanFull': 'full',
  'roleplay.stylePlain': 'plain',
  'roleplay.styleParchment': 'parch',
  'roleplay.styleDark': 'dark',
  'roleplay.addRow': '+ item',
  'roleplay.addGoal': '+ goal',
  'roleplay.addNpc': '+ NPC',
  'roleplay.addFaction': '+ faction',
  'roleplay.addMoment': '+ moment',
  'roleplay.addSession': '+ session',
  'roleplay.duplicate': 'Duplicate',
  'roleplay.pin': 'Pin',
  'roleplay.unpin': 'Unpin',
  'roleplay.changeType': 'Change type…',
  'roleplay.deleteCard': 'Delete…',
  'roleplay.deleteConfirmTitle': 'Delete card',
  'roleplay.deleteConfirmDesc': 'Delete card "{title}"? This cannot be undone.',
  'roleplay.changeTypeConfirm': 'Current card content will be lost. Continue?',
  'roleplay.momentPrologue': 'prologue',
  'roleplay.momentMinor': 'minor',
  'roleplay.momentEncounter': 'encounter',
  'roleplay.momentDilemma': 'dilemma',
  'roleplay.momentRevelation': 'revelation',
  'roleplay.momentTurning': 'turning point',
  'roleplay.momentFinale': 'arc finale',
  'roleplay.relAlly': 'ally',
  'roleplay.relWarm': 'warm',
  'roleplay.relNeutral': 'neutral',
  'roleplay.relEnemy': 'enemy',
  'roleplay.searchPlaceholder': 'Search cards…',
  'roleplay.autoLayout': '▭▭ auto',
  'roleplay.addCard': '+ card',
  'roleplay.addCardTitle': 'New Card',
  'roleplay.addCardHeading': 'Title',
  'roleplay.addCardType': 'Type',
  'roleplay.addCardContent': 'Content',
  'roleplay.addCardSize': 'Size',
  'roleplay.addCardStyle': 'Style',
  'roleplay.addCardSubmit': 'Add',
  'roleplay.addCardCancel': 'Cancel',
  'roleplay.addCardTitlePlaceholder': 'Card title…',
  'roleplay.addCardContentPlaceholder': 'Content…',
  'roleplay.editPlaceholder': 'Click ✎ to edit',
  'roleplay.noResults': 'No results found',
  'roleplay.emptyBoard': 'The board is empty — add the first card with «+ Card»: a goal, a bond, a story moment…',
  'roleplay.labelIdeal': 'Ideal',
  'roleplay.labelBond': 'Bond',
  'roleplay.labelFlaw': 'Flaw',
  'roleplay.priorityArc': 'arc',
  'roleplay.prioritySession': 'session',
  'roleplay.sessionNumber': '#{n}',
  'roleplay.sessionLabel': 'session',
  'roleplay.placeholderTitle': 'Title...',
  'roleplay.placeholderSummary': 'Summary...',
  'roleplay.placeholderDescription': 'Description...',
  'roleplay.placeholderName': 'Name...',
  'roleplay.placeholderNotes': 'Notes...',
  'roleplay.placeholderFaction': 'Faction...',
  'roleplay.placeholderGoal': '...',
  'roleplay.dragToReorder': 'Drag to reorder',
  'roleplay.editOptions': 'Card edit options',
  'roleplay.removeGoal': 'Remove goal',
  'roleplay.loyaltyLevel': 'Loyalty {n}',
  'roleplay.footerCount': '{n} entries',

  // === Studio (T008) ===
  // Breadcrumbs
  'studio.breadcrumb.prep': 'Prep',
  'studio.topbar.renameCampaign': 'Rename the campaign',
  'studio.topbar.unnamed': 'Untitled',
  'studio.topbar.renameFailed': 'Could not rename the campaign',
  'studio.enter': 'Studio',
  'studio.beat.add': 'add beat',
  'studio.beat.cycleStatus': 'Cycle status',
  'studio.rail.search': 'Search everywhere, nested included…',
  'studio.sheet.features': 'Features',
  'studio.sheet.inventory': 'Inventory',
  'studio.sheet.gold': 'Gold',
  'studio.workingSet.empty': 'This beat has no linked entities yet — open one from the Library',
  'studio.workspace.placeholder': 'Select a beat or open an entity from the Library',
  'studio.entity.openFull': 'Open full view',
  'studio.breadcrumb.sessionLabel': 'Session {n}',
  // Table button
  'studio.toTable': 'To Table',
  // === Studio AI Assistant (T019) ===
  'studio.ai.title': 'Tia',
  'studio.ai.groupApplied': 'Applied',
  'studio.ai.accept': 'Accept',
  'studio.ai.reject': 'Reject',
  'studio.ai.revert': '⟲ Revert',
  'studio.ai.badgeConflict': 'conflict',
  'studio.ai.badgeReview': 'review',
  'studio.ai.badgeDraft': 'draft',
  'tia.status.idle': 'waiting',
  'tia.status.thinking': 'thinking…',
  'tia.status.done': 'done',
  'tia.status.error': 'failed',
  'tia.status.notes.one': 'found {n} remark',
  'tia.status.notes.few': 'found {n} remarks',
  'tia.status.notes.many': 'found {n} remarks',
  'tia.say.thinking': 'Looking at the plan and quests…',
  'tia.say.doneN': 'Done — {n} changes.',
  'tia.say.done': 'Done.',
  'tia.say.error': 'That didn\'t work. Try again.',
  'tia.say.noKey': 'No model key — Tia is not answering yet.',
  'tia.say.notes': '{n} remarks. Show them?',
  'tia.collapse': 'Collapse',
  'tia.expand': 'Expand',
  'ai.allowEdits': 'Allow changes',
  'ai.allowEditsHint': 'She changes the prep herself and shows what she changed. Showing to the players and deleting still ask. Does not affect the buttons in Remarks — pressing one is the request.',
  'ai.placeholderAdvise': 'Ask Tia… Tia only advises — she changes nothing herself',
  'ai.focus.prefix': 'Tia sees:',
  'ai.focus.beat': 'beat “{name}”',
  'ai.focus.quest': 'quest “{name}”',
  'ai.focus.record': 'record “{name}”',
  'ai.focus.map': 'map “{name}”',
  'ai.focus.sheet': 'sheet “{name}”',
  'ai.focus.library': 'the Library',
  'ai.focus.remarks': 'Remarks',
  'ai.focus.playingNow': '(playing now)',
  'ai.focus.playing': 'playing “{name}”',
  'ai.focus.table': 'the table',
  'ai.focus.myMap': 'your map “{name}”',
  'ai.focus.playersMap': 'players\' map “{name}”',
  'ai.focus.selected': 'selected {names}',
  'ai.emptyStudio': 'Open something on the left — a beat, a quest, an NPC — or go to the table: the examples under the box change with what you look at.',
  'ai.chip.beat1': 'Make a reserve beat in case the players flee',
  'ai.chip.beat2': 'Add a detail to the prep',
  'ai.chip.beat3': 'What is undone in this beat?',
  'ai.chip.quest1': 'Add an objective',
  'ai.chip.quest2': 'What is open in this quest?',
  'ai.chip.npc1': 'Write a description from the beats\' prep',
  'ai.chip.npc2': 'Who is this — briefly',
  'ai.chip.lib1': 'What is undone in the prep?',
  'ai.chip.lib2': 'What is open in the quests now?',
  'ai.chip.table1': 'What is open in the quests now?',
  'ai.chip.table2': 'Mark an objective as done',
  'ai.chip.table3': 'The players lost — switch the beat',
  'ai.plan.title': 'Plan:',
  'ai.plan.do': 'Do it',
  'ai.plan.done': 'Done — the summary is below.',
  'ai.plan.stale.beat': 'Out of date: the beat has changed',
  'ai.plan.stale.objective': 'Out of date: the objective has changed',
  'ai.plan.stale.missing': 'Out of date: something is gone',
  'ai.plan.stale.gone': 'Out of date — ask again',
  'ai.outward.stale': 'This button is out of date — ask Tia again',
  'ai.focus.beatN': 'Beat {n}',
  'ai.plan.step.reserve': 'I will make the reserve beat “{name}”',
  'ai.plan.step.outcome': 'Fork “{label}” at “{name}”',
  'ai.plan.step.outcomeBare': 'A fork at “{name}”',
  'ai.plan.step.note': 'Note “{name}”',
  'ai.plan.step.objective': 'I will mark “{name}” in “{quest}”',
  'ai.plan.step.objectiveBare': 'I will mark “{name}”',
  'ai.plan.step.current': 'I will make “{name}” current',
  'ai.plan.step.chronicle': 'Chronicle draft “{name}”',
  'ai.plan.step.edit': 'Change “{name}”',
  'ai.plan.step.sheet': 'Change the sheet “{name}”',
  'ai.plan.step.linkQuest': 'Link quest "{name}"',
  'ai.plan.step.other': '{tool}: “{name}”',
  'ai.outward.show': 'Show “{name}” to the players',
  'ai.outward.showCaption': 'the players will see this',
  'ai.outward.delete': 'Delete “{name}”',
  'ai.outward.deleteCaption': 'this cannot be undone',
  'ai.outward.confirmShow': 'Show “{name}” to the players?',
  'ai.outward.confirmShowBody': 'The players will see this. It cannot be taken back.',
  'ai.outward.confirmDelete': 'Delete “{name}”?',
  'ai.outward.confirmDeleteBody': 'This cannot be undone.',
  'ai.outward.confirmShowButton': 'Show',
  'ai.outward.confirmDeleteButton': 'Delete',
  'ai.outward.doneShow': 'The players already see “{name}” — this cannot be undone',
  'ai.outward.doneDelete': 'Deleted: “{name}”',
  'ai.outward.running': 'Running…',
  'ai.outward.partial': 'Done only in part: «{name}» — check what is missing',
  'ai.outward.unknown': 'Not known whether «{name}» happened — check it. It will not run again.',
  'ai.outward.failed': 'Not done: «{name}»',
  'ai.outward.changed': 'It changed since — a new button is below',
  'ai.outward.gone': 'Not done: it is gone',
  'ai.outward.already': 'Already so — nothing to do',
  'ai.outward.saveFailed': 'Could not save — nothing was done, press again',
  'ai.outward.fromSource': 'Before this Tia read records or material someone else may have written — check what you show',
  'ai.outward.sourceUnknown': 'It is not known what material Tia had seen before this — check what you show',
  'ai.plan.unknown.all': 'A step failed: the recorded changes were undone, but the outcome is unknown — check',
  'ai.plan.unknown.partial': 'A step failed: not everything was undone — check the change card',
  'ai.plan.unknown.none': 'A step failed: the changes were not undone — check the change card',
  'ai.topic.line': 'Topic: {title}',
  'ai.topic.close': 'close the topic',
  'ai.topic.discuss': 'Let us discuss this remark',
  'ai.topic.ended': 'Remark closed: {option}.',
  'ai.topic.endedDone': 'Remark closed — done, the summary is below.',
  'ai.topic.closed': 'Topic closed. The remark stays in the list.',
  'ai.remark.fixLine': 'Fix: {title}',
  'ai.remark.hintLine': 'Mark: {title}',
  'aiChange.sheetValue': 'Sheet “{name}”: {field}',
  // AI drafts
  'studio.aiDrafts.stub': 'AI Drafts',
  'aiDrafts.title': 'AI Drafts',
  'aiDrafts.pendingCount': 'Pending review: {count}',
  'aiDrafts.badgeReview': 'review',
  'aiDrafts.badgeDraft': 'draft',
  'studio.aiDrafts.count': 'Pending review',
  // Rail sections
  'studio.rail.plan': 'Plan',
  'studio.rail.library': 'Library',
  // Beat statuses
  'studio.beat.status.draft': 'Not ready',
  'studio.beat.status.ready': 'Ready',
  'studio.beat.status.played': 'Played',
  'studio.beat.status.carried': 'Postponed',
  'studio.beat.chooseStatus': 'Choose the beat\'s status',
  'plan.title': 'Game plan',
  'plan.empty.title': 'No prepared beats yet',
  'plan.empty.body': 'The plan shows the beats prepared in Studio: read the prep, open NPCs and clues, mark what is being played now and what is done — without leaving the table. Beats are created in Studio.',
  'plan.openStudio': 'Open Studio',
  'plan.openInStudio': 'Open in Studio',
  'plan.current.label': 'Now playing',
  'plan.current.none': 'No beat is marked as current',
  'plan.makeCurrent': 'Make current',
  'plan.endCurrent': 'End the beat',
  'plan.pending': 'Waiting for the server…',
  'plan.refused': 'Not applied: {reason}',
  'plan.lostAnswer': 'The connection dropped before the server answered. Shown is what the server has confirmed.',
  'plan.prep': 'Prep',
  'plan.prep.none': 'No prep text',
  'plan.unavailable': 'Deleted — unavailable',
  'plan.map.deleted': 'The map was deleted',
  'plan.map.openHere': 'Open for me',
  'plan.map.show': 'Show map to players',
  'plan.map.showing': 'Showing…',
  'plan.maps': 'Maps',
  'mapView.editsSaved': 'edits are saved',
  'mapView.viewedDeleted': 'The map you were viewing was deleted — you are back on the players\' map.',
  'plan.next.button': 'Next: {beat}',
  'plan.next.last': 'This is the last beat of the plan',
  'plan.next.markPlayed': 'Mark as played',
  'plan.next.back': '← Back to “{beat}”',
  'plan.next.another': 'Another beat…',
  'plan.next.hint': 'Switches the current beat for the GMs only. The players are shown nothing.',
  'plan.list.label': 'Plan',
  'plan.list.view': 'View',
  'plan.list.now': 'now',
  'plan.viewing': 'Viewing: {beat} — not the current beat',
  'plan.viewing.back': 'Back to the current beat',
  'plan.pick.hint': 'Pick where to continue.',
  'plan.sec.quest': 'Quest',
  'plan.sec.maps': 'Beat maps',
  'plan.sec.noMaps': 'No maps',
  'plan.sec.notes': 'Beat notes',
  'plan.sec.npcs': 'Beat NPCs',
  'plan.sec.quick': 'Quick note to the beat',
  'plan.npc.hint': 'drag onto the map — a token',
  'plan.quick.add': 'Add',
  'plan.quick.into': 'To the beat: {beat}',
  'plan.quick.pick': 'Pick the current beat',
  'plan.quick.placeholder': 'The players befriended the goblin Yeemik',
  'plan.hint.showMap': 'Show the players the primary map of the beat, “{map}”?',
  'plan.hint.dismiss': 'Close',
  'map.row.playersHere': 'players here',
  'map.row.viewing': 'viewing',
  'map.row.openForMe': 'Open for me',
  'map.row.more': 'More',
  'map.row.playersAlreadyHere': 'Players are already here',
  'map.row.studioNote': 'Rename, order, nesting, delete — in Studio',
  'maps.beat.title': 'Maps of the beat “{beat}”',
  'maps.beat.none': 'Maps of the beat — no current beat',
  'maps.shared.title': 'Shared maps',
  'maps.others.show': 'From other beats ({count})',
  'maps.others.hide': 'Hide',
  'maps.on.title': 'On the map “{map}”',
  'maps.studioHint': 'Rename, order, nesting and delete — in Studio.',
  'studio.mapMenu.rename': 'Rename…',
  'studio.mapMenu.moveUp': 'Move up',
  'studio.mapMenu.moveDown': 'Move down',
  'studio.mapMenu.nest': 'Nest into…',
  'studio.mapMenu.delete': 'Delete…',
  'studio.mapMenu.aiMap': 'AI map…',
  'studio.mapMenu.clearDrawings': 'Clear drawings',
  'studio.mapMenu.imagePosition': 'Image position…',
  'studio.mapDialog.renameTitle': 'Rename the map',
  'studio.mapDialog.nestTitle': 'Nest the map',
  'studio.mapDialog.parent': 'Parent map',
  'studio.mapDialog.topLevel': '— top level —',
  'studio.mapDialog.positionTitle': 'Image position',
  'studio.mapDialog.moveImage': 'Move image',
  'studio.mapDialog.moveImageHint': 'Drag the image on the map. Esc — leave.',
  'studio.mapDialog.moveImageActive': 'Moving the image: drag on the map. Esc — leave.',
  'studio.mapDialog.save': 'Save',
  'studio.mapDialog.cancel': 'Cancel',
  'studio.mapDialog.nameLabel': 'Name',
  'studio.mapCrumbs.label': 'Map path',
  'studio.mapDrawer.open': 'Map contents',
  'studio.mapDialog.px': 'px',
  'beat.next.title': 'Where next',
  'beat.next.outcome': 'If {label} → {beat}',
  'beat.next.addFork': '＋ Fork',
  'beat.next.formLabel': 'New fork',
  'beat.next.if': 'If',
  'beat.next.ifPlaceholder': 'lost',
  'beat.next.leadsTo': 'Leads to',
  'beat.next.newReserve': '＋ New reserve beat',
  'beat.next.reserveName': 'Name of the reserve beat',
  'beat.next.reservePlaceholder': 'Captured in the lair',
  'beat.next.optionMain': 'Beat {n}: {name}',
  'beat.next.optionReserve': 'Reserve: {name}',
  'beat.next.noForks': 'No forks — next by plan order',
  'beat.next.removeFork': 'Remove fork',
  'beat.next.add': 'Add',
  'beat.next.cancel': 'Cancel',
  'beat.reserve.pill': 'reserve',
  'beat.reserve.title': 'Reserve beat',
  'beat.reserve.from': 'A fork from the beat “{beat}” — not in the main line of the plan',
  'beat.reserve.railCaption': 'If {label}',
  'mapView.label': 'Maps: yours and the players\'',
  'mapView.youSee': 'You see: {name}',
  'mapView.playersSee': 'Players see: {name}',
  'mapView.together': 'You and the players see: {name}',
  'mapView.playerSees': 'Player sees: {name}',
  'mapView.togetherHint': 'This is the players\' map: edits on it are visible to them at once.',
  'mapView.ownViewHint': 'Your own view: the players stay on “{name}”. Edits to this map are saved in it — other GMs see them at once, players when the map is shown to them.',
  'mapView.returnToPlayers': 'Back to the players\' map',
  'mapView.show': 'Show to players…',
  'mapView.showing': 'Showing…',
  'mapView.confirmTitle': 'Show “{name}” to the players?',
  'mapView.confirmBody': 'The players are on “{current}” now. A “Transition” draft will appear in the chronicle — the players will not see it until you publish it.',
  'mapView.confirm': 'Show',
  'mapView.showWithParty': 'Show with the characters ({n})',
  'mapView.showMapOnly': 'Map only',
  'mapView.shownCarried': 'Characters moved: {n}',
  'mapView.shownWithoutParty': 'The map is shown without the characters',
  'mapView.carried': "players' characters moved: {n}",
  'mapView.openPlayersMap': 'Open the players\' map',
  'mapView.shown': 'The players see “{name}”',
  'mapView.shownDraft': 'The transition is a chronicle draft: the players do not see it until you publish it.',
  'mapView.backTo': 'Back to “{name}”',
  // Working-set chip labels
  'studio.workingSet.pin': 'Pin',
  'studio.workingSet.unpin': 'Unpin',
  // Tabs
  'studio.tabs.noTabs': 'No open tabs',
  'studio.tabs.close': 'Close tab',
  'studio.tabs.preview': 'Preview',
  'studio.tabs.suggestedFromBeat': 'Suggested from beat',
  'studio.tabs.kindEntry': 'Entry: {type}',
  'studio.tabs.selected': 'main view',
  'studio.tabs.beside': 'open beside',
  'studio.tabs.temporary': 'temporary tab',
  'studio.tabs.playersMap': 'the players see this map',
  'studio.ref.named': '{type} “{name}”',
  'studio.side.region': 'Second pane: {name}',
  'studio.side.temporary': 'Temporary view',
  'studio.side.temporaryHint': 'The next object you open from a map replaces this one. Pin it to keep it.',
  'studio.side.pinned': 'Pinned',
  'studio.side.close': 'Close',
  'studio.full.restore': 'Back to two panes',
  'studio.full.restoreLabel': 'Back to two panes — beside: {name}',
  'studio.full.exit': 'Leave full view',
  'studio.full.openBeside': 'Open beside',
  'studio.full.openBesideLabel': 'Open beside — choose an object for the second pane',
  'studio.full.besidePalette': 'What to open in the second pane…',
  'studio.replace.title': 'Replace the pinned pane?',
  'studio.replace.body': 'The second pane holds a pinned {current}. Open {next} there instead? {current} stays in the tabs.',
  'studio.mapMenu.label': 'Map actions',
  'studio.mapMenu.noSnapshots': 'No other snapshots yet',
  // Create from Studio
  'studio.create.entryPrefix': 'New entry: ',
  'studio.create.sheetPc': 'New sheet: character',
  'studio.create.sheetNpc': 'New sheet: NPC',
  'studio.create.map': 'New map',
  'studio.create.namePrompt': 'Name',
  'studio.create.failed': 'Could not create “{name}”',
  'studio.create.retry': 'Retry',
  'studio.create.unresolved': 'the server could not tell whether it was created',
  'studio.create.unresolvedTitle': 'Could not find out whether “{name}” was created — check before creating it again',
  'studio.create.goneSince': 'it was created and has since been deleted',
  'studio.rail.create': 'Create',
  'studio.library.createAny': '＋ Create…',
  'studio.beat.delete': 'Delete beat',
  'studio.beat.deleteConfirm': 'Delete this beat? Its journal note stays.',
  // EntityView
  'studio.entity.cardPreview': 'Card Preview',
  'studio.entity.tokenCount': 'Tokens: {count}',
  'studio.entity.openWorkshop': 'Open for me',
  'studio.entity.openWorkshopHint': 'Leave Studio and open this map at the table for yourself only. The players stay on their map.',
  'studio.entity.snapshots': 'Snapshots of this location',
  'studio.entity.snapshotCreate': '＋ Create snapshot',
  'studio.entity.snapshotCreateHint': 'Save the current state of this map as a separate map — a snapshot of this location',
  'studio.entity.snapshotPrompt': 'Snapshot name',
  // === Studio Map (T003) ===
  'studio.map.empty': 'No map on this page yet',
  'studio.map.legend': 'Legend',
  // Peek captions
  'studio.peek.ac': 'AC',
  'studio.peek.hp': 'HP',
  'studio.peek.hpMax': 'Max HP',
  'studio.entry.delete': 'Delete the entry',
  'studio.entry.deleteTitle': 'Delete “{name}”?',
  'studio.entry.deleteBody': "The entry leaves the journal, the players' too. A map marker, if any, stays without an entry. This cannot be undone.",
  'studio.peek.speed': 'Speed',
  'studio.palette.hint': 'Find · go to · create…',
  'studio.palette.placeholder': 'Where to go or what to create?',
  'studio.palette.empty': 'Nothing found',
  'studio.palette.createBeat': 'New beat',
  'studio.palette.kind.beat': 'beat',
  'studio.palette.kind.sheet': 'sheet',
  'studio.palette.kind.journal': 'entry',
  'studio.palette.kind.page': 'map',
  'studio.palette.kind.action': 'action',
  'studio.sheet.mentionedIn': 'Mentioned in',
  'studio.beat.one': 'Beat',
  'studio.beat.overview': 'Beat overview',
  'studio.map.targetMissing': 'This item is no longer available',
  'studio.beat.rename': 'Double-click to rename',
  'studio.beatPanel.note': 'Prep text source',
  'studio.beatPanel.none': '— none —',
  'studio.beatPanel.hint': 'Link a note — the working set assembles from its connections',
  'studio.beat.prepEntryName': '{beat} — prep',
  'studio.beatPanel.prep': 'Prep text',
  'studio.beatPanel.prepPlaceholder': 'What happens in this beat: scene, NPCs, clues…',
  'studio.beatPanel.prepAdd': '＋ Add prep text',
  'studio.beatPanel.prepPending': 'Preparing the text field…',
  'studio.beatPanel.prepLoading': 'Loading…',
  'studio.beatPanel.prepFailedEntry': 'Could not create the entry for the text',
  'studio.beatPanel.prepFailedLink': 'The entry is created but not linked to the beat',
  'studio.beatPanel.prepFailedToast': 'Prep text for “{beat}”: {step}',
  'studio.beatPanel.prepRetry': 'Retry',
  'studio.beatPanel.prepMissing': 'The entry with this beat’s text was deleted.',
  'studio.beatPanel.prepSource': 'Source',
  'studio.beatPanel.prepOpen': 'Open material',
  'studio.beatPanel.prepShared': 'Shared entry — the same text in: {beats}',
  'studio.beatPanel.prepOtherType': 'A “{type}” entry: shown as is, edited in the material.',
  'studio.materials.title': 'Materials',
  'studio.materials.empty': 'The beat’s materials gather here: NPCs, sheets, clues. Opening a document does not add it.',
  'studio.materials.add': '＋ Add material',
  'studio.materials.groupUnfold': 'Show each',
  'studio.materials.groupFold': 'Fold',
  'studio.bestiary.count': 'How many',
  'studio.materials.addTo': 'Add to “{beat}”',
  'studio.materials.search': 'Search entries and sheets…',
  'studio.materials.inBeat': 'in the beat',
  'studio.materials.isPrepSource': 'prep source',
  'studio.materials.nothingFound': 'Nothing found',
  'studio.materials.open': 'Open',
  'studio.materials.remove': 'Remove from beat',
  'studio.materials.adding': 'adding…',
  'studio.materials.removing': 'removing…',
  'studio.materials.offline': 'no connection — sent when it is back',
  'studio.materials.addFailed': 'Material not added',
  'studio.materials.removeFailed': 'Material not removed',
  'studio.materials.retry': 'Retry',
  'studio.materials.dismiss': 'Dismiss',
  'studio.materials.unavailable': 'Deleted — no longer in the campaign',
  'studio.materials.create': '＋ Create material',
  'studio.materials.chooseType': 'Type of the new material:',
  'studio.materials.creating': 'creating “{name}”…',
  'studio.materials.createUnknown': 'connection lost — the outcome is checked when it is back',
  'studio.materials.createUnresolved': 'The server could not confirm whether “{name}” was created — check the library and add it from the search',
  'studio.materials.close': 'Close',
  'studio.materials.sheetPc': 'PC sheet',
  'studio.materials.sheetNpc': 'NPC sheet',
  'studio.materials.noOwner': 'no owner',
  'studio.materials.map': 'Map',
  'studio.maps.title': 'Beat maps',
  'studio.maps.empty': 'No maps. Add the campaign maps this beat needs: tabs and location snapshots never land here by themselves.',
  'studio.maps.primary': 'Primary',
  'studio.maps.playersSee': 'Players see',
  'studio.maps.deleted': 'Map deleted',
  'studio.maps.openPrimary': 'Open the primary map',
  'studio.maps.primaryDeleted': 'The primary map was deleted',
  'studio.maps.noPrimary': 'No primary map',
  'studio.maps.open': 'Open',
  'studio.maps.beside': 'Beside',
  'studio.maps.show': 'Show to players…',
  'studio.maps.showing': 'Showing…',
  'studio.maps.makePrimary': 'Make primary',
  'studio.maps.unsetPrimary': 'Unmark primary',
  'studio.maps.up': 'Move up',
  'studio.maps.down': 'Move down',
  'studio.maps.remove': 'Remove from beat',
  'studio.maps.addPlaceholder': 'Campaign map…',
  'studio.maps.add': 'Add',
  'studio.maps.addAsPrimary': 'Add — becomes the primary',
  'studio.maps.allIn': 'Every campaign map is in the beat',
  'studio.maps.create': '＋ New map',
  'studio.maps.createPrompt': 'Name of the new map',
  'studio.maps.createPick': 'Choose an image…',
  'studio.maps.createChange': 'Another image…',
  'studio.maps.createNoImage': 'You can add the image later',
  'studio.maps.createSubmit': 'Create the map',
  'studio.maps.creating': 'Creating the map «{name}»…',
  'studio.maps.createFailed': 'Could not create the map',
  'studio.maps.createUnknown': 'The connection dropped — after reconnecting we ask the server whether «{name}» was created',
  'studio.maps.createUnresolved': 'Could not find out whether «{name}» was created. Check the campaign maps before creating it again.',
  'studio.mapImage.upload': 'Upload image',
  'studio.mapImage.replace': 'Replace image',
  'studio.mapImage.uploading': 'Uploading «{name}»…',
  'studio.mapImage.saving': 'Saving…',
  'studio.mapImage.offline': 'The connection dropped — the image is sent again after reconnecting',
  'studio.mapImage.failed': 'Could not set «{name}»',
  'studio.mapImage.uploadFailed': 'the upload failed',
  'studio.maps.adding': 'adding…',
  'studio.maps.removing': 'removing…',
  'studio.maps.primaryChanging': 'changing the primary…',
  'studio.maps.moving': 'reordering…',
  'studio.maps.offline': 'offline — will be sent when the connection is back',
  'studio.maps.lost': 'the answer was lost — the server’s state is shown',
  'studio.maps.addFailed': 'Map not added',
  'studio.maps.removeFailed': 'Map not removed',
  'studio.maps.primaryFailed': 'Primary not changed',
  'studio.maps.moveFailed': 'Order not changed',
  'studio.maps.retry': 'Retry',
  'studio.maps.dismiss': 'Dismiss',
  'studio.maps.removePrimaryTitle': 'Remove the primary map “{name}” from the beat?',
  'studio.maps.removePrimaryBody': 'The map stays in the campaign. Choose a new primary or leave the beat without one — another map is never chosen by itself.',
  'studio.maps.keepNoPrimary': 'Leave without a primary',
  'studio.maps.makeNextPrimary': 'Make “{name}” the primary',
  'studio.maps.removeConfirm': 'Remove',
  'studio.maps.cancel': 'Cancel',
  'studio.beatPanel.prepUnknown': 'Connection lost before the server answered — the outcome is checked when it is back',
  'studio.beatPanel.prepUnresolved': 'The server could not confirm whether the entry was created. If it is in “Prep text source”, choose it there; if not, create a new one (one created meanwhile stays in the library).',
  'studio.beatPanel.prepCreateNew': '＋ Create a new entry',
  'studio.beatPanel.prepSaveFailed': 'Edit not saved',
  'studio.beatPanel.prepResave': 'Save again',
  'studio.beatPanel.prepOffline': 'No connection — the text is saved when it is back',
  'studio.library.empty': 'Nothing in this campaign yet — create with “＋ Create…” or import',
  'studio.library.noteLead': 'Shared records: {shared}. Hidden in beats and objectives: {nested} (notes, maps, prep texts)',
  'studio.library.noteFine': 'Created inside a beat or an objective, it lives there. “Make shared” moves it here.',
  'studio.rail.kindPc': 'Character',
  'studio.rail.kindNpcSheet': 'NPC sheet',
  'studio.rail.createSheetPc': '＋ Character',
  'studio.rail.createSheetNpc': '＋ NPC sheet',
  'studio.rail.mapsOnlyInBeats': '{count} more — only in beats',
  'studio.palette.askAssistant': 'Ask the assistant',
  'studio.workingSet.add': 'Add to working set',
  'studio.rail.draftBadge': 'draft',
  'studio.sheet.pendingDrafts': 'AI drafts · pending review',
  'studio.rail.sheetsPc': 'Sheets · characters',
  'studio.rail.sheetsNpc': 'Sheets · NPC',
  'studio.rail.maps': 'Campaign maps',
  'studio.rail.nested': 'More in beats and quests ({count})',
  'studio.rail.nestedBeat': 'Beat “{name}”',
  'studio.rail.nestedQuest': 'Quest “{name}”',
  'studio.owner.inBeat': 'in beat “{name}”',
  'studio.owner.inObjective': 'in objective “{name}”',
  'studio.owner.inQuest': 'in quest “{name}”',
  'studio.owner.toShared': 'To the shared list',
  'studio.owner.mapToBeat': 'Only for this beat',
  'studio.owner.move': 'Move into a beat / objective',
  'studio.owner.movePick': 'Move into',
  'studio.owner.moveBeatGroup': 'Beats',
  'studio.owner.moveObjectiveGroup': 'Quest objectives',
  'studio.owner.objectiveOption': '{quest} · objective “{objective}”',
  'studio.owner.noteInBeat': '＋ Note in beat',
  'studio.owner.noteToObjective': '＋ Note to objective',
  'studio.owner.beatNotes': 'Beat notes',
  'studio.owner.newNoteName': 'New note',
  'studio.noteForm.name': 'Note title',
  'studio.noteForm.players': 'Players see',
  'studio.noteForm.gm': 'For the GM',
  'studio.noteForm.done': 'Done',
  'studio.noteForm.cancel': 'Cancel',
  'studio.owner.failed': 'Could not change where the entry lives',
  'studio.owner.secondUseTitle': '“{name}” lives {place}',
  'studio.owner.secondUseBody': 'The entry lives only there. “To the shared list and add” makes it shared, in the Library. “Add, leave where it is” keeps it there and uses it here too. The entry is never moved on its own.',
  'studio.owner.secondUseShare': 'To the shared list and add',
  'studio.owner.secondUseKeep': 'Add, leave where it is',
  'studio.owner.secondUseCancel': 'Cancel',
  'studio.note.playersSee': 'Players see',
  'studio.note.forGm': 'For the GM',
  'studio.note.typeWord': 'Note',
  'studio.note.placeOnMap': 'Place on map',
  'studio.note.placeHint': 'Click where the players can find it. The pin is seen only by you at first.',
  'studio.note.onMapGm': 'on the map · GM only',
  'studio.note.onMapShown': 'on the map · players see it',
  'studio.note.pinOn': 'Pin on “{map}”',
  'studio.note.pinOffscreen': 'Pin on “{map}”. Press “Map beside” to see it.',
  'studio.note.pinPlaced': 'Pin “{name}” on “{map}” · seen only by you',
  'studio.note.makeShared': 'Make shared',
  'studio.note.pinTitle': 'Show the pin on the map',
  'studio.note.dragTitle': 'Drag onto the map — it becomes a pin',
  'studio.note.noMapForPlace': 'There is no beat map to put this note on',
  'studio.beat.mapBeside': 'Map beside',
  'studio.beat.mapHide': 'Hide map',
  'studio.mapbar.map': 'Map',
  'studio.mapbar.thisBeat': 'This beat\'s map',
  'studio.mapbar.otherBeat': 'Not this beat\'s map',
  'studio.mapbar.dropHint': 'Drag a note or an NPC of the beat here. A click on a pin scrolls the beat to its note.',
  'studio.noteDrop.inBeat': '“{note}” lives in the beat “{beat}”',
  'studio.noteDrop.inObjective': '“{note}” lives in the objective “{objective}”',
  'studio.noteDrop.body': 'You are placing it on another beat\'s map — the note will be needed in two places.',
  'studio.noteDrop.share': 'Make shared and place',
  'studio.noteDrop.keep': 'Place, keep in the beat',
  'studio.noteDrop.cancel': 'Cancel',
  'pinPopup.show': 'Show to players',
  'pinPopup.shown': 'Visible to players',
  'pinPopup.tick': 'Mark objective “{objective}”',
  'pinPopup.objectiveDone': 'Objective ticked',
  'pinPopup.hint': 'Two separate buttons: showing to the players does not tick the objective, and the other way round.',
  'pinPopup.confirmTitle': 'Show “{name}” to the players?',
  'pinPopup.confirmBody': 'They will see the pin and the part «Players see».',
  'pinPopup.confirm': 'Show',

  // === Studio Start Screen (T001) ===
  // Doors
  'studio.start.tell.title': 'Tell a story',
  'studio.start.tell.desc': 'Describe a scene or idea — the assistant drafts entities for you',
  'studio.start.load.title': 'Load a campaign',
  'studio.start.load.desc': 'Import text, notes, or a file — we extract NPCs, locations, and quests',
  'studio.start.first.title': 'Create the first beat',
  'studio.start.first.desc': 'Start by hand: name the beat and write its prep text right in the overview',
  // Bulk review — group labels
  'studio.start.bulk.group.npc': 'NPCs',
  'studio.start.bulk.group.location': 'Locations',
  'studio.start.bulk.group.note': 'Notes',
  'studio.start.bulk.group.journal': 'Journal entries',
  'studio.start.bulk.group.encounter': 'Encounters',
  // Bulk review — selected/total counter
  'studio.start.bulk.selected': 'Selected {selected} of {total}',
  // Bulk review — confidence buckets
  'studio.start.bulk.conf.high': 'High confidence (≥80%)',
  'studio.start.bulk.conf.mid': 'Medium confidence (50–79%)',
  'studio.start.bulk.conf.low': 'Low confidence (<50%)',
  // Bulk review — action buttons
  'studio.start.bulk.apply': 'Apply {count}',
  'studio.start.bulk.reject': 'Reject {count}',
  // Maps banner (E5)
  'studio.start.maps.banner': '{count} locations without maps — create pages and generate?',
  'studio.start.maps.action': 'Create maps',

  // === Studio Digest (T004) ===
  'studio.digest.button': 'Digest',
  'studio.digest.loading': 'Building digest…',
  'studio.digest.success': 'Done: {count} drafts',
  'studio.digest.empty': 'No new drafts',
  'studio.digest.error': 'Failed to build digest',
  'characters.nested': 'More in beats ({count})',
  'characters.manageHint': 'Owner, folder, public fields and deletion — in Prep',
  'studio.sheet.manage': 'Sheet',
  'studio.sheet.owner': 'Owner',
  'studio.sheet.noOwner': 'No owner',
  'studio.sheet.group': 'Folder',
  'studio.sheet.publicFields': 'Public fields…',
  'studio.sheet.delete': 'Delete sheet…',
  'sidebar.map.heading': 'Map',
  'journal.form.quest.followsFrom': 'Follows from',
  'journal.form.quest.followsObjective': 'after objective',
  'journal.form.quest.followsAnyObjective': 'any objective',
  'journal.chain.followsFrom': 'Follows from',
  'journal.chain.afterObjective': 'after objective “{name}”',
  'journal.chain.next': 'Next:',
  'journal.quest.from': 'from {name}',
  'studio.quest.wherePlan': 'Where in the plan',
  'studio.quest.notInPlan': 'Not in any beat yet',
  'studio.rail.bestiary': 'Bestiary',
  'studio.rail.bestiarySearch': 'Search the bestiary',
  'studio.mode.label': 'Mode',
  'studio.mode.prep': 'Prep',
  'studio.mode.play': 'Play',
  'timeline.form.atWorldTime': 'at the current world time',
  'timeline.form.autoLinked': 'Auto event — linked by itself: {what}',
  'timeline.form.autoLinkedTime': 'time change',
  'timeline.cursor.worldHeading': 'In the world',
  'timeline.cursor.nextDayPart': 'Next day · {part}',
  'sidebar.map.playerHint': 'A player sees only the map they were shown.',
  'timeline.form.changedMeanwhile': 'The event changed while you were editing it — the form is closed, nothing was sent',
  'studio.rail.groupMenu': 'Folder actions',
  'studio.rail.groupRename': 'Rename…',
  'studio.rail.groupDelete': 'Delete folder (sheets stay without a folder)',
  'journal.editor.group': 'Section',
  'studio.sheet.renameGroup': 'Rename folder',
  'studio.sheet.deleteGroup': 'Delete folder',
  'studio.sheet.renameGroupPrompt': 'New folder name',
  'studio.sheet.deleteGroupConfirm': 'Delete the folder “{name}”? Its sheets stay without a folder.',
  'journal.panel.sharedHint': 'Shared only: quests (with objective progress), NPCs, world lore.',
  'journal.panel.playerHint': 'Quests, NPCs and world lore the GM has opened to you.',
  'journal.panel.nestedHint': 'Notes of beats, objectives and maps are not in the shared list — open on a click, as in Studio.',
  'characters.description': 'Player characters and shared NPCs. An encounter\'s monsters live in its beat.',
  'journal.chain.change': 'Change',
  'studio.materials.quests': 'Beat quests',
  'studio.bestiary.more': 'Showing {shown} of {total} — narrow the search',
  'studio.bestiary.add': '＋ to map',
  'studio.bestiary.addTo': 'Add to the map “{map}”',
  'studio.bestiary.noMap': 'Open a map first — as a tab or «Map beside»',
  'studio.bestiary.ruHint': 'Monster names are English — search in English (for example, goblin).',
  'studio.sheet.ownerConfirmTitle': 'Give “{name}” to {player}?',
  'studio.sheet.ownerConfirmDesc': 'They will see the whole sheet.',
  'studio.sheet.ownerConfirm': 'Give',
  'remarks.rail': 'Remarks',
  'remarks.none': 'none',
  'remarks.heading': 'Remarks',
  'remarks.configure': 'Configure',
  'remarks.hint': 'Everything that looks unfinished or contradictory. Marked “Tia\'s opinion” may be wrong — better discuss it. Nothing changes until you press.',
  'remarks.pill.game': 'from the game',
  'remarks.pill.gameStale': 'from the game · out of date',
  'remarks.pill.opinion': 'Tia\'s opinion',
  'remarks.mark': 'Mark',
  'remarks.no': 'No',
  'remarks.discuss': 'Discuss',
  'remarks.leave': 'Leave as is',
  'remarks.asIntended': 'As intended',
  'remarks.empty': 'No remarks.',
  'remarks.leftAsIs': 'Left as is: {n}. I won\'t remind you until these records change.',
  'remarks.footer': 'Fixes from here also come to the chat as a summary — undo in one place.',
  'remarks.checkPrep': 'Check the prep',
  'remarks.checkPrepBusy': 'Checking…',
  'remarks.checkPrepFailed': 'The check did not work. Try again.',
  'remarks.badge.one': '{n} remark — open',
  'remarks.badge.few': '{n} remarks — open',
  'remarks.badge.many': '{n} remarks — open',
  'remarks.settings.title': 'For this campaign',
  'remarks.sw.game': 'Game hints',
  'remarks.sw.gameHint': '“looks like an objective is done” — from the chat and the chronicle',
  'remarks.sw.prep': 'Prep hints',
  'remarks.sw.prepHint': 'contradictions and forgotten things',
  'remarks.sw.fixes': 'Offer a fix',
  'remarks.sw.fixesHint': 'a button at the remark',
  'remarks.sw.simple': 'Simple checks',
  'remarks.sw.simpleHint': 'duplicates, empty fields, broken links — no AI',
  'remarks.sw.off': ' · off: Tia does not do this',
  'remarks.fix.duplicate': 'Delete the record',
  'remarks.fix.fieldMisuse': 'Move to the description',
  'remarks.fix.emptyPrep': 'Draft the prep',
  'remarks.fix.emptyNpc': 'Write a description',
  'remarks.fix.brokenLink': 'Remove the link',
  'remarks.fix.model': 'Fix it',
  'remarks.kind.quest': 'quest',
  'remarks.kind.record': 'record',
  'remarks.t.duplicate': '“{a}” repeats the {kind} “{b}”',
  'remarks.t.emptyPrep': 'The prep of the beat “{beat}” is empty',
  'remarks.t.emptyNpc': '“{npc}” has no description',
  'remarks.t.fieldMisuse': 'The rewards of “{quest}” hold a condition',
  'remarks.t.brokenLink': '“{owner}”: a link to something deleted',
  'remarks.b.duplicate': 'The text is the same, word for word.',
  'remarks.b.emptyPrep': 'There is nothing written to play from.',
  'remarks.b.emptyNpc': 'The players will ask who this is.',
  'remarks.b.fieldMisuse': 'A condition belongs in the description, not in the rewards.',
  'remarks.b.brokenLink': 'The link leads nowhere.',
  'remarks.game.body': 'Mark the objective “{obj}” in the quest “{quest}”?',
  'remarks.confirm.delete': 'Delete the record “{a}”? Its text stays in “{b}”.',
  'remarks.confirm.deleteBtn': 'Delete',
  'remarks.ask.prep': 'Draft the prep of the beat “{beat}”',
  'remarks.ask.npc': 'Write a description of “{npc}” from the prep of the beats',
  'remarks.discussMsg': 'Let\'s discuss this remark',
  'remarks.hint.line': 'Tia: {reason} — mark it?',
  'remarks.hint.marked': 'Objective marked — undo it in the chat',
  'remarks.summary.line': 'Game hints: {n} — open Remarks',
  'remarks.lk.beat': 'beat “{name}”',
  'remarks.lk.quest': 'quest “{name}”',
  'remarks.lk.record': 'record “{name}”',
  'remarks.lk.sheet': 'sheet “{name}”',
  'remarks.lk.npc': 'NPC “{name}”',
  'remarks.lk.page': 'map “{name}”',
  'search.open': 'Search',
  'search.placeholder.gm': 'Find an entry, map, beat, sheet, monster, rule or action…',
  'search.placeholder.player': 'Find an entry, the map, your sheet, a chronicle event or a rule…',
  'search.empty': 'Nothing found for “{q}”',
  'search.emptyScope.gm': 'Searched: entries (nested in beats and objectives too), maps, beats, sheets, the chronicle, the bestiary and the SRD rules; typos are forgiven.',
  'search.emptyScope.player': 'Searched: what is open to you — entries, the players\' map, your sheets, the chronicle and the SRD rules; typos are forgiven.',
  'search.loadingSrd': 'Loading the bestiary and the rules…',
  'search.keys': '↑↓ select · Enter open · → actions · Esc close',
  'search.group.recent': 'Recent and open',
  'search.group.action': 'Actions',
  'search.group.beat': 'Beats',
  'search.group.journal': 'Entries',
  'search.group.page': 'Maps',
  'search.group.sheet': 'Sheets',
  'search.group.token': 'Tokens',
  'search.group.timeline': 'Chronicle',
  'search.group.monster': 'Bestiary',
  'search.group.spell': 'Spells',
  'search.group.condition': 'Conditions',
  'search.group.item': 'Equipment and items',
  'search.act.toMap': 'On the map',
  'search.act.toBeat': 'To the beat',
  'search.act.beside': 'Open beside',
  'search.act.openHere': 'Open for me',
  'search.act.show': 'Show to players…',
  'search.act.reveal': 'Show to players…',
  'search.hint.current': 'running',
  'search.hint.open': 'open',
  'search.hint.players': 'players\'',
  'search.hint.cantrip': 'cantrip',
  'search.hint.level': 'level {n}',
  'search.cmd.toPrep': 'Prep',
  'search.cmd.toPlay': 'Play',
  'search.cmd.openJournal': 'Open the journal',
  'search.cmd.openMaps': 'Open the maps',
  'search.cmd.openChronicle': 'Open the chronicle',
  'search.cmd.openRules': 'Open the SRD rules',
  'search.cmd.openPlan': 'Open the plan',
  'search.srd.noMap': 'No map is open — open a map to put the monster on it',
  'search.srd.close': 'Close',
  'search.srd.loading': 'Loading…',
  'search.srd.failed': 'The article could not be loaded',
  'search.maps.find': 'Find a map…',
  'search.hint.npc': 'NPC',
  'images.title': 'Images',
  'images.titleBusy': 'Images · generating: {n}',
  'images.filter.all': 'All',
  'images.filter.map': 'Maps',
  'images.filter.portrait': 'Portraits',
  'images.filter.chat': 'Chat',
  'images.filter.other': 'Other',
  'images.what.map': 'map',
  'images.what.portrait': 'portrait',
  'images.what.chat': 'chat picture',
  'images.what.token': 'token',
  'images.what.other': 'picture',
  'images.empty': 'No generated pictures yet.',
  'images.emptyFilter': 'Nothing here yet.',
  'images.generating': 'Generating…',
  'images.failed': 'Failed',
  'images.failedShort': 'Failed',
  'images.offline': 'Network unavailable',
  'images.retry': 'Retry',
  'images.open': 'Open',
  'images.copyPrompt': 'Copy prompt',
  'images.copied': 'Prompt copied',
  'images.variant': 'Another variant',
  'images.more': 'More actions',
  'images.delete': 'Delete…',
  'images.deleteTitle': 'Delete the picture?',
  'images.deleteBody': 'The file is erased and cannot be brought back.',
  'images.deleteChatBody': 'The file is erased and cannot be brought back. The chat message will say «Picture deleted».',
  'images.deleteConfirm': 'Delete',
  'images.inUse': 'Cannot delete — used in: {places}. Remove it there first.',
  'images.back': '← All pictures',
  'images.field.kind': 'Kind',
  'images.field.prompt': 'Prompt',
  'images.field.userPrompt': 'Additional',
  'images.field.described': 'Description',
  'images.describedByAi': 'composed by AI',
  'images.field.style': 'Style',
  'images.field.model': 'Model',
  'images.field.size': 'Size',
  'images.field.author': 'Author',
  'images.field.time': 'Time',
  'images.field.usedIn': 'Used in',
  'images.field.error': 'Error',
  'images.usedIn.page': 'map «{name}»',
  'images.usedIn.sheet': 'portrait of «{name}»',
  'images.usedIn.token': 'token «{name}»',
  'images.usedIn.journal': 'entry «{name}»',
  'images.usedIn.chat-message': 'a chat message',
  'images.notUsed': 'nowhere',
  'images.toast.ready': 'Ready: {what}',
  'images.toast.failed': 'Failed: {what}',
  'images.toast.portraitNotSet': 'The portrait for «{name}» is ready, but the sheet has another one',
  'images.setPortrait': 'Set',
  'images.use': 'Use as…',
  'images.use.portrait': 'Sheet portrait',
  'images.use.token': 'Token image',
  'images.use.map': 'Page map',
  'images.use.hint.noMap': 'no map',
  'images.use.kind.sheet': 'sheet',
  'images.use.kind.token': 'token',
  'images.use.kind.page': 'page',
  'images.use.hint.players': 'players see it',
  'images.use.hint.current': 'this page',
  'images.use.hint.noImage': 'no picture',
  'images.use.hint.noPortrait': 'no portrait',
  'images.use.hint.npc': 'NPC',
  'images.use.hint.pc': 'player character',
  'images.use.scope': 'Searched: sheets, tokens of this map, pages.',
  'images.use.placeholder': 'Where to put the picture…',
  'images.use.suggested': 'Suggested',
  'images.use.done': 'Set: {what}',
  'images.use.mapTitle': 'Replace the map of «{name}»?',
  'images.use.mapBody': 'The picture becomes the page map. Drawings on this page will be erased.',
  'images.use.mapConfirm': 'Replace',
  'images.chatDeleted': 'Picture deleted',
  'portraitGen.open': 'Generate portrait',
  'portraitGen.fromSheet': 'From the sheet',
  'portraitGen.extra': 'Additional',
  'portraitGen.extraPlaceholder': 'E.g.: a scar over the left eye, a hooded cloak',
  'portraitGen.style': 'Style',
  'portraitGen.style.fantasy painting': 'Fantasy painting',
  'portraitGen.style.ink sketch': 'Ink sketch',
  'portraitGen.style.watercolor': 'Watercolor',
  'portraitGen.style.anime': 'Anime',
  'portraitGen.style.photorealistic': 'Photorealistic',
  'portraitGen.create': 'Create',
  'portraitGen.hint': 'The portrait goes on the sheet by itself when ready. Progress is on the «Images» icon.',
  'portraitGen.describe': 'Compose description with AI',
  'portraitGen.describeAgain': 'Compose again',
  'portraitGen.describing': 'Composing description…',
  'portraitGen.description': 'Description',
  'portraitGen.resetToSheet': 'Back to the sheet',
  'portraitGen.describedHint': 'Composed by AI from the sheet and «Additional», in English — the image model reads it better. You can edit it: this text goes to the generation.',
  'aiMap.background': 'The window closes at once: the map goes on this page by itself when ready. Progress is on the «Images» icon.',
  'images.before': 'Before',
  'images.after': 'After',
  'images.reject': 'Reject',
  'images.accept': 'Accept',
  'images.compare': 'Compare',
  'images.toast.compare': 'Ready — compare: {what}',
  'images.toast.notPlaced': 'Ready: {what} — not put, the page has changed',
  'images.placeMapWarn': '«Set» replaces the map of the page.',
  'images.placeMapWarnDrawings': '«Set» replaces the map of the page and erases its drawings ({n}).',
  'images.placePortraitWarn': '«Set» replaces the portrait of the sheet.',
  'images.inUseHidden': 'Cannot delete — the picture is in use.',
  'images.sketchLost': 'The sketch is not kept — «Another variant» goes by the text.',
  'images.usedIn.note': 'note «{name}»',
  'images.usedIn.alert': 'effect «{name}»',
  'images.usedIn.beat': 'beat «{name}»',
  'images.usedIn.timeline': 'chronicle «{name}»',
  'images.edit.inpaint': 'Brush',
  'images.edit.outpaint': 'Map expansion',
  'images.edit.detail': 'Detail «{name}»',
  'images.edit.acceptDetail': 'Accept — a new map',
  'images.edit.replaceLinked': 'Update «{name}»',
  'images.edit.started': 'Generating — the compare window opens when ready',
  'sb.more': 'More',
  'sb.cancel': 'Cancel',
  'sb.expand': 'Expand',
  'sb.collapse': 'Collapse',
  'sb.collapseAll': 'Collapse all',
  'sb.showMore': 'Show {n} more',
  'sb.showLess': 'Back to {n}',
  'sb.nothingFound': 'Nothing found',
  'sb.filter': 'Filter',
  'sb.chip.all': 'All',
  'sb.gmOnly': 'GM only — players do not see it',
  'sb.playersSee': 'Players see it',
  'sb.hidden': 'Hidden levels of the path',
  'sb.plan.openBeatInPrep': 'Open the beat in Prep',
  'sb.plan.openQuestInPrep': 'Open the quest in Prep',
  'sb.plan.current': 'Now playing:',
  'sb.note.open': 'Open',
  'sb.note.reveal': 'Show to players',
  'sb.note.hide': 'Hide from players',
  'sb.removeFromBeat': 'Remove from the beat',
  'sb.removedFromBeat': '«{name}» removed from the beat',
  'sb.undo': 'Undo',
  'sb.delete': 'Delete…',
  'sb.deleteYes': 'Delete',
  'sb.note.deleteTitle': 'Delete the note «{name}»?',
  'sb.note.deleteBody': 'The note lives in this beat only and goes for good.',
  'sb.sheet.open': 'Open the sheet',
  'sb.sheet.npcKind': 'NPC sheet',
  'sb.plan.list': 'Beats',
  'sb.map.youHere': 'you',
  'sb.maps.others': 'From other beats',
  'sb.pins': 'Pins',
  'sb.objects': 'Objects',
  'sb.map.openTip': '{name} — open for me',
  'sb.map.find': 'Find a map',
  'sb.map.addToken': 'Add a token',
  'sb.map.addPin': 'Place a marker',
  'sb.map.addObject': 'Create an object',
  'sb.map.noObjects': 'No objects on this map.',
  'sb.map.noTokens': 'No tokens.',
  'sb.map.youAre': 'you are here',
  'sb.map.playerHint': 'Only the map the GM showed is visible.',
  'sb.map.playerTitle': 'Map',
  'sb.tok.find': 'Find on the map',
  'sb.tok.hide': 'Hide from players',
  'sb.tok.show': 'Show to players',
  'sb.tok.deleteTitle': 'Delete the token «{name}»?',
  'sb.tok.deleteBody': 'The token goes from the map for everyone. The character sheet stays.',
  'sb.tok.mine': 'yours',
  'sb.tok.presets': 'Quick token',
  'sb.pin.open': 'Open the note',
  'sb.pin.edit': 'Edit the marker',
  'sb.pin.deleteTitle': 'Delete the marker?',
  'sb.pin.deleteBody': 'The marker goes from the map. The note stays.',
  'sb.obj.edit': 'Edit the object',
  'sb.rail.title': 'Prep',
  'sb.rail.create': 'Create… (a beat, an entry, a map, a sheet)',
  'sb.rail.createBeat': 'A beat',
  'sb.rail.createEntry': 'A Library entry…',
  'sb.rail.createMap': 'A map',
  'sb.rail.createPc': 'A character',
  'sb.rail.createNpc': 'An NPC sheet',
  'sb.rail.collapseAll': 'Fold every section',
  'sb.rail.addBeat': 'Add a beat',
  'sb.rail.rename': 'Rename',
  'sb.rail.up': 'Up',
  'sb.rail.down': 'Down',
  'sb.rail.pin': 'Open as a pinned tab',
  'sb.rail.sheets': 'Character sheets',
  'sb.rail.newMap': 'A new map',
  'sb.rail.onlyInBeats': 'Only in beats',
  'sb.rail.beatN': 'beat {n}',
  'sb.rail.openRemarks': 'Open the remarks',
  'sb.tl.world': 'In the world',
  'sb.tl.whatWas': 'What happened',
  'sb.tl.session': 'Session {number}',
  'sb.tl.noSession': 'No session',
  'sb.tl.now': 'now in the world',
  'sb.tl.stuckSession': 'session {number}',
  'sb.tl.showSession': 'Show session {label} ({count})',
  'sb.tl.earlier': '{count} more earlier',
  'sb.tl.onlyCurrent': 'Keep the current session only',
  'sb.tl.desc': 'description',
  'sb.tl.gmOnly': 'GM only',
  'sb.tl.onlyPlayers': 'only: {names}',
  'sb.tl.edit': 'Edit',
  'sb.tl.showAll': 'Show to all players…',
  'sb.tl.toGm': 'GM only',
  'sb.tl.toPlayers': 'Selected players…',
  'sb.tl.link': 'Link to an entry…',
  'sb.tl.deleteTitle': 'Delete the event «{title}»?',
  'sb.tl.deleteBody': 'The event is gone from the chronicle for everyone.',
  'sb.tl.publish': 'Publish — players will see',
  'sb.tl.accept': 'Accept — players will see',
  'sb.tl.reject': 'Reject…',
  'sb.tl.remove': 'Remove',
  'sb.tl.draft': 'draft',
  'sb.tl.gmAuthor': 'GM',
  'sb.tl.findGm': 'Find an event or a draft',
  'sb.tl.find': 'Find an event',
  'sb.tl.filters': 'Filters (type, tag, session)…',
  'sb.tl.addToDay': 'Add an event to this day',
  'sb.tl.waitingGm': 'waiting for the GM',
  'sb.fx.own': 'Own effects',
  'sb.fx.runAll': 'Run for everyone',
  'sb.fx.edit': 'Edit the effect',
  'sb.fx.auto': 'Fires by itself',
  'sb.fx.dragHint': '{name} — edit; drag to reorder',
  'sb.stickers': 'Stickers',
  'sb.stickers.create': 'Create a sticker pack',
  'sb.stickers.connect': 'Connect a pack',
  'sb.srd.find': 'Find in the reference',
  'sb.srd.open': 'Open the stat block',
  'sb.srd.place': 'Place on the map',
  'sb.srd.placeWithSheet': 'Place with an NPC sheet',
  'sb.hb.import': 'Import homebrew',
  'sb.char.create': 'Create a character — the builder',
  'sb.char.createBlank': 'A blank sheet — fill it by hand',
  'sb.char.pcs': 'Player characters',
  'sb.char.npcs': 'NPCs',
  'sb.char.mine': 'My characters',
  'sb.char.inBeats': 'Inside beats',
  'sb.char.inBeatsInfo': 'Monsters and NPCs that live in a beat (encounters). They open on a click, as in Prep.',
  'sb.char.inFolder': 'Create in the folder «{name}»',
  'sb.char.owner': 'player {name}',
  'sb.char.myOwn': 'your sheet',
  'sb.char.show': 'Show the sheet to players',
  'sb.char.hide': 'Hide the sheet from players',
  'sb.char.playersSee': 'Players see the sheet',
  'sb.char.hidden': 'The sheet is hidden from players',
  'sb.char.card': 'Card on the scene: {state}',
  'sb.char.manageNote': 'Owner, folder, public fields and delete — in Prep',
  'sb.char.dragHint': '{name} — open the sheet; drag onto the map for a token',
  'sb.journal.find': 'Find in the journal',
  'sb.journal.create': 'Create an entry',
  'sb.journal.open': 'Open',
  'sb.journal.edit': 'Edit',
  'sb.journal.reveal': 'Show to players…',
  'sb.journal.hide': 'Hide from players',
  'sb.journal.shownToggle': 'Players see it. Click to hide it from players',
  'sb.journal.hiddenToggle': 'Hidden from players. Click to show it to players…',
  'sb.journal.pin': 'Pin to the top',
  'sb.journal.unpin': 'Unpin',
  'sb.journal.pinned': 'Pinned to the top',
  'sb.journal.setSection': 'Section…',
  'sb.journal.sectionTitle': 'Section of «{name}»',
  'sb.journal.sectionLabel': 'Section',
  'sb.journal.sectionHint': 'Empty — no section.',
  'sb.journal.deleteTitle': 'Delete the entry «{name}»?',
  'sb.journal.deleteBody': 'The entry is gone from the campaign for everyone. Links to it in beats and markers will be empty.',
  'sb.journal.playersSee': 'Players see it',
  'sb.journal.personal': 'A player\'s own note',
  'sb.journal.playerEmpty': 'The GM has not shown anything yet. Entries appear here when the GM opens them.',
  'sb.section.rename': 'Rename the section…',
  'sb.section.renameTitle': 'Rename the section «{name}»',
  'sb.section.remove': 'Remove the section (entries stay)',
  'sb.section.menu': 'Section: actions',
  'sb.nested': 'Inside beats and objectives',
  'sb.type.quest': 'Quests',
  'sb.type.npc': 'NPCs',
  'sb.type.location': 'Locations',
  'sb.type.faction': 'Factions',
  'sb.type.handout': 'Handouts',
  'sb.type.lore': 'World',
  'sb.quests.active': 'Active',
  'sb.quests.completed': 'Completed',
  'sb.quests.failed': 'Failed',
  'sb.quests.hidden': 'Hidden',
};
